import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from './contexts/LanguageContext';
import { useTranslations } from './hooks/useTranslations';
import LanguageSelector from './components/LanguageSelector';
import LanguageSwitcher from './components/LanguageSwitcher';
import AsesoriaPageNew from "./AsesoriaPageNew";
import SobreMiPage from "./SobreMiPage";
import MetodologiaPage from "./MetodologiaPage";
import ServiciosPage from "./ServiciosPage";
import CasosEstudioPage from "./CasosEstudioPage";
import ImprovedCalculatorSimple from "./ImprovedCalculatorSimple";
import Breadcrumbs from "./Breadcrumbs";
import CheckoutSuccessPage from './CheckoutSuccessPage';
import ReservaPage from './ReservaPage';
import ShareResultPage from './ShareResultPage';
import FeedbackPopup from './components/FeedbackPopup';
import FAQPage from './FAQPage';
import ThanksPage from './ThanksPage';
import TestAssetsPage from './TestAssetsPage';

// Calendly Integration Component
const CalendlyButton = ({ 
  text = "Reserva tu sesión", 
  variant = "primary", 
  size = "medium",
  className = "",
  showMicrocopy = true,
  icon = "📅"
}) => {
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";
  
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 text-center";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg",
    secondary: "border-2 border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300",
    outline: "border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40",
    accent: "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg"
  };
  
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <div className="text-center">
      <a
        href={calendlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        aria-label="Reservar sesión gratuita de 30 minutos con Tamara Navarrete"
      >
        <span className="mr-2">{icon}</span>
        {text}
      </a>
      {showMicrocopy && (
        <p className="text-sm text-gray-400 mt-2">
          Primera sesión gratuita de 30 minutos. Sin compromiso.
        </p>
      )}
    </div>
  );
};

// Components
const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setMobileDropdownOpen(false);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const toggleMobileDropdown = () => {
    setMobileDropdownOpen(!mobileDropdownOpen);
    console.log('Mobile dropdown toggled:', !mobileDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.relative')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Get current path for active state
  const currentPath = window.location.pathname;

  // Check if link is active
  const isActive = (path) => currentPath === path;

  // Check if dropdown section is active
  const isDropdownActive = (paths) => paths.some(path => currentPath === path);

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] bg-slate-900/95 backdrop-blur-md border-b border-white/20 shadow-lg w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          {/* Logo */}
          <Link 
            to="/" 
            className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0 hover:scale-105 transition-transform duration-300" 
            onClick={closeMobileMenu}
          >
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1 flex-shrink-0">
            {/* Primary CTAs */}
            <Link 
              to="/calculator" 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                isActive('/calculator') 
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
                  : 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white hover:from-purple-600 hover:to-pink-600 shadow-md hover:shadow-lg'
              }`}
            >
              🧮 {t('navigation.calculator')}
            </Link>
            
            <Link 
              to="/asesoria" 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ml-2 ${
                isActive('/asesoria') 
                  ? 'bg-orange-500 text-white shadow-lg' 
                  : 'bg-orange-500/80 text-white hover:bg-orange-500 shadow-md hover:shadow-lg'
              }`}
            >
              🚀 {t('navigation.consulting')}
            </Link>

            {/* Separator */}
            <div className="w-px h-6 bg-white/20 mx-4"></div>

            {/* Navigation Links */}
            <Link 
              to="/" 
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                isActive('/') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('navigation.home')}
            </Link>

            <Link 
              to="/pricing" 
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                isActive('/pricing') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('navigation.pricing')}
            </Link>

            <Link 
              to="/demo" 
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                isActive('/demo') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('navigation.demo')}
            </Link>

            {/* Sobre Tamara Dropdown */}
            <div className="relative group">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'about' ? null : 'about')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center ${
                  isDropdownActive(['/sobre-mi', '/servicios', '/casos-estudio']) 
                    ? 'text-white bg-white/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {t('navigation.aboutTamara')}
                <svg className={`ml-1 h-4 w-4 transition-transform duration-300 ${
                  activeDropdown === 'about' ? 'rotate-180' : ''
                }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {activeDropdown === 'about' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-white/10 z-[110]">
                  <div className="py-2">
                    <Link 
                      to="/sobre-mi" 
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isActive('/sobre-mi') 
                          ? 'text-white bg-white/10' 
                          : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                      onClick={() => setActiveDropdown(null)}
                    >
                      👤 {t('navigation.aboutMe')}
                    </Link>
                    <Link 
                      to="/servicios" 
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isActive('/servicios') 
                          ? 'text-white bg-white/10' 
                          : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                      onClick={() => setActiveDropdown(null)}
                    >
                      💼 {t('navigation.services')}
                    </Link>
                    <Link 
                      to="/casos-estudio" 
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isActive('/casos-estudio') 
                          ? 'text-white bg-white/10' 
                          : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                      onClick={() => setActiveDropdown(null)}
                    >
                      📊 {t('navigation.caseStudies')}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/contacto" 
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                isActive('/contacto') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('navigation.contact')}
            </Link>

            {/* Language Switcher for Desktop */}
            <div className="ml-4">
              <LanguageSwitcher compact={false} />
            </div>
          </div>

          {/* Medium Screen Menu (md-lg) */}
          <div className="hidden md:flex lg:hidden items-center space-x-1 flex-shrink-0">
            <Link 
              to="/calculator" 
              className={`px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                isActive('/calculator') 
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                  : 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white hover:from-purple-600 hover:to-pink-600'
              }`}
            >
              {t('navigation.calculator')}
            </Link>
            
            <Link 
              to="/asesoria" 
              className={`px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                isActive('/asesoria') 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-orange-500/80 text-white hover:bg-orange-500'
              }`}
            >
              {t('navigation.consulting')}
            </Link>

            <Link 
              to="/sobre-mi" 
              className={`px-2 py-2 rounded-lg text-sm transition-all duration-300 ${
                isActive('/sobre-mi') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('navigation.aboutMe')}
            </Link>

            <Link 
              to="/demo" 
              className={`px-2 py-2 rounded-lg text-sm transition-all duration-300 ${
                isActive('/demo') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('navigation.demo')}
            </Link>

            <Link 
              to="/contacto" 
              className={`px-2 py-2 rounded-lg text-sm transition-all duration-300 ${
                isActive('/contacto') 
                  ? 'text-white bg-white/10' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('navigation.contact')}
            </Link>

            {/* Separator */}
            <div className="w-px h-6 bg-white/20 mx-2"></div>

            <Link 
              to="/reserva" 
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                isActive('/reserva') 
                  ? 'bg-green-500 text-white shadow-lg' 
                  : 'bg-green-500/80 text-white hover:bg-green-500 shadow-md hover:shadow-lg'
              }`}
            >
              📅 {t('navigation.bookSession')}
            </Link>

            {/* Language Switcher for Medium Screens */}
            <div className="ml-2">
              <LanguageSwitcher compact={true} />
            </div>
          </div>

          {/* Mobile Language Switcher and Menu Button */}
          <div className="flex items-center space-x-3 flex-shrink-0 md:hidden">
            {/* Mobile Language Switcher - Always visible */}
            <LanguageSwitcher compact={true} />
            
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white focus:outline-none focus:text-white p-2 rounded-lg hover:bg-white/10 transition-all duration-300"
              aria-label="Abrir menú"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out w-full ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}>
          <div className="w-full px-3 pt-3 pb-4 space-y-1 bg-slate-800/98 backdrop-blur-md rounded-b-2xl border-t border-white/10 shadow-xl max-h-screen overflow-y-auto">
            
            {/* Primary CTAs for Mobile */}
            <div className="space-y-2 pb-3 border-b border-white/10 mb-3">
              <Link 
                to="/calculator" 
                className={`block w-full px-4 py-3 rounded-lg font-semibold text-center transition-all duration-300 ${
                  isActive('/calculator') 
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
                    : 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white'
                }`}
                onClick={closeMobileMenu}
              >
                🧮 {t('navigation.calculatorFull')}
              </Link>
              
              <Link 
                to="/asesoria" 
                className={`block w-full px-4 py-3 rounded-lg font-semibold text-center transition-all duration-300 ${
                  isActive('/asesoria') 
                    ? 'bg-orange-500 text-white shadow-lg' 
                    : 'bg-orange-500/80 text-white'
                }`}
                onClick={closeMobileMenu}
              >
                🚀 {t('navigation.strategicConsulting')}
              </Link>
            </div>

            {/* Navigation Links */}
            <Link 
              to="/" 
              className={`block w-full px-4 py-3 rounded-lg transition-all duration-300 ${
                isActive('/') 
                  ? 'text-white bg-white/10 font-medium' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              onClick={closeMobileMenu}
            >
              🏠 {t('navigation.home')}
            </Link>

            <Link 
              to="/pricing" 
              className={`block w-full px-4 py-3 rounded-lg transition-all duration-300 ${
                isActive('/pricing') 
                  ? 'text-white bg-white/10 font-medium' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              onClick={closeMobileMenu}
            >
              💰 {t('navigation.pricing')}
            </Link>

            <Link 
              to="/demo" 
              className={`block w-full px-4 py-3 rounded-lg transition-all duration-300 ${
                isActive('/demo') 
                  ? 'text-white bg-white/10 font-medium' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              onClick={closeMobileMenu}
            >
              🎯 {t('navigation.demo')}
            </Link>

            {/* Sobre Tamara Section - Simplified for Mobile */}
            <div className="space-y-1">
              <div className="text-gray-400 text-sm font-medium px-4 py-2">
                👤 {t('navigation.aboutTamara')}
              </div>
              <div className="pl-4 space-y-1">
                <Link 
                  to="/sobre-mi" 
                  className={`block w-full px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                    isActive('/sobre-mi') 
                      ? 'text-white bg-white/10 font-medium' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={closeMobileMenu}
                >
                  • {t('navigation.myHistory')}
                </Link>
                <Link 
                  to="/servicios" 
                  className={`block w-full px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                    isActive('/servicios') 
                      ? 'text-white bg-white/10 font-medium' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={closeMobileMenu}
                >
                  • {t('navigation.services')}
                </Link>
                <Link 
                  to="/casos-estudio" 
                  className={`block w-full px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                    isActive('/casos-estudio') 
                      ? 'text-white bg-white/10 font-medium' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={closeMobileMenu}
                >
                  • {t('navigation.successCases')}
                </Link>
              </div>
            </div>

            <Link 
              to="/contacto" 
              className={`block w-full px-4 py-3 rounded-lg transition-all duration-300 ${
                isActive('/contacto') 
                  ? 'text-white bg-white/10 font-medium' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              onClick={closeMobileMenu}
            >
              📞 {t('navigation.contact')}
            </Link>

            <Link 
              to="/reserva" 
              className={`block w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                isActive('/reserva') 
                  ? 'bg-green-500 text-white shadow-lg' 
                  : 'bg-green-500/80 text-white hover:bg-green-500'
              }`}
              onClick={closeMobileMenu}
            >
              📅 {t('navigation.freeSession')}
            </Link>
            
            {/* Extra spacing to ensure everything fits */}
            <div className="h-4"></div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const DemoPage = () => {
  const { t, language } = useTranslations();
  const navigate = useNavigate();
  
  // Helper function to construct calculator URL with brand type and preserve query params
  const getCalculatorUrl = (brandType) => {
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams(currentParams);
    
    // Add brandType parameter
    newParams.set('brandType', brandType);
    
    // Add language parameter if not already present
    if (!newParams.has('lang')) {
      newParams.set('lang', language);
    }
    
    return `/calculator?${newParams.toString()}`;
  };
  
  const demoData = {
    revenue: '1250000',
    clients: '185',
    yearsInMarket: '6',
    socialFollowers: '68000',
    monthlyTraffic: '95000',
    tangibleAssets: '450000',
    sector: 'tecnologia',
    businessType: 'B2B',
    brandType: 'empresa',
    brandRecognition: '9',
    currency: 'EUR'
  };

  const personalBrandDemoData = {
    revenue: '420000',
    clients: '0',
    yearsInMarket: '4',
    socialFollowers: '285000',
    monthlyTraffic: '125000',
    tangibleAssets: '35000',
    sector: 'lifestyle',
    businessType: 'B2C',
    brandType: 'personal',
    brandRecognition: '8',
    currency: 'EUR',
    personalBrandFields: {
      instagramFollowers: '180000',
      tiktokFollowers: '95000',
      linkedinFollowers: '28000',
      youtubeSubscribers: '45000',
      youtubeViews: '850000',
      averageEngagementRate: '5.8',
      monthlyCollaborations: '12',
      coursesProducts: '8',
      avgProductPrice: '450',
      speakingFees: '3500',
      consultingHourlyRate: '180',
      personalBrandNiche: 'lifestyle',
      contentType: 'mixed',
      audienceLocation: 'international',
      brandAuthority: '8'
    }
  };

  const handleStartDemo = (demoType = 'business') => {
    const selectedData = demoType === 'personal' ? personalBrandDemoData : demoData;
    localStorage.setItem('demoData', JSON.stringify(selectedData));
    navigate('/?demo=true');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      {/* Back to Home Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('demo.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('demo.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Business Brand Demo */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10 
                          grid grid-rows-[auto,1fr,auto,auto] min-h-[600px]">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              {t('demo.businessExample')}
            </h2>
            
            {/* Contenido expandible (1fr) */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-purple-400 mb-4">{t('demo.basicInfo')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.businessType')}</span>
                  <span className="text-white font-semibold">B2B</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.brandType')}</span>
                  <span className="text-white font-semibold">{t('home.calculator.brandCompany')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.annualRevenue')}</span>
                  <span className="text-white font-semibold">€1,250,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.corporateClients')}</span>
                  <span className="text-white font-semibold">185</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.yearsInMarket')}</span>
                  <span className="text-white font-semibold">{t('demo.demoValues.businessYears')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.sector')}</span>
                  <span className="text-white font-semibold">{t('demo.demoValues.businessSector')}</span>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-purple-400 mb-4">{t('demo.digitalMetrics')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.socialFollowers')}</span>
                  <span className="text-white font-semibold">45,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.monthlyTraffic')}</span>
                  <span className="text-white font-semibold">35,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.tangibleAssets')}</span>
                  <span className="text-white font-semibold">€300,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.recognition')}</span>
                  <span className="text-white font-semibold">9/10</span>
                </div>
              </div>
            </div>

            {/* Resultado Estimado (auto - alineado) */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl border border-purple-500/30">
              <h3 className="text-xl font-bold text-white mb-2">{t('demo.estimatedResult')}</h3>
              <div className="text-3xl font-bold text-purple-400 mb-2">€1,750,000</div>
              <p className="text-gray-300 text-sm">
                {t('demo.b2bDescription')}
              </p>
            </div>
            
            {/* Botón (auto - alineado) */}
            <div className="mt-6 text-center">
              <Link
                to={getCalculatorUrl('company')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 inline-block text-decoration-none"
              >
                {t('demo.calculateCompanyBrand')}
              </Link>
            </div>
          </div>

          {/* Personal Brand Demo */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10
                          grid grid-rows-[auto,1fr,auto,auto] min-h-[600px]">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              {t('demo.personalExample')}
            </h2>
            
            {/* Contenido expandible (1fr) */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4">{t('demo.basicInfo')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.businessType')}</span>
                  <span className="text-white font-semibold">B2C</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.brandType')}</span>
                  <span className="text-white font-semibold">{t('home.calculator.brandPersonal')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.annualRevenue')}</span>
                  <span className="text-white font-semibold">€420,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.yearsInMarket')}</span>
                  <span className="text-white font-semibold">{t('demo.demoValues.personalYears')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.niche')}</span>
                  <span className="text-white font-semibold">{t('demo.demoValues.personalNiche')}</span>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-cyan-400 mb-4">{t('demo.socialNetworks')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.instagram')}</span>
                  <span className="text-white font-semibold">75,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.tiktok')}</span>
                  <span className="text-white font-semibold">35,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.linkedin')}</span>
                  <span className="text-white font-semibold">12,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.youtube')}</span>
                  <span className="text-white font-semibold">8,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.engagementRate')}</span>
                  <span className="text-white font-semibold">4.2%</span>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-cyan-400 mb-4">{t('demo.monetization')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.collaborationsMonth')}</span>
                  <span className="text-white font-semibold">6</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.productsMonth')}</span>
                  <span className="text-white font-semibold">15</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('demo.avgPrice')}</span>
                  <span className="text-white font-semibold">€180</span>
                </div>
              </div>
            </div>

            {/* Resultado Estimado (auto - alineado) */}
            <div className="mt-6 p-4 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl border border-cyan-500/30">
              <h3 className="text-xl font-bold text-white mb-2">{t('demo.estimatedResult')}</h3>
              <div className="text-3xl font-bold text-cyan-400 mb-2">€609,000</div>
              <p className="text-gray-300 text-sm">
                {t('demo.personalDescription')}
              </p>
            </div>
            
            {/* Botón (auto - alineado) */}
            <div className="mt-6 text-center">
              <Link
                to={getCalculatorUrl('personal')}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 inline-block text-decoration-none"
              >
                {t('demo.calculatePersonalBrand')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PricingPage = () => {
  const { t, language } = useTranslations();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' o 'yearly'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Handle Stripe checkout with language support
  const handleCheckout = async (packageId) => {
    setIsLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: originUrl,
          language: language, // Add language parameter for LIVE checkout
          metadata: {
            source: 'pricing_page',
            package_name: packageId,
            user_language: language
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle One-off Report Purchase (using unified checkout endpoint)
  const handleOneoffCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: 'informe_unico', // Use the unified package system
          origin_url: originUrl,
          language: language,
          metadata: {
            source: 'one_off_report',
            package_name: 'informe_unico',
            user_language: language
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (response.ok && data.success && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Failed to create one-off checkout session');
      }
    } catch (error) {
      console.error('Error creating one-off checkout:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const plans = [
    {
      name: t('pricing.plans.free.name'),
      monthlyPrice: 0,
      yearlyPrice: 0,
      period: t('pricing.alwaysFree'),
      description: t('pricing.plans.free.description'),
      features: [
        t('pricing.plans.free.features.feature1'),
        t('pricing.plans.free.features.feature2'),
        t('pricing.plans.free.features.feature3'),
        t('pricing.plans.free.features.feature4'),
        t('pricing.plans.free.features.feature5')
      ],
      buttonText: t('pricing.plans.free.buttonText'),
      buttonColor: 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800',
      borderColor: 'border-gray-600/30',
      popular: false,
      packageId: 'free'
    },
    {
      name: t('pricing.plans.professional.name'),
      monthlyPrice: 29,
      yearlyPrice: 279, // 20% descuento (29 * 12 * 0.8 = 278.4 ≈ 279)
      period: billingCycle === 'monthly' ? t('pricing.monthly') : t('pricing.yearly'),
      description: t('pricing.plans.professional.description'),
      features: [
        t('pricing.plans.professional.features.feature1'),
        t('pricing.plans.professional.features.feature2'),
        t('pricing.plans.professional.features.feature3'),
        t('pricing.plans.professional.features.feature4'),
        t('pricing.plans.professional.features.feature5')
      ],
      buttonText: t('pricing.plans.professional.buttonText'),
      buttonColor: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700',
      borderColor: 'border-purple-500/50',
      popular: true,
      discount: billingCycle === 'yearly' ? '20% OFF' : null,
      packageId: billingCycle === 'monthly' ? 'professional_monthly' : 'professional_yearly'
    },
    {
      name: t('pricing.plans.enterprise.name'),
      monthlyPrice: 99,
      yearlyPrice: 950, // 20% descuento (99 * 12 * 0.8 = 950.4 ≈ 950)
      period: billingCycle === 'monthly' ? t('pricing.monthly') : t('pricing.yearly'),
      description: t('pricing.plans.enterprise.description'),
      features: [
        t('pricing.plans.enterprise.features.feature1'),
        t('pricing.plans.enterprise.features.feature2'),
        t('pricing.plans.enterprise.features.feature3'),
        t('pricing.plans.enterprise.features.feature4'),
        t('pricing.plans.enterprise.features.feature5')
      ],
      buttonText: t('pricing.plans.enterprise.buttonText'),
      buttonColor: 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600',
      borderColor: 'border-orange-500/50',
      popular: false,
      discount: billingCycle === 'yearly' ? '20% OFF' : null,
      packageId: billingCycle === 'monthly' ? 'enterprise_monthly' : 'enterprise_yearly'
    }
  ];

  const oneTimeReport = {
    name: t('pricing.oneTimeReport.name'),
    description: t('pricing.oneTimeReport.description'),
    price: 19,
    features: [
      t('pricing.oneTimeReport.features.feature1'),
      t('pricing.oneTimeReport.features.feature2'),
      t('pricing.oneTimeReport.features.feature3'),
      t('pricing.oneTimeReport.features.feature4'),
      t('pricing.oneTimeReport.features.feature5')
    ],
    packageId: 'one_time_report'
  };

  const faqs = [
    {
      question: t('pricing.faq.q1.question'),
      answer: t('pricing.faq.q1.answer')
    },
    {
      question: t('pricing.faq.q2.question'),
      answer: t('pricing.faq.q2.answer')
    },
    {
      question: t('pricing.faq.q3.question'),
      answer: t('pricing.faq.q3.answer')
    },
    {
      question: t('pricing.faq.q4.question'),
      answer: t('pricing.faq.q4.answer')
    }
  ];

  return (
    <div id="pricing-landing" className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>
        
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            {t('pricing.subtitle')}
          </p>
          
          {/* Error display */}
          {error && (
            <div className="bg-red-900/30 border border-red-500/50 text-red-300 p-4 rounded-xl mb-8 max-w-md mx-auto">
              <p>{error}</p>
            </div>
          )}
          
          {/* Selector de Facturación */}
          <div className="flex items-center justify-center mb-12">
            <div className="bg-slate-800/50 backdrop-blur-sm p-1 rounded-2xl border border-purple-500/20 shadow-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  billingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-300 relative ${
                  billingCycle === 'yearly'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {t('pricing.yearly')}
                {/* Badge de descuento */}
                <span className="absolute -top-2 -right-2 md:-top-2 md:-right-2 -top-1 -right-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg animate-pulse md:text-xs text-[10px] md:px-2 md:py-1 px-1.5 py-0.5">
                  <span className="md:inline hidden">20% OFF</span>
                  <span className="md:hidden inline">20% DTO</span>
                </span>
              </button>
            </div>
          </div>
          
          {/* Mensaje de oferta por lanzamiento */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-full px-6 py-2">
              <span className="text-green-400 mr-2">🚀</span>
              <span className="text-green-300 font-semibold">{t('pricing.launchOffer')}</span>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative p-8 rounded-2xl border ${plan.borderColor} ${
                plan.popular 
                  ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 scale-105 shadow-2xl shadow-purple-500/20' 
                  : 'bg-slate-800/50 backdrop-blur-sm'
              } transition-all duration-300 hover:scale-105 hover:shadow-xl`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                    {t('pricing.badges.mostPopular')}
                  </span>
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-white">
                    {plan.monthlyPrice === 0 ? t('common.pricing.free') : 
                     `€${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}`}
                  </span>
                  {plan.monthlyPrice > 0 && (
                    <span className="text-gray-400 text-lg">
                      {billingCycle === 'monthly' ? t('pricing.monthly') : t('pricing.yearly')}
                    </span>
                  )}
                  {/* Mostrar descuento para plan anual */}
                  {billingCycle === 'yearly' && plan.discount && (
                    <div className="mt-2">
                      <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                        {plan.discount} - {t('pricing.save')} €{(plan.monthlyPrice * 12) - plan.yearlyPrice}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {plan.monthlyPrice === 0 
                    ? t('pricing.alwaysFree') 
                    : billingCycle === 'yearly' 
                      ? `${t('pricing.equivalentMonthly')} €${Math.round(plan.yearlyPrice / 12)}/${t('pricing.month')}` 
                      : `${t('pricing.billedMonthly')}`
                  }
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start text-gray-300">
                    <span className="text-green-400 mr-3 mt-1 flex-shrink-0 text-lg">✓</span>
                    <span className="text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${plan.buttonColor} text-white shadow-lg ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading}
                onClick={() => {
                  if (plan.name === t('pricing.plans.free.name')) {
                    window.location.href = '/calculator';
                  } else if (plan.name === 'Enterprise') {
                    window.location.href = '/contacto';
                  } else {
                    handleCheckout(plan.packageId);
                  }
                }}
              >
                {isLoading ? t('pricing.processing') : (plan.name === 'Enterprise' ? t('pricing.contact') : plan.buttonText)}
              </button>
            </div>
          ))}
        </div>

        {/* One-time Report Option */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-gradient-to-r from-blue-900/50 to-cyan-900/50 p-8 rounded-2xl border border-cyan-500/30 backdrop-blur-sm">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/20 rounded-full mb-4">
                <span className="text-3xl">📊</span>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {oneTimeReport.name}
              </h3>
              <div className="text-5xl font-bold text-cyan-400 mb-2">
                €{oneTimeReport.price}
              </div>
              <p className="text-xl text-gray-300">{oneTimeReport.description}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {oneTimeReport.features.map((feature, index) => (
                <div key={index} className="flex items-center text-cyan-100">
                  <span className="text-cyan-400 mr-3 text-lg">✓</span>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <button
                className={`bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-12 py-4 rounded-xl font-bold text-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading}
                onClick={() => handleOneoffCheckout()}
              >
                {isLoading ? t('pricing.processing') : t('pricing.buyUniqueReport')}
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {t('pricing.faqTitle')}
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-purple-500/20 overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-purple-500/10 transition-colors duration-300"
                >
                  <span className="font-semibold text-white">{faq.question}</span>
                  <span className={`text-purple-400 text-xl transition-transform duration-300 ${
                    openFAQ === index ? 'rotate-180' : ''
                  }`}>
                    ↓
                  </span>
                </button>
                
                <div className={`transition-all duration-300 ${
                  openFAQ === index 
                    ? 'max-h-48 opacity-100 pb-6 px-6' 
                    : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                  <p className="text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AsesioriaPage = () => {
  const { t } = useTranslations();
  const [expandedService, setExpandedService] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const services = [
    {
      id: 'estrategia',
      icon: '🎯',
      title: 'Estrategia de Marketing Digital',
      price: '€350/hora',
      duration: 'Sesiones flexibles',
      description: 'Estrategias personalizadas para maximizar la presencia digital de tu marca.',
      features: ['Análisis de mercado', 'Plan de contenido', 'Optimización ROI', 'KPIs específicos'],
      deliverables: ['Informe de análisis detallado', 'Plan de acción mensual', 'Seguimiento KPIs']
    },
    {
      id: 'ecommerce',
      icon: '🛒',
      title: 'Consultoría E-commerce',
      price: '€2,500',
      duration: '2-4 semanas',
      description: 'Optimización completa para tiendas online y plataformas de venta.',
      features: ['Análisis UX/UI', 'Conversión optimizada', 'Estrategia omnicanal', 'Growth hacking'],
      deliverables: ['Audit UX completo', 'Plan de conversión', 'Implementación estrategia']
    },
    {
      id: 'linkedin',
      icon: '💼',
      title: 'Estrategia LinkedIn',
      price: '€1,800',
      duration: '3 semanas',
      description: 'Potencia tu marca personal y empresarial en LinkedIn con estrategias probadas.',
      features: ['Optimización perfil', 'Content strategy', 'Network building', 'Lead generation'],
      deliverables: ['Perfil optimizado', 'Calendario de contenido', 'Estrategia networking']
    },
    {
      id: 'marca',
      icon: '✨',
      title: 'Desarrollo de Marca',
      price: '€3,200',
      duration: '4-6 semanas',
      description: 'Construcción integral de identidad de marca para conectar con tu audiencia.',
      features: ['Identidad visual', 'Posicionamiento', 'Storytelling', 'Brand guidelines'],
      deliverables: ['Manual de marca', 'Guía de estilo', 'Estrategia de posicionamiento']
    }
  ];

  const testimonials = [
    {
      name: "María García",
      company: "TechStart Solutions",
      role: "CEO",
      text: "La consultoría de Tamara transformó completamente nuestra estrategia digital. Aumentamos nuestro valor de marca en un 85% en solo 6 meses.",
      rating: 5,
      result: "+85% valor de marca"
    },
    {
      name: "Carlos Rodríguez",
      company: "E-commerce Plus",
      role: "Director de Marketing",
      text: "Increíble trabajo en optimización de conversiones. Nuestras ventas online se triplicaron después de implementar sus recomendaciones.",
      rating: 5,
      result: "3x ventas online"
    },
    {
      name: "Ana Fernández",
      company: "Personal Brand Pro",
      role: "Consultora",
      text: "Su estrategia de LinkedIn fue clave para posicionar mi marca personal. Ahora soy referente en mi sector.",
      rating: 5,
      result: "Líder en sector"
    }
  ];

  const faqs = [
    {
      question: "¿Cuánto tiempo toma ver resultados?",
      answer: "Los primeros resultados suelen verse en 2-4 semanas, pero el impacto completo se desarrolla en 3-6 meses dependiendo del servicio y objetivos específicos."
    },
    {
      question: "¿Trabajas con empresas de todos los tamaños?",
      answer: "Sí, trabajo con startups, pymes y grandes empresas. Cada estrategia se adapta al tamaño, presupuesto y objetivos específicos de cada cliente."
    },
    {
      question: "¿Incluyes seguimiento post-implementación?",
      answer: "Todos mis servicios incluyen seguimiento durante 30 días post-entrega. También ofrezco paquetes de mantenimiento mensual para asegurar resultados sostenibles."
    },
    {
      question: "¿Cómo funciona la consulta gratuita?",
      answer: "Es una sesión de 30 minutos donde evaluamos tu situación actual, identificamos oportunidades y diseñamos una estrategia personalizada sin compromiso."
    }
  ];

  const servicesDetailed = [
    {
      id: 'estrategia-digital',
      title: '🎯 Estrategia Digital',
      price: '€350/hora',
      duration: 'Sesiones flexibles',
      description: 'Estrategias personalizadas para maximizar la presencia digital de tu marca.',
      features: ['Análisis de mercado', 'Plan de contenido', 'Optimización ROI', 'KPIs específicos']
    },
    {
      id: 'ecommerce-opt',
      title: '🛒 E-commerce',
      price: '€2,500',
      duration: '2-4 semanas',
      description: 'Optimización completa para tiendas online y plataformas de venta.',
      features: ['Análisis UX/UI', 'Conversión optimizada', 'Estrategia omnicanal', 'Growth hacking']
    },
    {
      id: 'linkedin-strategy',
      title: '💼 LinkedIn',
      price: '€1,800',
      duration: '3 semanas',
      description: 'Potencia tu marca personal y empresarial en LinkedIn con estrategias probadas.',
      features: ['Optimización perfil', 'Content strategy', 'Network building', 'Lead generation']
    },
    {
      id: 'brand-dev',
      title: '✨ Desarrollo de Marca',
      price: '€3,200',
      duration: '4-6 semanas',
      description: 'Construcción integral de identidad de marca para conectar con tu audiencia.',
      features: ['Identidad visual', 'Posicionamiento', 'Storytelling', 'Brand guidelines']
    }
  ];

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navigation />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-6">
            <span className="mr-2">←</span>
            {t('home.calculator.backHome')}
          </Link>

          {/* Hero Section Compacto */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-full px-4 py-2 mb-4 border border-purple-500/30">
              <span className="text-purple-400 text-sm font-semibold">💼 CONSULTORÍA DIGITAL</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Asesoría Estratégica con <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">Tamara Navarrete</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experta en Marketing Digital que ha generado €150M+ en valor para empresas
            </p>
          </div>

          {/* Profile Card Compacto */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center text-xl font-bold text-white">
                  TN
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Tamara Navarrete</h2>
                  <p className="text-purple-400 text-sm">Estratega Marketing Digital</p>
                  <div className="flex items-center space-x-3 text-xs text-gray-400 mt-1">
                    <span>🏆 #29 España</span>
                    <span>💼 7.3K LinkedIn</span>
                    <span>📊 2M+ impresiones</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setShowProfile(!showProfile)}
                className="text-purple-400 hover:text-purple-300 transition-colors text-sm"
              >
                {showProfile ? '▲ Menos' : '▼ Más info'}
              </button>
            </div>
            
            {/* Stats compactas */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">300+</div>
                <p className="text-gray-300 text-xs">Proyectos</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">€150M+</div>
                <p className="text-gray-300 text-xs">Valor</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">98%</div>
                <p className="text-gray-300 text-xs">Éxito M&A</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">5★</div>
                <p className="text-gray-300 text-xs">Rating</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={() => window.open('https://calendly.com/tamara-navarrete', '_blank')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                🎁 Consulta Gratuita
              </button>
              <Link 
                to="/contacto"
                className="border-2 border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 text-center"
              >
                Contactar Ahora
              </Link>
            </div>

          {/* Testimonials Section */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Casos de <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">Éxito</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{testimonial.name}</h4>
                      <p className="text-gray-400 text-xs">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-3 italic">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex text-yellow-400">
                      {'★'.repeat(testimonial.rating)}
                    </div>
                    <span className="text-green-400 text-sm font-semibold">{testimonial.result}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        {/* Expert Profile Section Mejorada */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-20">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="flex flex-col sm:flex-row items-center sm:items-start mb-8">
              <div className="w-32 h-32 rounded-2xl mr-0 sm:mr-6 mb-6 sm:mb-0 relative overflow-hidden group">
                <div className="w-full h-full relative">
                  <img 
                    src="data:image/jpeg;base64,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"
                    alt="Tamara Navarrete - Estratega de M&A y Desarrollo de Negocio" 
                    className="w-full h-full object-cover rounded-2xl shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-2xl"></div>
                  <div className="absolute inset-0 border-2 border-purple-400/30 rounded-2xl shadow-lg shadow-purple-500/20"></div>
                </div>
              </div>
              <div className="text-center sm:text-left">
                <h2 className="text-3xl font-bold text-white mb-2">Tamara Navarrete</h2>
                <p className="text-purple-400 font-semibold text-lg">Estratega de M&A y Desarrollo de Negocio</p>
                <p className="text-gray-400 mb-3">Especialista en Valoración de Marcas</p>
                <div className="flex flex-col sm:flex-row gap-2 text-sm">
                  <span className="text-cyan-400">✉️ Email</span>
                  <span className="text-cyan-400">💼 LinkedIn</span>
                </div>
                <div className="flex justify-center sm:justify-start mt-4 space-x-2">
                  {[1,2,3,4,5].map((star) => (
                    <span key={star} className="text-yellow-400 text-lg">⭐</span>
                  ))}
                  <span className="text-gray-400 ml-2">(4.9/5 - 127 reseñas)</span>
                </div>
              </div>
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-purple-900/30 p-4 rounded-xl">
                <h3 className="text-white font-semibold mb-2 flex items-center">
                  🎓 Formación
                </h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• MBA en Corporate Finance - IESE</li>
                  <li>• M&A Certified Professional</li>
                  <li>• Valuation Expert Certificate</li>
                </ul>
              </div>
              <div className="bg-blue-900/30 p-4 rounded-xl">
                <h3 className="text-white font-semibold mb-2 flex items-center">
                  🏆 Reconocimientos
                </h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Top M&A Advisor 2023</li>
                  <li>• Brand Consultant of the Year</li>
                  <li>• Speaker en 15+ conferencias</li>
                </ul>
              </div>
            </div>

            {/* Quote */}
            <div className="bg-slate-700/30 p-6 rounded-xl border border-slate-600">
              <div className="flex items-start mb-4">
                <span className="text-4xl text-purple-400 mr-4">"</span>
                <p className="text-gray-300 italic leading-relaxed">
                  Mi pasión es ayudar a empresarios y marcas a descubrir y maximizar su verdadero potencial. 
                  Con más de una década en M&A y estrategia empresarial, he desarrollado metodologías únicas 
                  que combinan análisis financiero riguroso con visión estratégica de largo plazo.
                </p>
              </div>
              <div className="text-right">
                <span className="text-purple-400 font-semibold">- Tamara Navarrete</span>
              </div>
            </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 p-4 rounded-xl border border-cyan-500/30 mt-6">
              <div className="flex items-center justify-center mb-2">
                <span className="text-cyan-400 text-2xl mr-2">🎁</span>
                <h3 className="text-xl font-bold text-white">Consulta Estratégica Gratuita</h3>
              </div>
              <p className="text-cyan-200 text-sm text-center">
                30 minutos de análisis inicial sin costo para nuevos clientes
              </p>
            </div>
          </div>

          {/* Why Choose Section */}
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h2 className="text-3xl font-bold text-white mb-6">
                ¿Por Qué Elegir Tamara?
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-full p-3 flex-shrink-0">
                    <span className="text-white text-xl">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Experiencia Comprobada</h3>
                    <p className="text-gray-300">+10 años especializándose en M&A y valoración de marcas, con +€150M en transacciones exitosas.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full p-3 flex-shrink-0">
                    <span className="text-white text-xl">🚀</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Metodología Propia</h3>
                    <p className="text-gray-300">Framework único que combina IA, análisis financiero y estrategia de marca para maximizar valor.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-full p-3 flex-shrink-0">
                    <span className="text-white text-xl">📊</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Resultados Medibles</h3>
                    <p className="text-gray-300">95% de éxito en procesos M&A y incremento promedio del 70% en valoraciones de marca.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-full p-3 flex-shrink-0">
                    <span className="text-white text-xl">🤝</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Acompañamiento Integral</h3>
                    <p className="text-gray-300">Desde diagnóstico hasta implementación, con seguimiento continuo para garantizar resultados.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial - Single Featured */}
        <div className="mb-16 text-center">
          <div className="bg-slate-800/50 rounded-2xl p-8 max-w-2xl mx-auto border border-white/10">
            <div className="flex justify-center mb-4">
              {[1,2,3,4,5].map((star) => (
                <span key={star} className="text-yellow-400 text-lg">⭐</span>
              ))}
            </div>
            <p className="text-gray-300 italic text-lg mb-6 leading-relaxed">
              "Increíble herramienta. En 3 meses aumentamos el valor de nuestra marca un 55%. Recomiendo totalmente."
            </p>
            <div className="text-white font-bold">María García</div>
            <div className="text-purple-400">CEO, Tech Solutions</div>
          </div>
        </div>

        {/* Servicios Destacados con ID para navegación */}
        <div id="servicios" className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Servicios <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">Estratégicos</span>
            </h2>
            <p className="text-xl text-gray-300">Soluciones personalizadas para cada fase de crecimiento de tu marca</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesDetailed && servicesDetailed.slice(0, 6).map((service) => (
              <div 
                key={service.id}
                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer transform hover:scale-105"
                onClick={() => toggleService(service.id)}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-400">{service.price}</div>
                    <div className="text-xs text-gray-400">{service.duration}</div>
                  </div>
                </div>
                
                <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  {service.features && service.features.slice(0, 3).map((feature, index) => (
                    <div key={index} className="flex items-center text-gray-300 text-sm">
                      <span className="text-green-400 mr-2">✓</span>
                      {feature}
                    </div>
                  ))}
                  {service.features && service.features.length > 3 && (
                    <div className="text-purple-400 text-sm">
                      +{service.features.length - 3} características más...
                    </div>
                  )}
                </div>
                
                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 p-3 rounded-xl border border-purple-500/30 mb-4">
                  <p className="text-purple-200 text-sm">
                    <strong>Ideal para:</strong> {service.ideal}
                  </p>
                </div>
                
                <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300">
                  Solicitar Información
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Services */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10 mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Servicios Detallados
          </h2>

          <div className="space-y-6">
            {/* Service 1 */}
            <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 p-6 rounded-2xl border border-purple-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">🎯 Auditoría Integral de Marca</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-400">€2,500</div>
                  <div className="text-sm text-gray-400">Proyecto completo</div>
                </div>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 mb-4">
                <li>• Análisis profundo de posicionamiento</li>
                <li>• Evaluación de activos intangibles</li>
                <li>• Benchmarking competitivo detallado</li>
                <li>• Roadmap de optimización</li>
                <li>• Informe ejecutivo de 50+ páginas</li>
              </ul>
              <p className="text-purple-200 text-sm">
                ⏱️ Duración: 2-3 semanas | 📋 Incluye presentación ejecutiva
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-gradient-to-r from-blue-900/40 to-cyan-900/40 p-6 rounded-2xl border border-blue-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">🚀 Preparación para M&A</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-400">€5,000</div>
                  <div className="text-sm text-gray-400">Proyecto intensivo</div>
                </div>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 mb-4">
                <li>• Due diligence preparation</li>
                <li>• Optimización de métricas clave</li>
                <li>• Estrategia de negociación</li>
                <li>• Data room organization</li>
                <li>• Coaching para presentaciones</li>
              </ul>
              <p className="text-blue-200 text-sm">
                ⏱️ Duración: 4-6 semanas | 🎯 Objetivo: Maximizar valuación
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 p-6 rounded-2xl border border-green-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">📈 Desarrollo de Negocio</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-400">€450</div>
                  <div className="text-sm text-gray-400">Por hora</div>
                </div>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 mb-4">
                <li>• Estrategias de crecimiento acelerado</li>
                <li>• Optimización de revenue streams</li>
                <li>• Partnerships estratégicos</li>
                <li>• Scaling de operaciones</li>
                <li>• KPIs y métricas de seguimiento</li>
              </ul>
              <p className="text-green-200 text-sm">
                ⏱️ Modalidad: Sesiones 1-2h | 📊 Mínimo: 10 horas mensuales
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-gradient-to-r from-yellow-900/40 to-orange-900/40 p-6 rounded-2xl border border-yellow-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">💰 Preparación para Inversión</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-yellow-400">€3,500</div>
                  <div className="text-sm text-gray-400">Paquete completo</div>
                </div>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 mb-4">
                <li>• Pitch deck optimization</li>
                <li>• Financial modeling avanzado</li>
                <li>• Investor readiness assessment</li>
                <li>• Preparation para due diligence</li>
                <li>• Networking con inversores</li>
              </ul>
              <p className="text-yellow-200 text-sm">
                ⏱️ Duración: 3-4 semanas | 🎯 Enfoque: Rondas Serie A-B
              </p>
            </div>

            {/* Service 5 - Rebranding */}
            <div className="bg-gradient-to-r from-pink-900/40 to-rose-900/40 p-6 rounded-2xl border border-pink-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">🎨 Asesoramiento en Rebranding</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-pink-400">€4,200</div>
                  <div className="text-sm text-gray-400">Transformación completa</div>
                </div>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 mb-4">
                <li>• Análisis integral de identidad actual</li>
                <li>• Estrategia de reposicionamiento</li>
                <li>• Desarrollo de nueva identidad visual</li>
                <li>• Plan de comunicación de cambio</li>
                <li>• Implementación y seguimiento</li>
                <li>• Migración de activos digitales</li>
              </ul>
              <p className="text-pink-200 text-sm">
                ⏱️ Duración: 6-8 semanas | 🎯 Incluye: Guía completa de implementación
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 p-8 rounded-3xl border border-cyan-500/30 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            🎁 Solicita tu Consulta Gratuita
          </h2>
          <p className="text-cyan-200 mb-8 text-lg">
            Análisis inicial de 30 minutos sin costo para evaluar tu situación y diseñar la estrategia perfecta.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.open('https://calendly.com/tamara-navarrete', '_blank')}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              📅 Reservar Consulta Gratuita
            </button>
            <button 
              onClick={() => window.open('mailto:info@yourbrandvalue.com?subject=Consulta%20sobre%20Servicios%20de%20Asesoría&body=Hola,%0A%0AMe%20interesa%20conocer%20más%20sobre%20los%20servicios%20de%20asesoría.%0A%0AMi%20empresa/marca:%0ATipo%20de%20consulta%20que%20necesito:%0A%0AGracias!', '_blank')}
              className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-cyan-400 hover:text-slate-900 transition-all duration-300"
            >
              ✉️ Contactar por Email
            </button>
          </div>

          <div className="mt-6 text-sm text-gray-400">
            ✉️ Email | 💼 LinkedIn: tamara-navarrete-navas-digital-marketing
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const ContactPage = () => {
  const { t } = useTranslations();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
        console.log('Contact form submitted successfully:', result);
      } else {
        const errorData = await response.json();
        console.error('Contact form submission failed:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      {/* Back to Home Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6">
              {t('contact.formTitle')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-900/30 border border-green-500/50 text-green-300 p-4 rounded-xl">
                  <div className="flex items-center">
                    <span className="text-xl mr-3">✅</span>
                    <div>
                      <h4 className="font-semibold">{t('contact.success.title')}</h4>
                      <p className="text-sm">{t('contact.success.message')}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-900/30 border border-red-500/50 text-red-300 p-4 rounded-xl">
                  <div className="flex items-center">
                    <span className="text-xl mr-3">❌</span>
                    <div>
                      <h4 className="font-semibold">{t('contact.error.title')}</h4>
                      <p className="text-sm">{t('contact.error.message')}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-white text-lg font-semibold mb-2">
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none"
                  placeholder={t('contact.form.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-white text-lg font-semibold mb-2">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none"
                  placeholder={t('contact.form.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-white text-lg font-semibold mb-2">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none"
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-white text-lg font-semibold mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none"
                  placeholder={t('contact.form.messagePlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                  isSubmitting 
                    ? 'bg-gray-600 cursor-not-allowed opacity-50' 
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transform hover:scale-105'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('contact.form.sending')}
                  </span>
                ) : (
                  t('contact.form.sendButton')
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const TermsPage = () => {
  const { t, language } = useTranslations();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      {/* Back to Home Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('terms.title', { defaultValue: language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions' })}
          </h1>
          <p className="text-xl text-gray-300">
            {t('terms.lastUpdated', { defaultValue: language === 'es' ? 'Your Brand Value - Última actualización: Enero 2025' : 'Your Brand Value - Last updated: January 2025' })}
          </p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section1.title', { defaultValue: language === 'es' ? '1. Aceptación de los Términos' : '1. Acceptance of Terms' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('terms.section1.content', { defaultValue: language === 'es' ? 'Al utilizar Your Brand Value, aceptas estos términos y condiciones.' : 'By using Your Brand Value, you accept these terms and conditions.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section2.title', { defaultValue: language === 'es' ? '2. Descripción del Servicio' : '2. Service Description' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('terms.section2.content', { defaultValue: language === 'es' ? 'Your Brand Value proporciona una plataforma avanzada de inteligencia de marca con IA.' : 'Your Brand Value provides an advanced AI-powered brand intelligence platform.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section3.title', { defaultValue: language === 'es' ? '3. Limitaciones de Responsabilidad' : '3. Limitation of Liability' })}</h2>
            <ul className="text-gray-300 mb-6 space-y-2">
              <li>{t('terms.section3.item1', { defaultValue: language === 'es' ? '• Los resultados son estimaciones con fines informativos únicamente' : '• Results are estimates for informational purposes only' })}</li>
              <li>{t('terms.section3.item2', { defaultValue: language === 'es' ? '• No garantizamos la precisión absoluta de las valoraciones' : '• We do not guarantee absolute accuracy of valuations' })}</li>
              <li>{t('terms.section3.item3', { defaultValue: language === 'es' ? '• No somos responsables por decisiones tomadas basadas en nuestros resultados' : '• We are not responsible for decisions made based on our results' })}</li>
              <li>{t('terms.section3.item4', { defaultValue: language === 'es' ? '• Para valoraciones oficiales, consulta a profesionales certificados' : '• For official valuations, consult certified professionals' })}</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section4.title', { defaultValue: language === 'es' ? '4. Uso Aceptable' : '4. Acceptable Use' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('terms.section4.content', { defaultValue: language === 'es' ? 'Te comprometes a usar BrandValue de manera responsable y legal.' : 'You agree to use BrandValue responsibly and legally.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section5.title', { defaultValue: language === 'es' ? '5. Propiedad Intelectual' : '5. Intellectual Property' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('terms.section5.content', { defaultValue: language === 'es' ? 'Todos los derechos de propiedad intelectual de Your Brand Value pertenecen a sus creadores.' : 'All intellectual property rights belong to Your Brand Value creators.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section6.title', { defaultValue: language === 'es' ? '6. Modificaciones' : '6. Modifications' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('terms.section6.content', { defaultValue: language === 'es' ? 'Nos reservamos el derecho de modificar estos términos en cualquier momento.' : 'We reserve the right to modify these terms at any time.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('terms.section7.title', { defaultValue: language === 'es' ? '7. Contacto' : '7. Contact' })}</h2>
            <p className="text-gray-300">
              {t('terms.section7.content', { defaultValue: 'Para preguntas sobre estos términos, contáctanos en info@yourbrandvalue.com' })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivacyPage = () => {
  const { t, language } = useTranslations();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      {/* Back to Home Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('privacy.title', { defaultValue: language === 'es' ? 'Política de Privacidad' : 'Privacy Policy' })}
          </h1>
          <p className="text-xl text-gray-300">
            {t('privacy.lastUpdated', { defaultValue: language === 'es' ? 'Your Brand Value - Última actualización: Enero 2025' : 'Your Brand Value - Last updated: January 2025' })}
          </p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section1.title', { defaultValue: language === 'es' ? '1. Información que Recopilamos' : '1. Information We Collect' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('privacy.section1.content', { defaultValue: language === 'es' ? 'Datos de Cálculo: Los datos que ingresas en la calculadora se procesan localmente en tu navegador.' : 'Calculation Data: The data you enter in the calculator is processed locally in your browser.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section2.title', { defaultValue: language === 'es' ? '2. Cómo Usamos tu Información' : '2. How We Use Your Information' })}</h2>
            <ul className="text-gray-300 mb-6 space-y-2">
              <li>{t('privacy.section2.item1', { defaultValue: language === 'es' ? '• Los cálculos se realizan completamente en tu navegador' : '• Calculations are performed entirely in your browser' })}</li>
              <li>{t('privacy.section2.item2', { defaultValue: language === 'es' ? '• No almacenamos datos de valoración en nuestros servidores' : '• We do not store valuation data on our servers' })}</li>
              <li>{t('privacy.section2.item3', { defaultValue: language === 'es' ? '• Solo recopilamos estadísticas anónimas de uso' : '• We only collect anonymous usage statistics' })}</li>
              <li>{t('privacy.section2.item4', { defaultValue: language === 'es' ? '• Los datos de contacto se usan únicamente para responder consultas' : '• Contact data is used solely to respond to inquiries' })}</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section3.title', { defaultValue: language === 'es' ? '3. Cookies y Tecnologías Similares' : '3. Cookies and Similar Technologies' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('privacy.section3.content', { defaultValue: language === 'es' ? 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies de análisis para mejorar la experiencia del usuario.' : 'We use essential cookies for site functionality and analytics cookies to improve user experience.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section4.title', { defaultValue: language === 'es' ? '4. Seguridad de los Datos' : '4. Data Security' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('privacy.section4.content', { defaultValue: language === 'es' ? 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información.' : 'We implement technical and organizational security measures to protect your information.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section5.title', { defaultValue: language === 'es' ? '5. Terceros' : '5. Third Parties' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('privacy.section5.content', { defaultValue: language === 'es' ? 'No compartimos tu información personal con terceros.' : 'We do not share your personal information with third parties.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section6.title', { defaultValue: language === 'es' ? '6. Tus Derechos' : '6. Your Rights' })}</h2>
            <ul className="text-gray-300 mb-6 space-y-2">
              <li>{t('privacy.section6.item1', { defaultValue: language === 'es' ? '• Derecho de acceso a tu información personal' : '• Right of access to your personal information' })}</li>
              <li>{t('privacy.section6.item2', { defaultValue: language === 'es' ? '• Derecho de rectificación y eliminación' : '• Right of rectification and deletion' })}</li>
              <li>{t('privacy.section6.item3', { defaultValue: language === 'es' ? '• Derecho a la portabilidad de datos' : '• Right to data portability' })}</li>
              <li>{t('privacy.section6.item4', { defaultValue: language === 'es' ? '• Derecho a oponerte al procesamiento' : '• Right to object to processing' })}</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section7.title', { defaultValue: language === 'es' ? '7. Retención de Datos' : '7. Data Retention' })}</h2>
            <p className="text-gray-300 mb-6">
              {t('privacy.section7.content', { defaultValue: language === 'es' ? 'Los datos de contacto se conservan por el tiempo necesario para responder a tu consulta.' : 'Contact data is retained for the time necessary to respond to your inquiry.' })}
            </p>

            <h2 className="text-2xl font-bold text-white mb-4">{t('privacy.section8.title', { defaultValue: language === 'es' ? '8. Contacto' : '8. Contact' })}</h2>
            <p className="text-gray-300">
              {t('privacy.section8.content', { defaultValue: 'Para ejercer tus derechos o hacer consultas sobre privacidad, contáctanos en info@yourbrandvalue.com' })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BrandValueCalculator = () => {
  const { t, language } = useTranslations();
  
  // Helper function to construct calculator URL with brand type and preserve query params
  const getCalculatorUrl = (brandType) => {
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams(currentParams);
    
    // Add brandType parameter
    newParams.set('brandType', brandType);
    
    // Add language parameter if not already present
    if (!newParams.has('lang')) {
      newParams.set('lang', language);
    }
    
    return `/calculator?${newParams.toString()}`;
  };
  
  const [formData, setFormData] = useState({
    brandName: '',
    website: '', // New field for website URL
    brandType: 'empresa',
    companySize: 'mediana',
    country: 'ES',
    // Personal Brand Specific Fields
    personalBrandFields: {
      instagramFollowers: '',
      tiktokFollowers: '',
      linkedinFollowers: '',
      youtubeSubscribers: '',
      youtubeViews: '',
      averageEngagementRate: '',
      monthlyCollaborations: '',
      coursesProducts: '',
      avgProductPrice: '',
      speakingFees: '',
      consultingHourlyRate: '',
      personalBrandNiche: 'lifestyle',
      contentType: 'mixed',
      audienceLocation: 'national',
      brandAuthority: '5'
    },
    // Sector-specific fields
    sectorSpecificFields: {
      // Ecommerce
      has_own_website: true,
      sells_on_marketplaces: false,
      marketplaces_used: [],
      marketplace_revenue_percentage: 0,
      has_multiple_brands: false,
      number_of_brands: 1,
      main_sales_channel: 'website',
      
      // Technology
      has_mobile_app: false,
      tech_stack: [],
      has_api: false,
      has_saas_model: false,
      
      // Consulting
      service_types: [],
      client_retention_rate: 0.8,
      average_project_value: 0,
      
      // Healthcare
      has_certifications: false,
      regulatory_compliance: [],
      
      // Manufacturing
      has_own_production: false,
      distribution_channels: [],
      
      // Finance
      licenses_held: [],
      aum: 0,
      
      // Retail
      number_of_locations: 1,
      franchise_model: false
    },
    customParameters: {
      sectorMultiplierAdjustment: 1.0,
      digitalPresenceWeight: 1.0,
      clientBaseWeight: 1.0,
      longevityWeight: 1.0,
      // Personal Brand Parameters
      engagementWeight: 1.0,
      influenceWeight: 1.0,
      monetizationWeight: 1.0,
      authorityWeight: 1.0
    }
  });

  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [brandTypeSelected, setBrandTypeSelected] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [competitiveAnalysis, setCompetitiveAnalysis] = useState(null);
  const [isAnalyzingAI, setIsAnalyzingAI] = useState(false);
  const [userPlan, setUserPlan] = useState('free'); // 'free', 'pro', 'enterprise'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  
  // Lead capture states
  const [leadData, setLeadData] = useState({
    name: '',
    email: ''
  });
  const [leadId, setLeadId] = useState(null);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // Visual & Audio Benchmark states
  const [visualBenchmark, setVisualBenchmark] = useState(null);
  const [isAnalyzingVisual, setIsAnalyzingVisual] = useState(false);
  
  // AI Recommendations states
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [brandColors, setBrandColors] = useState([]);
  const [selectedFont, setSelectedFont] = useState('');
  const [showVisualAnalysis, setShowVisualAnalysis] = useState(false);

  // Brand Audit states
  const [brandAudit, setBrandAudit] = useState(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // Executive Report states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  
  // Blockchain Certificate states
  const [blockchainCertificate, setBlockchainCertificate] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  
  // Checkout states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Lead capture function
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingLead(true);
    setLeadError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData),
      });

      if (!response.ok) {
        throw new Error('Error al guardar la información');
      }

      const result = await response.json();
      setLeadId(result.id);
      setLeadSubmitted(true);
      setActiveStep(4); // Move to results step
    } catch (error) {
      setLeadError('Error al procesar la información. Por favor intenta nuevamente.');
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  // Handle One-off Report Purchase (using unified checkout endpoint)
  const handleOneoffCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: 'informe_unico', // Use the unified package system
          origin_url: originUrl,
          language: language,
          metadata: {
            source: 'one_off_report',
            package_name: 'informe_unico',
            user_language: language
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (response.ok && data.success && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Failed to create one-off checkout session');
      }
    } catch (error) {
      console.error('Error creating one-off checkout:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Visual & Audio Benchmark Analysis
  const analyzeVisualBenchmark = () => {
    setIsAnalyzingVisual(true);
    
    setTimeout(() => {
      const mockAnalysis = generateVisualBenchmarkAnalysis();
      setVisualBenchmark(mockAnalysis);
      setIsAnalyzingVisual(false);
      setShowVisualAnalysis(true);
    }, 3000);
  };

  const generateVisualBenchmarkAnalysis = () => {
    const successCases = [
      {
        brand: "Zara",
        sector: "Fashion",
        elements: {
          logo: "Minimalista, sans-serif, alta legibilidad",
          colors: ["#000000", "#FFFFFF", "#F5F5F5"],
          typography: "Didot, elegante y sofisticada",
          performance: 95
        },
        insights: "Identidad visual cohesiva que transmite elegancia y modernidad"
      },
      {
        brand: "Airbnb",
        sector: "Technology",
        elements: {
          logo: "Símbolo 'Bélo' distintivo y memorable",
          colors: ["#FF5A5F", "#00A699", "#FC642D", "#484848"],
          typography: "Circular, amigable y accesible",
          performance: 92
        },
        insights: "Paleta de colores vibrante que transmite confianza y comunidad"
      },
      {
        brand: "Spotify",
        sector: "Entertainment",
        elements: {
          logo: "Ondas sonoras verdes distintivas",
          colors: ["#1DB954", "#191414", "#FFFFFF"],
          typography: "Spotify Circular, moderna y legible",
          performance: 89
        },
        insights: "Verde distintivo que se asocia instantáneamente con música"
      }
    ];

    // Simular análisis de elementos del usuario
    const userAnalysis = {
      logoScore: Math.floor(Math.random() * 30) + 60, // 60-90
      colorScore: Math.floor(Math.random() * 25) + 65, // 65-90
      typographyScore: Math.floor(Math.random() * 20) + 70, // 70-90
      cohesionScore: Math.floor(Math.random() * 35) + 55 // 55-90
    };

    const recommendations = generateVisualRecommendations(userAnalysis, successCases);
    
    return {
      userAnalysis,
      successCases,
      recommendations,
      overallScore: Math.round((userAnalysis.logoScore + userAnalysis.colorScore + 
                               userAnalysis.typographyScore + userAnalysis.cohesionScore) / 4)
    };
  };

  const generateVisualRecommendations = (analysis, cases) => {
    const recommendations = [];
    
    if (analysis.logoScore < 75) {
      recommendations.push({
        category: "Logo",
        priority: "Alta",
        suggestion: "Simplifica el diseño del logo para mayor impacto y legibilidad",
        example: "Como Zara: logotipo minimalista que funciona en cualquier tamaño",
        improvement: "+15 puntos en reconocimiento"
      });
    }

    if (analysis.colorScore < 80) {
      recommendations.push({
        category: "Colores",
        priority: "Media", 
        suggestion: "Desarrolla una paleta de colores más cohesiva y distintiva",
        example: "Como Spotify: color verde icónico que se asocia con la marca",
        improvement: "+20 puntos en memorabilidad"
      });
    }

    if (analysis.typographyScore < 75) {
      recommendations.push({
        category: "Tipografía",
        priority: "Media",
        suggestion: "Adopta una tipografía personalizada que refleje la personalidad de marca",
        example: "Como Airbnb: tipografía Circular que transmite cercanía y confianza",
        improvement: "+12 puntos en profesionalidad"
      });
    }

    if (analysis.cohesionScore < 70) {
      recommendations.push({
        category: "Cohesión Visual",
        priority: "Alta",
        suggestion: "Crea un sistema visual más cohesivo entre todos los elementos",
        example: "Consistencia visual en todos los puntos de contacto",
        improvement: "+25 puntos en reconocimiento de marca"
      });
    }

    return recommendations;
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedLogo(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorAdd = (color) => {
    // Ensure brandColors is defined and is an array
    const currentColors = Array.isArray(brandColors) ? brandColors : [];
    
    if (currentColors.length < 5 && !currentColors.includes(color)) {
      setBrandColors([...currentColors, color]);
    }
  };

  const handleColorRemove = (colorToRemove) => {
    // Ensure brandColors is defined and is an array
    const currentColors = Array.isArray(brandColors) ? brandColors : [];
    setBrandColors(currentColors.filter(color => color !== colorToRemove));
  };

  // Check for demo data on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true') {
      const demoData = localStorage.getItem('demoData');
      if (demoData) {
        setFormData(JSON.parse(demoData));
        localStorage.removeItem('demoData');
      }
    }
  }, []);

  const subscriptionPlans = {
    free: {
      name: 'Calculadora Básica',
      monthlyPrice: 0,
      yearlyPrice: 0,
      period: 'Gratis',
      features: [
        'Valoración básica de marca',
        'Análisis de métricas principales',
        'Comparación sectorial básica',
        'Informe PDF básico',
        'Soporte por email'
      ],
      limitations: [
        'Sin análisis predictivo con IA',
        'Sin análisis de sentimiento',
        'Sin benchmarking competitivo',
        'Máximo 3 valoraciones por mes'
      ],
      color: 'gray',
      popular: false
    },
    pro: {
      name: 'Inteligencia Profesional',
      monthlyPrice: 29,
      yearlyPrice: 278,
      period: '/mes',
      features: [
        'Todo lo incluido en Básica',
        '🤖 Análisis predictivo con IA (5 años)',
        '💭 Análisis de sentimiento en tiempo real',
        '🏆 Benchmarking competitivo automatizado',
        '📊 Informes avanzados con recomendaciones',
        '📈 Proyecciones de crecimiento',
        '⚙️ Parámetros personalizables',
        'Valoraciones ilimitadas',
        'Soporte prioritario'
      ],
      limitations: [],
      color: 'purple',
      popular: true
    },
    enterprise: {
      name: 'Enterprise',
      monthlyPrice: 99,
      yearlyPrice: 950,
      period: '/mes',
      features: [
        'Todo lo incluido en Profesional',
        '🏢 Análisis multi-empresa',
        '🔗 API de integración',
        '👥 Cuentas de equipo (hasta 10 usuarios)',
        '📋 Informes personalizados con branding',
        '💬 Soporte dedicado prioritario',
        '🔒 Seguridad empresarial avanzada',
        '📊 Dashboard ejecutivo',
        'Onboarding personalizado'
      ],
      limitations: [],
      color: 'gold',
      popular: false
    }
  };

  const payPerReport = {
    name: 'Informe Premium Individual',
    price: 19,
    period: '/informe',
    description: 'Análisis completo con IA una sola vez',
    features: [
      '🤖 Análisis predictivo completo',
      '💭 Análisis de sentimiento detallado',
      '🏆 Benchmarking competitivo',
      '📋 Informe PDF premium (20+ páginas)',
      'Válido por 30 días'
    ]
  };

  const checkFeatureAccess = (feature) => {
    if (userPlan === 'free') {
      const freeFeatures = ['basic-calculation', 'basic-report', 'basic-comparison'];
      return freeFeatures.includes(feature);
    }
    return true; // Pro y Enterprise tienen acceso completo
  };

  const getUsageLimit = () => {
    if (userPlan === 'free') return { limit: 3, used: 0 }; // Simulamos 0 usado
    return { limit: 'unlimited', used: 0 };
  };

  const countries = [
    { code: 'ES', name: 'España', multiplier: 1.0, currency: 'EUR' },
    { code: 'FR', name: 'Francia', multiplier: 1.1, currency: 'EUR' },
    { code: 'DE', name: 'Alemania', multiplier: 1.2, currency: 'EUR' },
    { code: 'IT', name: 'Italia', multiplier: 0.9, currency: 'EUR' },
    { code: 'US', name: 'Estados Unidos', multiplier: 1.5, currency: 'USD' },
    { code: 'UK', name: 'Reino Unido', multiplier: 1.3, currency: 'GBP' },
    { code: 'MX', name: 'México', multiplier: 0.7, currency: 'USD' },
    { code: 'AR', name: 'Argentina', multiplier: 0.6, currency: 'USD' },
    { code: 'CO', name: 'Colombia', multiplier: 0.65, currency: 'USD' },
    { code: 'CL', name: 'Chile', multiplier: 0.8, currency: 'USD' },
    { code: 'BR', name: 'Brasil', multiplier: 0.75, currency: 'USD' }
  ];

  const companySizes = [
    { value: 'micro', label: 'Microempresa (1-10 empleados)', multiplier: 0.8 },
    { value: 'pequeña', label: 'Pequeña empresa (11-50 empleados)', multiplier: 0.9 },
    { value: 'mediana', label: 'Mediana empresa (51-250 empleados)', multiplier: 1.0 },
    { value: 'grande', label: 'Gran empresa (250+ empleados)', multiplier: 1.2 }
  ];

  const sectors = [
    { value: 'tecnologia', label: 'Tecnología', multiplier: { B2B: 8, B2C: 6 }, benchmarks: { revenue: 1000000, clients: 150, digital: 50000 } },
    { value: 'servicios', label: 'Servicios Profesionales', multiplier: { B2B: 5, B2C: 3.5 }, benchmarks: { revenue: 500000, clients: 75, digital: 25000 } },
    { value: 'retail', label: 'Retail / E-commerce', multiplier: { B2B: 4, B2C: 3 }, benchmarks: { revenue: 2000000, clients: 5000, digital: 100000 } },
    { value: 'manufactura', label: 'Manufactura', multiplier: { B2B: 3, B2C: 2.5 }, benchmarks: { revenue: 5000000, clients: 200, digital: 15000 } },
    { value: 'salud', label: 'Salud y Bienestar', multiplier: { B2B: 6, B2C: 4.5 }, benchmarks: { revenue: 800000, clients: 300, digital: 35000 } },
    { value: 'finanzas', label: 'Finanzas', multiplier: { B2B: 5.5, B2C: 4 }, benchmarks: { revenue: 1500000, clients: 100, digital: 20000 } },
    { value: 'educacion', label: 'Educación', multiplier: { B2B: 4.5, B2C: 3.5 }, benchmarks: { revenue: 600000, clients: 500, digital: 30000 } },
    { value: 'entretenimiento', label: 'Entretenimiento', multiplier: { B2B: 3.5, B2C: 4.5 }, benchmarks: { revenue: 1200000, clients: 2000, digital: 150000 } },
    { value: 'alimentacion', label: 'Alimentación', multiplier: { B2B: 3.5, B2C: 3 }, benchmarks: { revenue: 3000000, clients: 1000, digital: 50000 } },
    { value: 'consultoria', label: 'Consultoría', multiplier: { B2B: 6, B2C: 4 }, benchmarks: { revenue: 400000, clients: 25, digital: 15000 } },
    { value: 'inmobiliaria', label: 'Inmobiliaria', multiplier: { B2B: 4.5, B2C: 3.5 }, benchmarks: { revenue: 2500000, clients: 80, digital: 25000 } },
    { value: 'marketing', label: 'Marketing y Publicidad', multiplier: { B2B: 5.5, B2C: 4 }, benchmarks: { revenue: 750000, clients: 40, digital: 40000 } }
  ];

  // Personal Brand Specific Sectors
  const personalBrandSectors = [
    { value: 'lifestyle', label: 'Lifestyle & Bienestar', multiplier: 4.5, benchmarks: { followers: 100000, engagement: 3.5, monthly_collabs: 5, avg_product_price: 150 } },
    { value: 'fitness', label: 'Fitness & Salud', multiplier: 5.2, benchmarks: { followers: 150000, engagement: 4.2, monthly_collabs: 8, avg_product_price: 200 } },
    { value: 'business_coach', label: 'Business Coach', multiplier: 6.8, benchmarks: { followers: 50000, engagement: 2.8, monthly_collabs: 3, avg_product_price: 800 } },
    { value: 'tech_influencer', label: 'Tech Influencer', multiplier: 5.8, benchmarks: { followers: 75000, engagement: 3.2, monthly_collabs: 4, avg_product_price: 350 } },
    { value: 'fashion_beauty', label: 'Fashion & Beauty', multiplier: 4.8, benchmarks: { followers: 200000, engagement: 4.8, monthly_collabs: 12, avg_product_price: 120 } },
    { value: 'food_travel', label: 'Food & Travel', multiplier: 4.2, benchmarks: { followers: 120000, engagement: 3.8, monthly_collabs: 6, avg_product_price: 180 } },
    { value: 'education_online', label: 'Educación Online', multiplier: 6.2, benchmarks: { followers: 60000, engagement: 2.5, monthly_collabs: 2, avg_product_price: 500 } },
    { value: 'entertainment', label: 'Entretenimiento', multiplier: 3.8, benchmarks: { followers: 250000, engagement: 5.2, monthly_collabs: 8, avg_product_price: 80 } },
    { value: 'finance_investing', label: 'Finanzas e Inversión', multiplier: 7.5, benchmarks: { followers: 80000, engagement: 2.2, monthly_collabs: 3, avg_product_price: 1200 } },
    { value: 'parenting', label: 'Crianza & Familia', multiplier: 4.0, benchmarks: { followers: 90000, engagement: 4.5, monthly_collabs: 6, avg_product_price: 100 } },
    { value: 'gaming', label: 'Gaming & Esports', multiplier: 4.5, benchmarks: { followers: 300000, engagement: 6.0, monthly_collabs: 10, avg_product_price: 150 } },
    { value: 'art_design', label: 'Arte & Diseño', multiplier: 3.5, benchmarks: { followers: 80000, engagement: 3.0, monthly_collabs: 4, avg_product_price: 250 } }
  ];

  // Content Types for Personal Brands
  const contentTypes = [
    { value: 'mixed', label: 'Contenido Mixto', multiplier: 1.0 },
    { value: 'video_dominant', label: 'Video Dominante (YouTube, TikTok)', multiplier: 1.3 },
    { value: 'photo_stories', label: 'Fotos e Historias (Instagram)', multiplier: 1.1 },
    { value: 'written_content', label: 'Contenido Escrito (Blog, LinkedIn)', multiplier: 1.2 },
    { value: 'live_streaming', label: 'Live Streaming & Webinars', multiplier: 1.4 },
    { value: 'courses_digital', label: 'Cursos y Productos Digitales', multiplier: 1.6 }
  ];

  // Audience Location Impact
  const audienceLocations = [
    { value: 'local', label: 'Local/Regional', multiplier: 0.8 },
    { value: 'national', label: 'Nacional', multiplier: 1.0 },
    { value: 'international', label: 'Internacional', multiplier: 1.3 },
    { value: 'global', label: 'Global (Multi-idioma)', multiplier: 1.5 }
  ];

  // AI-Powered Predictive Analysis
  const generatePredictiveAnalysis = (brandValue, formData) => {
    // Handle personal brands differently
    if (formData.brandType === 'personal') {
      return generatePersonalBrandPredictiveAnalysis(brandValue, formData);
    }
    
    const sector = sectors.find(s => s.value === formData.sector);
    const country = countries.find(c => c.code === formData.country);
    const years = parseFloat(formData.yearsInMarket) || 0;
    const recognition = parseFloat(formData.brandRecognition) || 5;
    
    // AI-simulated growth predictions
    const currentGrowthRate = formData.businessType === 'B2B' ? 0.15 : 0.12;
    const sectorGrowthRate = {
      'tecnologia': 0.25,
      'finanzas': 0.08,
      'salud': 0.18,
      'consultoria': 0.12,
      'retail': 0.06,
      'manufactura': 0.04,
      'educacion': 0.10,
      'entretenimiento': 0.15,
      'alimentacion': 0.05,
      'inmobiliaria': 0.07,
      'marketing': 0.20,
      'servicios': 0.09
    };
    
    const baseGrowthRate = sectorGrowthRate[formData.sector] || 0.08;
    const countryMultiplier = country.multiplier;
    const brandMaturityFactor = Math.min(years / 10, 1);
    
    // Predictive scenarios
    const scenarios = {
      conservative: {
        growthRate: baseGrowthRate * 0.7 * countryMultiplier,
        confidence: 85,
        description: 'Escenario conservador basado en tendencias históricas'
      },
      realistic: {
        growthRate: baseGrowthRate * countryMultiplier,
        confidence: 70,
        description: 'Proyección realista considerando factores de mercado'
      },
      optimistic: {
        growthRate: baseGrowthRate * 1.4 * countryMultiplier,
        confidence: 55,
        description: 'Escenario optimista con estrategias de crecimiento acelerado'
      }
    };
    
    // Generate 5-year predictions
    const predictions = {};
    Object.keys(scenarios).forEach(scenario => {
      predictions[scenario] = [];
      for (let year = 1; year <= 5; year++) {
        const compoundGrowth = Math.pow(1 + scenarios[scenario].growthRate, year);
        const predictedValue = Math.round(brandValue * compoundGrowth);
        predictions[scenario].push({
          year: new Date().getFullYear() + year,
          value: predictedValue,
          growth: Math.round((compoundGrowth - 1) * 100)
        });
      }
    });
    
    // AI insights and recommendations
    const insights = [];
    const strategicRecommendations = [];
    
    if (baseGrowthRate > 0.15) {
      insights.push('Tu sector muestra tendencias de alto crecimiento');
      strategicRecommendations.push('Invierte agresivamente en expansión para capitalizar el crecimiento sectorial');
    }
    
    if (recognition >= 8) {
      insights.push('Alto reconocimiento de marca es un activo valioso para crecimiento futuro');
      strategicRecommendations.push('Leverage tu reputación para expansión a nuevos mercados o productos');
    } else if (recognition <= 5) {
      insights.push('El reconocimiento de marca limitado puede frenar el crecimiento');
      strategicRecommendations.push('Prioriza estrategias de branding y marketing para aumentar visibilidad');
    }
    
    if (formData.brandType === 'personal') {
      insights.push('Las marcas personales tienden a tener mayor volatilidad pero mayor potencial de crecimiento exponencial');
      strategicRecommendations.push('Construye sistemas y procesos para reducir dependencia personal y escalar');
    }
    
    if (countryMultiplier > 1.2) {
      insights.push('Tu mercado geográfico ofrece valoraciones premium');
      strategicRecommendations.push('Considera mantener operaciones en mercados de alto valor y explorar expansión similar');
    }
    
    return {
      scenarios,
      predictions,
      insights,
      strategicRecommendations,
      riskFactors: [
        'Cambios regulatorios en el sector',
        'Disrupciones tecnológicas',
        'Fluctuaciones económicas globales',
        'Competencia emergente',
        'Cambios en comportamiento del consumidor'
      ],
      confidenceLevel: 75
    };
  };

  // AI-Powered Sentiment Analysis Simulation
  const generateSentimentAnalysis = (formData) => {
    // Handle personal brands differently
    if (formData.brandType === 'personal') {
      return generatePersonalBrandSentimentAnalysis(formData);
    }
    
    const sector = sectors.find(s => s.value === formData.sector);
    const followers = parseFloat(formData.socialFollowers) || 0;
    const recognition = parseFloat(formData.brandRecognition) || 5;
    
    // Simulate sentiment analysis based on brand metrics
    const baseSentiment = (recognition / 10) * 100;
    const followerImpact = Math.min((followers / 100000) * 10, 15);
    const sectorSentiment = {
      'tecnologia': 5,
      'salud': 10,
      'educacion': 8,
      'finanzas': -5,
      'consultoria': 3,
      'retail': 0,
      'manufactura': -2,
      'entretenimiento': 8,
      'alimentacion': 2,
      'inmobiliaria': -3,
      'marketing': 5,
      'servicios': 1
    };
    
    const overallSentiment = Math.min(Math.max(baseSentiment + followerImpact + (sectorSentiment[formData.sector] || 0), 0), 100);
    
    // Generate sentiment breakdown
    const positive = Math.round(overallSentiment * 0.6);
    const neutral = Math.round((100 - overallSentiment) * 0.7);
    const negative = 100 - positive - neutral;
    
    // Simulated social media mentions
    const mentions = Math.round(followers * 0.1 + Math.random() * 1000);
    const engagementRate = (recognition / 10) * 5 + Math.random() * 2;
    
    // Trending topics simulation
    const trendingTopics = [
      { topic: 'Calidad del servicio', sentiment: positive > 60 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.3) },
      { topic: 'Innovación', sentiment: formData.sector === 'tecnologia' ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.2) },
      { topic: 'Atención al cliente', sentiment: recognition > 7 ? 'positive' : 'negative', mentions: Math.round(mentions * 0.25) },
      { topic: 'Precio/Valor', sentiment: formData.businessType === 'B2B' ? 'neutral' : 'negative', mentions: Math.round(mentions * 0.15) },
      { topic: 'Experiencia de usuario', sentiment: positive > 70 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.1) }
    ];
    
    // AI-powered insights
    const sentimentInsights = [];
    if (positive > 70) {
      sentimentInsights.push('Excelente percepción de marca en redes sociales');
      sentimentInsights.push('Mantén la estrategia actual de comunicación');
    } else if (positive < 50) {
      sentimentInsights.push('Oportunidad de mejora en percepción de marca');
      sentimentInsights.push('Considera campañas de gestión de reputación');
    }
    
    if (negative > 20) {
      sentimentInsights.push('Atención requerida: nivel alto de comentarios negativos');
    }
    
    return {
      overall: {
        score: Math.round(overallSentiment),
        trend: positive > 60 ? 'rising' : positive < 40 ? 'declining' : 'stable',
        classification: positive > 70 ? 'Muy Positivo' : positive > 50 ? 'Positivo' : positive > 30 ? 'Neutral' : 'Negativo'
      },
      breakdown: { positive, neutral, negative },
      metrics: {
        totalMentions: mentions,
        engagementRate: Math.round(engagementRate * 10) / 10,
        reach: Math.round(mentions * engagementRate * 10),
        influence: Math.round((followers / 1000) * (positive / 100) * 100) / 100
      },
      trendingTopics,
      sentimentInsights,
      recommendedActions: [
        positive < 50 ? 'Implementar estrategia de gestión de crisis de reputación' : 'Amplificar contenido positivo',
        'Monitorear menciones en tiempo real',
        'Responder proactivamente a comentarios negativos',
        'Crear contenido que genere engagement positivo'
      ]
    };
  };

  const generatePersonalBrandSentimentAnalysis = (formData) => {
    const personalFields = formData.personalBrandFields;
    const personalSector = personalBrandSectors.find(s => s.value === personalFields.personalBrandNiche);
    
    // Calculate total followers
    const totalFollowers = (parseFloat(personalFields.instagramFollowers) || 0) +
                          (parseFloat(personalFields.tiktokFollowers) || 0) +
                          (parseFloat(personalFields.linkedinFollowers) || 0) +
                          (parseFloat(personalFields.youtubeSubscribers) || 0);
    
    const engagementRate = parseFloat(personalFields.averageEngagementRate) || 0;
    const brandAuthority = parseFloat(personalFields.brandAuthority) || 5;
    
    // Personal brand sentiment calculation
    const baseSentiment = (brandAuthority / 10) * 100;
    const engagementBonus = Math.min((engagementRate / 10) * 20, 25);
    const followersImpact = Math.min((totalFollowers / 100000) * 15, 20);
    
    const overallSentiment = Math.min(Math.max(baseSentiment + engagementBonus + followersImpact, 0), 100);
    
    // Generate sentiment breakdown
    const positive = Math.round(overallSentiment * 0.65);
    const neutral = Math.round((100 - overallSentiment) * 0.6);
    const negative = 100 - positive - neutral;
    
    // Simulated mentions based on following
    const mentions = Math.round(totalFollowers * 0.15 + Math.random() * 500);
    const reach = Math.round(mentions * engagementRate * 12);
    
    // Personal brand trending topics
    const trendingTopics = [
      { topic: 'Autenticidad', sentiment: positive > 60 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.25) },
      { topic: 'Contenido de calidad', sentiment: engagementRate > 3 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.2) },
      { topic: 'Interacción con audiencia', sentiment: engagementRate > 4 ? 'positive' : 'negative', mentions: Math.round(mentions * 0.2) },
      { topic: 'Productos/Servicios', sentiment: parseFloat(personalFields.avgProductPrice) > 100 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.15) },
      { topic: 'Inspiración/Motivación', sentiment: positive > 70 ? 'positive' : 'neutral', mentions: Math.round(mentions * 0.2) }
    ];
    
    // Personal brand insights
    const sentimentInsights = [];
    if (positive > 75) {
      sentimentInsights.push('Excelente conexión emocional con tu audiencia');
      sentimentInsights.push('Tu autenticidad resuena fuertemente');
    } else if (positive < 50) {
      sentimentInsights.push('Oportunidad de mejorar la conexión con tu audiencia');
      sentimentInsights.push('Considera ser más auténtico y personal en tu contenido');
    }
    
    if (engagementRate > 5) {
      sentimentInsights.push('Tu audiencia está altamente comprometida');
    } else if (engagementRate < 2) {
      sentimentInsights.push('Necesitas mejorar la interacción con tu audiencia');
    }
    
    return {
      overall: {
        score: Math.round(overallSentiment),
        trend: positive > 65 ? 'rising' : positive < 45 ? 'declining' : 'stable',
        classification: positive > 75 ? 'Muy Positivo' : positive > 60 ? 'Positivo' : positive > 40 ? 'Neutral' : 'Negativo'
      },
      breakdown: { positive, neutral, negative },
      metrics: {
        totalMentions: mentions,
        engagementRate: Math.round(engagementRate * 10) / 10,
        reach: reach,
        influence: Math.round((totalFollowers / 1000) * (positive / 100) * 100) / 100
      },
      trendingTopics,
      sentimentInsights,
      recommendedActions: [
        positive < 60 ? 'Implementar estrategia de contenido más auténtico' : 'Mantener la autenticidad actual',
        'Responder a todos los comentarios para aumentar engagement',
        'Compartir más contenido personal y detrás de escenas',
        'Crear contenido que genere conversación'
      ]
    };
  };

  // AI-Powered Competitive Analysis
  const generateCompetitiveAnalysis = (brandValue, formData) => {
    const sector = sectors.find(s => s.value === formData.sector);
    const revenue = parseFloat(formData.revenue) || 0;
    
    // Simulated competitor data based on sector and size
    const competitors = [];
    const competitorNames = {
      'tecnologia': ['TechCorp', 'InnovateSoft', 'DataFlow Solutions', 'CloudTech Pro', 'AI Systems'],
      'finanzas': ['FinanceFirst', 'Capital Solutions', 'Investment Pro', 'Banking Plus', 'Credit Expert'],
      'salud': ['HealthTech', 'MedSolutions', 'WellnessFirst', 'HealthCare Pro', 'Medical Innovation'],
      'consultoria': ['Strategy Plus', 'Consulting Pro', 'Business Solutions', 'Expert Advisory', 'Growth Partners'],
      'retail': ['RetailMax', 'ShopSmart', 'Commerce Pro', 'MarketLeader', 'Consumer First'],
      'manufactura': ['IndustrialCorp', 'Manufacturing Pro', 'Production Plus', 'Factory Solutions', 'Industrial Leader'],
      'educacion': ['EduTech', 'Learning Solutions', 'Academic Pro', 'Education Plus', 'Knowledge Systems'],
      'entretenimiento': ['Entertainment Pro', 'Media Solutions', 'Content Plus', 'Show Business', 'Creative Studios'],
      'alimentacion': ['FoodTech', 'Culinary Solutions', 'Restaurant Pro', 'Food Innovation', 'Gastronomy Plus'],
      'inmobiliaria': ['PropertyPro', 'Real Estate Plus', 'Housing Solutions', 'Property Expert', 'Realty Group'],
      'marketing': ['MarketingPro', 'Ad Solutions', 'Creative Agency', 'Brand Builders', 'Digital Marketing Plus'],
      'servicios': ['ServicePro', 'Professional Solutions', 'Service Excellence', 'Business Services', 'Support Plus']
    };
    
    const sectorCompetitors = competitorNames[formData.sector] || competitorNames['servicios'];
    
    for (let i = 0; i < 5; i++) {
      const competitorRevenue = revenue * (0.5 + Math.random() * 1.5);
      const competitorValue = competitorRevenue * (sector.multiplier[formData.businessType] || 4) * (0.8 + Math.random() * 0.4);
      const marketShare = Math.random() * 25 + 5;
      
      competitors.push({
        name: sectorCompetitors[i],
        estimatedValue: Math.round(competitorValue),
        revenue: Math.round(competitorRevenue),
        marketShare: Math.round(marketShare * 10) / 10,
        strengths: generateCompetitorStrengths(formData.sector),
        weaknesses: generateCompetitorWeaknesses(formData.sector),
        digitalPresence: Math.round((Math.random() * 50 + 25) * 10) / 10,
        brandRecognition: Math.round(Math.random() * 10) + 1,
        position: brandValue > competitorValue ? 'below' : 'above'
      });
    }
    
    // Sort competitors by value
    competitors.sort((a, b) => b.estimatedValue - a.estimatedValue);
    
    // Calculate market position
    const totalMarketValue = competitors.reduce((sum, comp) => sum + comp.estimatedValue, 0) + brandValue;
    const marketPosition = competitors.filter(comp => comp.estimatedValue > brandValue).length + 1;
    const marketShare = Math.round((brandValue / totalMarketValue) * 100 * 10) / 10;
    
    // AI-powered competitive insights
    const competitiveInsights = [];
    const strategicRecommendations = [];
    
    if (marketPosition <= 2) {
      competitiveInsights.push('Posición de liderazgo en el mercado');
      strategicRecommendations.push('Mantén ventajas competitivas y explora nuevos mercados');
    } else if (marketPosition > 3) {
      competitiveInsights.push('Oportunidad de mejora en posición competitiva');
      strategicRecommendations.push('Analiza estrategias de los líderes del mercado');
    }
    
    const avgDigitalPresence = competitors.reduce((sum, comp) => sum + comp.digitalPresence, 0) / competitors.length;
    const userDigitalScore = ((parseFloat(formData.socialFollowers) || 0) / 100000) * 100;
    
    if (userDigitalScore < avgDigitalPresence) {
      competitiveInsights.push('Presencia digital por debajo del promedio competitivo');
      strategicRecommendations.push('Invierte en marketing digital y construcción de audiencia');
    }
    
    return {
      marketPosition,
      totalCompetitors: competitors.length,
      marketShare,
      competitors,
      benchmarks: {
        avgValue: Math.round(competitors.reduce((sum, comp) => sum + comp.estimatedValue, 0) / competitors.length),
        avgRevenue: Math.round(competitors.reduce((sum, comp) => sum + comp.revenue, 0) / competitors.length),
        avgDigitalPresence: Math.round(avgDigitalPresence * 10) / 10,
        avgBrandRecognition: Math.round((competitors.reduce((sum, comp) => sum + comp.brandRecognition, 0) / competitors.length) * 10) / 10
      },
      competitiveInsights,
      strategicRecommendations,
      threats: [
        'Nuevos entrantes con tecnología disruptiva',
        'Competidores establecidos con mayor capital',
        'Cambios en preferencias del consumidor',
        'Guerra de precios en el sector'
      ],
      opportunities: [
        'Segmentos de mercado desatendidos',
        'Alianzas estratégicas',
        'Expansión geográfica',
        'Innovación en productos/servicios'
      ]
    };
  };

  const generateCompetitorStrengths = (sector) => {
    const strengthsPool = {
      'tecnologia': ['Innovación tecnológica', 'Talento técnico', 'Escalabilidad', 'Productos disruptivos'],
      'finanzas': ['Capital sólido', 'Regulación favorable', 'Red de sucursales', 'Productos diversificados'],
      'salud': ['Investigación avanzada', 'Equipo médico', 'Certificaciones', 'Experiencia clínica'],
      'consultoria': ['Expertise sectorial', 'Red de contactos', 'Metodologías probadas', 'Equipo senior'],
      'default': ['Marca reconocida', 'Equipo experimentado', 'Recursos financieros', 'Base de clientes sólida']
    };
    
    const strengths = strengthsPool[sector] || strengthsPool['default'];
    return [strengths[Math.floor(Math.random() * strengths.length)], strengths[Math.floor(Math.random() * strengths.length)]];
  };

  // Upgrade Modal Component
  const UpgradeModal = () => {
    if (!showUpgradeModal) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
          <div className="p-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-4xl font-bold text-white">
                Desbloquea el Poder de la IA
              </h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {Object.entries(subscriptionPlans).map(([key, plan]) => (
                <div
                  key={key}
                  className={`relative p-6 rounded-2xl border ${
                    plan.popular 
                      ? 'border-purple-500 bg-purple-900/20' 
                      : 'border-white/20 bg-slate-700/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        {t('pricing.mostPopular')}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-white mb-1">
                      €{plan.price}
                      <span className="text-lg text-gray-400">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <span className="text-green-400 mr-2 mt-1">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.limitations.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-red-400 font-semibold mb-2">Limitaciones:</h4>
                      <ul className="space-y-1">
                        {plan.limitations.map((limitation, index) => (
                          <li key={index} className="flex items-start text-red-300 text-sm">
                            <span className="text-red-400 mr-2 mt-1">×</span>
                            {limitation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                        : key === 'free'
                        ? 'bg-gray-600 text-gray-300 cursor-default'
                        : 'border border-white/20 text-white hover:bg-white/10'
                    }`}
                    onClick={() => {
                      if (key !== 'free') {
                        setUserPlan(key);
                        setShowUpgradeModal(false);
                        alert(`¡Gracias! Has actualizado a ${plan.name}. En producción esto procesaría el pago.`);
                      }
                    }}
                    disabled={key === 'free'}
                  >
                    {key === 'free' ? 'Plan Actual' : `Actualizar a ${plan.name}`}
                  </button>
                </div>
              ))}
            </div>

            {/* Pay Per Report Option */}
            <div className="bg-gradient-to-r from-blue-900/50 to-cyan-900/50 p-6 rounded-2xl border border-blue-500/30 mb-6">
              <h3 className="text-xl font-bold text-white mb-4 text-center">
                ¿Solo necesitas un informe? 
              </h3>
              <div className="text-center mb-4">
                <div className="text-3xl font-bold text-blue-400 mb-2">
                  €{payPerReport.price}{payPerReport.period}
                </div>
                <p className="text-gray-300">{payPerReport.description}</p>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {payPerReport.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-blue-200">
                    <span className="text-blue-400 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  setShowUpgradeModal(false);
                  handleOneoffCheckout();
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Procesando...' : 'Comprar Informe Premium'}
              </button>
            </div>

            <div className="text-center text-gray-400">
              <p className="mb-2">🔒 Pago seguro con encriptación SSL</p>
              <p>💳 Aceptamos todas las tarjetas principales y PayPal</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const generateCompetitorWeaknesses = (sector) => {
    const weaknessesPool = {
      'tecnologia': ['Dependencia tecnológica', 'Alta rotación de personal', 'Costos de I+D', 'Competencia feroz'],
      'finanzas': ['Regulación estricta', 'Riesgos crediticios', 'Costos operativos', 'Competencia digital'],
      'salud': ['Regulación compleja', 'Costos de investigación', 'Tiempo de desarrollo', 'Responsabilidad legal'],
      'consultoria': ['Dependencia de socios', 'Escalabilidad limitada', 'Competencia de precios', 'Retención de talento'],
      'default': ['Presencia digital limitada', 'Procesos obsoletos', 'Competencia de precios', 'Dependencia de clientes clave']
    };
    
    const weaknesses = weaknessesPool[sector] || weaknessesPool['default'];
    return [weaknesses[Math.floor(Math.random() * weaknesses.length)], weaknesses[Math.floor(Math.random() * weaknesses.length)]];
  };

  // Personal Brand Specific Analysis Functions
  const generatePersonalBrandInsights = (brandValue, formData) => {
    const personalFields = formData.personalBrandFields;
    const personalSector = personalBrandSectors.find(s => s.value === personalFields.personalBrandNiche);
    const benchmarks = personalSector ? personalSector.benchmarks : {};
    
    const insights = [];
    const recommendations = [];
    const opportunities = [];
    const threats = [];
    
    // Total followers analysis
    const totalFollowers = (parseFloat(personalFields.instagramFollowers) || 0) +
                          (parseFloat(personalFields.tiktokFollowers) || 0) +
                          (parseFloat(personalFields.linkedinFollowers) || 0) +
                          (parseFloat(personalFields.youtubeSubscribers) || 0);
    
    // Engagement analysis
    const engagementRate = parseFloat(personalFields.averageEngagementRate) || 0;
    const monthlyCollabs = parseFloat(personalFields.monthlyCollaborations) || 0;
    const avgProductPrice = parseFloat(personalFields.avgProductPrice) || 0;
    
    // Follower insights
    if (totalFollowers > benchmarks.followers * 1.5) {
      insights.push('🚀 Tienes una audiencia excepcional comparada con tu nicho');
      recommendations.push('Considera lanzar productos premium o exclusivos');
    } else if (totalFollowers < benchmarks.followers * 0.7) {
      insights.push('📈 Oportunidad de crecimiento en audiencia');
      recommendations.push('Enfócate en contenido viral y colaboraciones estratégicas');
    }
    
    // Engagement insights
    if (engagementRate > benchmarks.engagement * 1.3) {
      insights.push('💙 Excelente conexión con tu audiencia');
      recommendations.push('Mantén la autenticidad y considera aumentar la frecuencia de contenido');
    } else if (engagementRate < benchmarks.engagement * 0.8) {
      insights.push('⚡ El engagement necesita mejorar');
      recommendations.push('Prueba contenido más interactivo: polls, Q&As, live streams');
    }
    
    // Monetization insights
    if (avgProductPrice > benchmarks.avg_product_price * 1.2) {
      insights.push('💰 Productos de alto valor - excelente posicionamiento premium');
      recommendations.push('Enfócate en construir autoridad y credibilidad');
    } else if (avgProductPrice < benchmarks.avg_product_price * 0.8) {
      insights.push('💡 Oportunidad de aumentar precios');
      recommendations.push('Considera agregar más valor o crear productos premium');
    }
    
    // Platform specific insights
    const instagramFollowers = parseFloat(personalFields.instagramFollowers) || 0;
    const tiktokFollowers = parseFloat(personalFields.tiktokFollowers) || 0;
    const linkedinFollowers = parseFloat(personalFields.linkedinFollowers) || 0;
    const youtubeSubscribers = parseFloat(personalFields.youtubeSubscribers) || 0;
    
    if (instagramFollowers > totalFollowers * 0.6) {
      insights.push('📸 Instagram es tu plataforma principal');
      recommendations.push('Aprovecha Reels e Instagram Shopping para monetizar');
    }
    
    if (tiktokFollowers > totalFollowers * 0.4) {
      insights.push('🎵 TikTok es una fortaleza importante');
      recommendations.push('Crea contenido viral y considera el TikTok Creator Fund');
    }
    
    if (linkedinFollowers > 20000) {
      insights.push('💼 Fuerte presencia profesional en LinkedIn');
      recommendations.push('Monetiza con consultoría B2B y cursos profesionales');
    }
    
    if (youtubeSubscribers > 50000) {
      insights.push('🎬 YouTube es un activo valioso');
      recommendations.push('Monetiza con AdSense, membresías y productos digitales');
    }
    
    // Niche specific insights
    const nicheInsights = {
      'lifestyle': ['Considera colaboraciones con marcas de estilo de vida', 'Crea productos digitales sobre rutinas y hábitos'],
      'fitness': ['Monetiza con planes de entrenamiento y suplementos', 'Ofrece coaching personal y programas de transformación'],
      'business_coach': ['Desarrolla cursos avanzados y mastermind groups', 'Ofrece consultoría estratégica de alto valor'],
      'tech_influencer': ['Colabora con empresas tech y startups', 'Crea contenido educativo sobre tecnologías emergentes'],
      'fashion_beauty': ['Lanza tu propia línea de productos', 'Colabora con marcas de moda y belleza'],
      'food_travel': ['Monetiza con libros de cocina y guías de viaje', 'Colabora con hoteles y restaurantes'],
      'education_online': ['Expande tu oferta de cursos online', 'Crea una plataforma educativa propia'],
      'finance_investing': ['Ofrece asesoramiento financiero premium', 'Crea productos de inversión y trading'],
      'gaming': ['Monetiza con streaming y sponsorships', 'Desarrolla contenido sobre gaming y esports']
    };
    
    const nicheRecs = nicheInsights[personalFields.personalBrandNiche] || [];
    recommendations.push(...nicheRecs);
    
    // Opportunities
    opportunities.push('Diversificación de plataformas digitales');
    opportunities.push('Creación de productos digitales escalables');
    opportunities.push('Colaboraciones estratégicas con otras marcas personales');
    opportunities.push('Expansión a mercados internacionales');
    
    // Threats
    threats.push('Cambios en algoritmos de redes sociales');
    threats.push('Competencia de nuevos influencers');
    threats.push('Dependencia de plataformas digitales');
    threats.push('Cambios en tendencias de consumo');
    
    return {
      insights,
      recommendations,
      opportunities,
      threats,
      benchmarkComparison: {
        followers: Math.round((totalFollowers / benchmarks.followers) * 100),
        engagement: Math.round((engagementRate / benchmarks.engagement) * 100),
        monetization: Math.round((avgProductPrice / benchmarks.avg_product_price) * 100),
        collaboration: Math.round((monthlyCollabs / benchmarks.monthly_collabs) * 100)
      },
      platformDistribution: {
        instagram: Math.round((instagramFollowers / totalFollowers) * 100),
        tiktok: Math.round((tiktokFollowers / totalFollowers) * 100),
        linkedin: Math.round((linkedinFollowers / totalFollowers) * 100),
        youtube: Math.round((youtubeSubscribers / totalFollowers) * 100)
      }
    };
  };

  const generatePersonalBrandPredictiveAnalysis = (brandValue, formData) => {
    const personalFields = formData.personalBrandFields;
    const personalSector = personalBrandSectors.find(s => s.value === personalFields.personalBrandNiche);
    const years = parseFloat(formData.yearsInMarket) || 0;
    const engagementRate = parseFloat(personalFields.averageEngagementRate) || 2;
    
    // Growth rates specific to personal brands
    const baseGrowthRates = {
      'lifestyle': 0.18,
      'fitness': 0.22,
      'business_coach': 0.25,
      'tech_influencer': 0.28,
      'fashion_beauty': 0.20,
      'food_travel': 0.15,
      'education_online': 0.30,
      'entertainment': 0.12,
      'finance_investing': 0.35,
      'parenting': 0.14,
      'gaming': 0.25,
      'art_design': 0.16
    };
    
    const baseGrowthRate = baseGrowthRates[personalFields.personalBrandNiche] || 0.18;
    const engagementMultiplier = engagementRate > 4 ? 1.3 : engagementRate > 2 ? 1.1 : 0.9;
    
    const scenarios = {
      conservative: {
        growthRate: baseGrowthRate * 0.6 * engagementMultiplier,
        confidence: 85,
        description: 'Crecimiento orgánico manteniendo estrategias actuales'
      },
      realistic: {
        growthRate: baseGrowthRate * engagementMultiplier,
        confidence: 70,
        description: 'Crecimiento con optimización de contenido y monetización'
      },
      optimistic: {
        growthRate: baseGrowthRate * 1.6 * engagementMultiplier,
        confidence: 55,
        description: 'Crecimiento viral con lanzamientos exitosos y colaboraciones'
      }
    };
    
    // Generate predictions
    const predictions = {};
    Object.keys(scenarios).forEach(scenario => {
      predictions[scenario] = [];
      for (let year = 1; year <= 5; year++) {
        const compoundGrowth = Math.pow(1 + scenarios[scenario].growthRate, year);
        const predictedValue = Math.round(brandValue * compoundGrowth);
        predictions[scenario].push({
          year: new Date().getFullYear() + year,
          value: predictedValue,
          growth: Math.round((compoundGrowth - 1) * 100)
        });
      }
    });
    
    return {
      scenarios,
      predictions,
      personalBrandFactors: {
        engagement: engagementRate,
        niche: personalFields.personalBrandNiche,
        contentType: personalFields.contentType,
        audienceGrowthPotential: baseGrowthRate > 0.25 ? 'Alto' : baseGrowthRate > 0.18 ? 'Medio' : 'Moderado'
      }
    };
  };

  const generatePersonalizedReport = (brandValue, formData) => {
    // Handle personal brands differently
    if (formData.brandType === 'personal') {
      return generatePersonalBrandReport(brandValue, formData);
    }
    
    const sector = sectors.find(s => s.value === formData.sector);
    const country = countries.find(c => c.code === formData.country);
    const companySize = companySizes.find(s => s.value === formData.companySize);
    
    // Benchmarking analysis
    const benchmarks = sector.benchmarks;
    const revenue = parseFloat(formData.revenue) || 0;
    const clients = parseFloat(formData.clients) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    
    // Performance vs benchmarks
    const revenuePerformance = revenue / benchmarks.revenue;
    const clientPerformance = clients / benchmarks.clients;
    const digitalPerformance = followers / benchmarks.digital;
    
    // Strengths and weaknesses
    const strengths = [];
    const weaknesses = [];
    const recommendations = [];
    
    if (revenuePerformance > 1.2) {
      strengths.push("Ingresos superiores al promedio del sector");
    } else if (revenuePerformance < 0.8) {
      weaknesses.push("Ingresos por debajo del promedio del sector");
      recommendations.push("Considera estrategias de crecimiento de ingresos como diversificación de productos/servicios");
    }
    
    if (clientPerformance > 1.2) {
      strengths.push("Base de clientes sólida comparada con el sector");
    } else if (clientPerformance < 0.8) {
      weaknesses.push("Base de clientes limitada para el sector");
      recommendations.push("Implementa estrategias de adquisición de clientes y marketing digital");
    }
    
    if (digitalPerformance > 1.2) {
      strengths.push("Excelente presencia digital");
    } else if (digitalPerformance < 0.8) {
      weaknesses.push("Presencia digital mejorable");
      recommendations.push("Invierte en marketing digital y construcción de audiencia en redes sociales");
    }
    
    if (parseFloat(formData.brandRecognition) >= 8) {
      strengths.push("Alto reconocimiento de marca");
    } else if (parseFloat(formData.brandRecognition) <= 5) {
      weaknesses.push("Reconocimiento de marca bajo");
      recommendations.push("Desarrolla estrategias de branding y comunicación para aumentar la visibilidad");
    }
    
    // Country-specific insights
    const countryInsights = [];
    if (country.code === 'US') {
      countryInsights.push("El mercado estadounidense ofrece oportunidades de valoración premium");
    } else if (country.code === 'DE') {
      countryInsights.push("El mercado alemán valora la calidad y la innovación tecnológica");
    } else if (country.code === 'ES') {
      countryInsights.push("Considera expansión a mercados internacionales para aumentar valoración");
    }
    
    // Size-specific recommendations
    if (formData.companySize === 'micro' || formData.companySize === 'pequeña') {
      recommendations.push("Explora oportunidades de crecimiento para escalar a la siguiente categoría de tamaño");
    } else if (formData.companySize === 'grande') {
      strengths.push("Ventajas de escala que mejoran la valoración");
    }
    
    return {
      brandValue,
      benchmarks: {
        revenue: benchmarks.revenue,
        clients: benchmarks.clients,
        digital: benchmarks.digital,
        revenuePerformance: Math.round(revenuePerformance * 100),
        clientPerformance: Math.round(clientPerformance * 100),
        digitalPerformance: Math.round(digitalPerformance * 100)
      },
      strengths,
      weaknesses,
      recommendations,
      countryInsights,
      sector: sector.label,
      country: country.name,
      companySize: companySize.label
    };
  };

  const generatePersonalBrandReport = (brandValue, formData) => {
    const personalFields = formData.personalBrandFields;
    const personalSector = personalBrandSectors.find(s => s.value === personalFields.personalBrandNiche);
    const country = countries.find(c => c.code === formData.country);
    const benchmarks = personalSector ? personalSector.benchmarks : {};
    
    // Calculate total followers
    const totalFollowers = (parseFloat(personalFields.instagramFollowers) || 0) +
                          (parseFloat(personalFields.tiktokFollowers) || 0) +
                          (parseFloat(personalFields.linkedinFollowers) || 0) +
                          (parseFloat(personalFields.youtubeSubscribers) || 0);
    
    const engagementRate = parseFloat(personalFields.averageEngagementRate) || 0;
    const avgProductPrice = parseFloat(personalFields.avgProductPrice) || 0;
    const monthlyCollabs = parseFloat(personalFields.monthlyCollaborations) || 0;
    
    // Performance vs benchmarks
    const followersPerformance = totalFollowers / benchmarks.followers;
    const engagementPerformance = engagementRate / benchmarks.engagement;
    const pricePerformance = avgProductPrice / benchmarks.avg_product_price;
    const collabPerformance = monthlyCollabs / benchmarks.monthly_collabs;
    
    const strengths = [];
    const weaknesses = [];
    const recommendations = [];
    
    if (followersPerformance > 1.2) {
      strengths.push("Audiencia superior al promedio de tu nicho");
    } else if (followersPerformance < 0.8) {
      weaknesses.push("Audiencia por debajo del promedio del nicho");
      recommendations.push("Enfócate en estrategias de crecimiento orgánico y colaboraciones");
    }
    
    if (engagementPerformance > 1.2) {
      strengths.push("Engagement rate excelente");
    } else if (engagementPerformance < 0.8) {
      weaknesses.push("Engagement rate mejorable");
      recommendations.push("Crea contenido más interactivo y auténtico");
    }
    
    if (pricePerformance > 1.2) {
      strengths.push("Productos premium con precios superiores al mercado");
    } else if (pricePerformance < 0.8) {
      weaknesses.push("Oportunidad de aumentar precios de productos");
      recommendations.push("Considera crear productos de mayor valor agregado");
    }
    
    if (collabPerformance > 1.2) {
      strengths.push("Alto número de colaboraciones comerciales");
    } else if (collabPerformance < 0.8) {
      weaknesses.push("Oportunidad de aumentar colaboraciones");
      recommendations.push("Construye un media kit profesional y prospección activa");
    }
    
    // Platform-specific insights
    const platformInsights = [];
    const instagramFollowers = parseFloat(personalFields.instagramFollowers) || 0;
    const tiktokFollowers = parseFloat(personalFields.tiktokFollowers) || 0;
    const linkedinFollowers = parseFloat(personalFields.linkedinFollowers) || 0;
    const youtubeSubscribers = parseFloat(personalFields.youtubeSubscribers) || 0;
    
    if (instagramFollowers > totalFollowers * 0.4) {
      platformInsights.push("Instagram es tu plataforma principal - aprovecha Stories y Reels");
    }
    if (tiktokFollowers > totalFollowers * 0.3) {
      platformInsights.push("TikTok es una fortaleza - enfócate en contenido viral");
    }
    if (linkedinFollowers > 10000) {
      platformInsights.push("Fuerte presencia profesional - monetiza con servicios B2B");
    }
    if (youtubeSubscribers > 5000) {
      platformInsights.push("YouTube ofrece oportunidades de ingresos pasivos");
    }
    
    return {
      brandValue,
      benchmarks: {
        followers: benchmarks.followers,
        engagement: benchmarks.engagement,
        avgProductPrice: benchmarks.avg_product_price,
        monthlyCollabs: benchmarks.monthly_collabs,
        followersPerformance: Math.round(followersPerformance * 100),
        engagementPerformance: Math.round(engagementPerformance * 100),
        pricePerformance: Math.round(pricePerformance * 100),
        collabPerformance: Math.round(collabPerformance * 100)
      },
      strengths,
      weaknesses,
      recommendations,
      platformInsights,
      personalBrandMetrics: {
        totalFollowers,
        engagementRate,
        avgProductPrice,
        monthlyCollabs
      },
      sector: personalSector ? personalSector.label : 'Personal Brand',
      country: country.name,
      niche: personalFields.personalBrandNiche
    };
  };

  const calculateBrandValueSync = () => {
    const revenue = parseFloat(formData.revenue) || 0;
    const clients = parseFloat(formData.clients) || 0;
    const years = parseFloat(formData.yearsInMarket) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    const traffic = parseFloat(formData.monthlyTraffic) || 0;
    const recognition = parseFloat(formData.brandRecognition) || 5;
    const assets = parseFloat(formData.tangibleAssets) || 0;
    
    // If personal brand, use specialized calculation
    if (formData.brandType === 'personal') {
      return calculatePersonalBrandValue();
    }
    
    const sector = sectors.find(s => s.value === formData.sector);
    const country = countries.find(c => c.code === formData.country);
    const companySize = companySizes.find(s => s.value === formData.companySize);
    
    const sectorMultiplier = sector ? sector.multiplier[formData.businessType] : 4;
    const countryMultiplier = country ? country.multiplier : 1.0;
    const sizeMultiplier = companySize ? companySize.multiplier : 1.0;
    
    // Custom parameters adjustment
    const customParams = formData.customParameters;
    
    // Brand type multiplier (Personal brands typically have lower valuations)
    const brandTypeMultiplier = formData.brandType === 'personal' ? 0.7 : 1.0;
    
    // Business type adjustments
    const businessTypeAdjustment = formData.businessType === 'B2B' ? 1.2 : 1.0;
    
    // Base value from revenue with all multipliers (reduced for realism)
    const baseValue = revenue * sectorMultiplier * brandTypeMultiplier * countryMultiplier * sizeMultiplier * customParams.sectorMultiplierAdjustment * 0.3;
    
    // Longevity factor (1-1.5x based on years, reduced from 2x)
    const longevityFactor = Math.min(1 + (years * 0.03), 1.5) * customParams.longevityWeight;
    
    // Client base factor (adjusted for B2B vs B2C, reduced impact)
    const clientBase = formData.businessType === 'B2B' ? 
      1 + Math.log10(Math.max(clients, 1)) * 0.08 * customParams.clientBaseWeight : 
      1 + Math.log10(Math.max(clients, 1)) * 0.05 * customParams.clientBaseWeight;
    
    // Digital presence factor (reduced multiplier for realism)
    const digitalMultiplier = formData.brandType === 'personal' ? 1.2 : 0.8;
    const digitalFactor = (1 + (followers / 200000) * 0.05 * digitalMultiplier + (traffic / 100000) * 0.05) * customParams.digitalPresenceWeight;
    
    // Brand recognition factor (1-1.3x, reduced from 1.5x)
    const recognitionFactor = 1 + (recognition / 10) * 0.3;
    
    // Final calculation
    const brandValue = (baseValue * longevityFactor * clientBase * digitalFactor * recognitionFactor * businessTypeAdjustment) + assets;
    
    return Math.round(brandValue);
  };

  // Personal Brand Specific Calculation
  const calculatePersonalBrandValue = () => {
    const revenue = parseFloat(formData.revenue) || 0;
    const years = parseFloat(formData.yearsInMarket) || 0;
    const personalFields = formData.personalBrandFields;
    
    // Get personal brand sector
    const personalSector = personalBrandSectors.find(s => s.value === personalFields.personalBrandNiche);
    const country = countries.find(c => c.code === formData.country);
    const contentType = contentTypes.find(c => c.value === personalFields.contentType);
    const audienceLocation = audienceLocations.find(a => a.value === personalFields.audienceLocation);
    
    // Core multipliers
    const sectorMultiplier = personalSector ? personalSector.multiplier : 4.5;
    const countryMultiplier = country ? country.multiplier : 1.0;
    const contentMultiplier = contentType ? contentType.multiplier : 1.0;
    const audienceMultiplier = audienceLocation ? audienceLocation.multiplier : 1.0;
    
    // Calculate total social media following
    const totalFollowers = (parseFloat(personalFields.instagramFollowers) || 0) +
                          (parseFloat(personalFields.tiktokFollowers) || 0) +
                          (parseFloat(personalFields.linkedinFollowers) || 0) +
                          (parseFloat(personalFields.youtubeSubscribers) || 0);
    
    // Base calculation using revenue and following (reduced multiplier for realism)
    const baseValue = revenue * sectorMultiplier * countryMultiplier * contentMultiplier * audienceMultiplier * 0.25;
    
    // Engagement factor (reduced impact for realism)
    const engagementRate = parseFloat(personalFields.averageEngagementRate) || 2.0;
    const engagementFactor = (1 + (engagementRate / 100) * 2) * formData.customParameters.engagementWeight;
    
    // Influence factor based on total following (reduced)
    const influenceFactor = (1 + Math.log10(Math.max(totalFollowers, 1000)) * 0.1) * formData.customParameters.influenceWeight;
    
    // Monetization factor (reduced impact)
    const monthlyCollabs = parseFloat(personalFields.monthlyCollaborations) || 0;
    const avgProductPrice = parseFloat(personalFields.avgProductPrice) || 0;
    const speakingFees = parseFloat(personalFields.speakingFees) || 0;
    const consultingRate = parseFloat(personalFields.consultingHourlyRate) || 0;
    
    const monetizationScore = (monthlyCollabs * 50) + (avgProductPrice * 1) + (speakingFees * 0.2) + (consultingRate * 5);
    const monetizationFactor = (1 + Math.log10(Math.max(monetizationScore, 1)) * 0.08) * formData.customParameters.monetizationWeight;
    
    // Authority factor (reduced)
    const brandAuthority = parseFloat(personalFields.brandAuthority) || 5;
    const authorityFactor = (1 + (brandAuthority / 10) * 0.4) * formData.customParameters.authorityWeight;
    
    // Longevity factor (reduced maximum)
    const longevityFactor = Math.min(1 + (years * 0.05), 1.8) * formData.customParameters.longevityWeight;
    
    // YouTube specific bonus (reduced)
    const youtubeViews = parseFloat(personalFields.youtubeViews) || 0;
    const youtubeBonus = youtubeViews > 0 ? (1 + Math.log10(youtubeViews) * 0.03) : 1;
    
    // Final calculation
    const personalBrandValue = baseValue * engagementFactor * influenceFactor * monetizationFactor * 
                             authorityFactor * longevityFactor * youtubeBonus;
    
    return Math.round(personalBrandValue);
  };

  const calculateBrandValue = () => {
    console.log('calculateBrandValue called');
    console.log('Current formData:', formData);
    
    // Validation based on brand type
    if (formData.brandType === 'personal') {
      // Personal brand validation
      if (!formData.revenue || !formData.yearsInMarket) {
        alert('Por favor, completa los campos requeridos para marcas personales: ingresos anuales y años en el mercado.');
        return;
      }
      // Check at least one social media field
      const personalFields = formData.personalBrandFields;
      const hasFollowers = personalFields.instagramFollowers || 
                          personalFields.tiktokFollowers || 
                          personalFields.linkedinFollowers || 
                          personalFields.youtubeSubscribers;
      if (!hasFollowers) {
        alert('Por favor, ingresa al menos una métrica de redes sociales para tu marca personal.');
        return;
      }
    } else {
      // Business brand validation
      if (!formData.revenue || !formData.clients || !formData.yearsInMarket) {
        alert('Por favor, completa todos los campos requeridos.');
        return;
      }
    }
    
    try {
      // Show loading state
      setIsCalculating(true);
      setIsAnalyzingAI(true);
      
      // Use a shorter timeout and direct execution
      setTimeout(() => {
        try {
          console.log('Starting calculations...');
          
          // Calculate brand value
          const calculatedValue = calculateBrandValueSync();
          console.log('Calculated value:', calculatedValue);
          
          if (!calculatedValue || isNaN(calculatedValue)) {
            throw new Error('Calculation failed - invalid result');
          }
          
          // Generate all analyses with error handling
          let report, predictiveAnalysis, sentimentAnalysis, competitiveAnalysis;
          
          try {
            report = generatePersonalizedReport(calculatedValue, formData);
            console.log('Generated report');
          } catch (e) {
            console.error('Report generation error:', e);
            report = { error: 'Report generation failed' };
          }
          
          try {
            predictiveAnalysis = generatePredictiveAnalysis(calculatedValue, formData);
            console.log('Generated predictive analysis');
          } catch (e) {
            console.error('Predictive analysis error:', e);
            predictiveAnalysis = { error: 'Predictive analysis failed' };
          }
          
          try {
            sentimentAnalysis = generateSentimentAnalysis(formData);
            console.log('Generated sentiment analysis');
          } catch (e) {
            console.error('Sentiment analysis error:', e);
            sentimentAnalysis = { error: 'Sentiment analysis failed' };
          }
          
          try {
            competitiveAnalysis = generateCompetitiveAnalysis(calculatedValue, formData);
            console.log('Generated competitive analysis');
          } catch (e) {
            console.error('Competitive analysis error:', e);
            competitiveAnalysis = { error: 'Competitive analysis failed' };
          }
          
          console.log('All analyses completed');
          
          // Set all results
          setResult(calculatedValue);
          setGeneratedReport(report);
          setAiAnalysis(predictiveAnalysis);
          setSentimentAnalysis(sentimentAnalysis);
          setCompetitiveAnalysis(competitiveAnalysis);
          
          console.log('States set, transitioning to results');
          
          // Complete loading and transition to results
          setIsCalculating(false);
          setIsAnalyzingAI(false);
          setActiveStep(4); // Go to results step
          
          console.log('Successfully moved to results step');
          
          // Generate AI recommendations after calculation
          generateAIRecommendations();
          
        } catch (innerError) {
          console.error('Error in calculation process:', innerError);
          setIsCalculating(false);
          setIsAnalyzingAI(false);
          alert('Error en el cálculo: ' + innerError.message);
        }
      }, 2000); // Reduced timeout
      
    } catch (error) {
      console.error('Error in calculateBrandValue:', error);
      alert('Error iniciando el análisis: ' + error.message);
      setIsCalculating(false);
      setIsAnalyzingAI(false);
    }
  };

  // AI Recommendations function
  const generateAIRecommendations = async () => {
    if (!result) return;
    
    setIsLoadingRecommendations(true);
    
    try {
      const brandAnalysisData = {
        brand_name: formData.brandType === 'empresa' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        website: formData.website || null, // Website URL for deeper analysis
        revenue: parseFloat(formData.companyRevenue) || parseFloat(formData.personalRevenue) || parseFloat(formData.revenue) || 0,
        industry: formData.industry || 'technology',
        employees: parseInt(formData.employees) || null,
        brand_age: formData.brandAge || '3-5',
        market_position: formData.marketPosition || 'emerging',
        digital_presence: formData.digitalPresence || 'good',
        brand_recognition: formData.brandRecognition || 'national',
        current_value: result || 0,
        user_goals: formData.userGoals || null,
        
        // Sector-specific fields
        ...formData.sectorSpecificFields
      };

      console.log('Sending brand analysis data:', brandAnalysisData);
      console.log('Revenue being sent:', brandAnalysisData.revenue);
      console.log('Current value being sent:', brandAnalysisData.current_value);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(brandAnalysisData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recommendations = await response.json();
      console.log('AI Recommendations received:', recommendations);
      console.log('Checking for blockchain certificate...');
      console.log('recommendations.blockchain_certificate:', recommendations.blockchain_certificate);
      console.log('Certificate exists?', !!recommendations.blockchain_certificate);
      
      setAiRecommendations(recommendations);
      setShowRecommendations(true);
      
      // Check if blockchain certificate was issued for premium brand
      if (recommendations.blockchain_certificate) {
        console.log('✅ Blockchain certificate received:', recommendations.blockchain_certificate);
        setBlockchainCertificate(recommendations.blockchain_certificate);
        setShowCertificate(true);
        console.log('✅ Certificate display states set');
      } else {
        console.log('❌ No blockchain certificate in response');
        console.log('Response keys:', Object.keys(recommendations));
        console.log('Brand value used:', recommendations.current_value || 'not found');
      }
      
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      alert('Error generando recomendaciones: ' + error.message);
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  // Brand Audit function
  const generateBrandAudit = async () => {
    if (!result) return;
    
    setIsLoadingAudit(true);
    
    try {
      const auditData = {
        brand_name: formData.brandType === 'company' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        industry: formData.industry || 'general',
        current_brand_elements: {
          has_logo: formData.hasLogo === 'yes',
          has_guidelines: formData.hasBrandGuidelines === 'yes',
          brand_colors: brandColors || [],
          brand_consistency: formData.brandConsistency || 'medium'
        },
        digital_presence_data: {
          website_quality: formData.digitalPresence || 'basic',
          social_media: formData.socialMedia || 'basic',
          online_reviews: formData.onlineReviews || 'mixed',
          seo_ranking: formData.searchRanking || 'average'
        },
        market_data: {
          market_position: formData.marketPosition || 'emerging',
          brand_recognition: formData.brandRecognition || 'local',
          competitive_advantage: formData.competitiveAdvantage || 'limited',
          customer_loyalty: formData.customerLoyalty || 'medium'
        }
      };

      console.log('Sending brand audit data:', auditData);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audit = await response.json();
      console.log('Brand Audit received:', audit);
      
      setBrandAudit(audit);
      setShowAudit(true);
      
    } catch (error) {
      console.error('Error generating brand audit:', error);
      alert('Error generando auditoría: ' + error.message);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  // Executive Report function
  const generateExecutiveReport = async (reportType = 'comprehensive') => {
    if (!result && !aiRecommendations && !brandAudit) return;
    
    setIsGeneratingReport(true);
    
    try {
      const reportData = {
        brand_name: formData.brandType === 'company' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        report_type: reportType,
        include_charts: true,
        include_recommendations: true,
        data_source: {
          brand_value: result,
          ai_recommendations: aiRecommendations,
          audit_result: brandAudit,
          form_data: formData
        }
      };

      console.log('Generating executive report:', reportData);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-executive-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reportResult = await response.json();
      console.log('Executive Report generated:', reportResult);
      
      // Create download link
      const pdfBlob = new Blob([atob(reportResult.pdf_base64)], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setReportUrl(url);
      
      // Auto download
      const link = document.createElement('a');
      link.href = url;
      link.download = reportResult.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error generating executive report:', error);
      alert('Error generando reporte: ' + error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested personal brand fields
    if (name.startsWith('personalBrandFields.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        personalBrandFields: {
          ...prev.personalBrandFields,
          [fieldName]: value
        }
      }));
    } 
    // Handle sector-specific fields
    else if (name.startsWith('sectorSpecificFields.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        sectorSpecificFields: {
          ...prev.sectorSpecificFields,
          [fieldName]: value === 'true' ? true : value === 'false' ? false : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const formatCurrency = (value) => {
    // Validate input value
    if (value === undefined || value === null || isNaN(value)) {
      return '€0';
    }
    
    // Convert to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // Check if conversion resulted in NaN
    if (isNaN(numValue)) {
      return '€0';
    }
    
    const country = countries.find(c => c.code === formData.country);
    
    // Determine currency based on country selection first, then form currency
    let currency;
    if (country && country.currency) {
      currency = country.currency;
    } else {
      currency = formData.currency;
    }
    
    let symbol = '€';
    
    switch(currency) {
      case 'USD':
        symbol = '$';
        break;
      case 'EUR':
        symbol = '€';
        break;
      case 'GBP':
        symbol = '£';
        break;
      default:
        symbol = currency === 'USD' ? '$' : '€';
    }
    
    return `${symbol}${numValue.toLocaleString()}`;
  };

  // Safe number formatter for non-currency numbers
  const formatNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) {
      return '0';
    }
    
    return numValue.toLocaleString();
  };

  const steps = [
    { title: 'Información Básica', icon: '🏢' },
    { title: 'Métricas Digitales', icon: '📊' },
    { title: 'Reconocimiento', icon: '⭐' },
    { title: 'Resultado', icon: '💰' }
  ];

  // Safe step navigation function to prevent array length errors
  const nextStep = () => {
    console.log('nextStep called, current activeStep:', activeStep);
    
    // Ensure steps array exists and has content
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      console.error('Steps array is undefined, empty, or not an array');
      return;
    }
    
    if (activeStep < steps.length - 1) {
      console.log('Moving to step:', activeStep + 1);
      setActiveStep(activeStep + 1);
    } else {
      console.log('Already at last step');
    }
  };

  const prevStep = () => {
    console.log('prevStep called, current activeStep:', activeStep);
    if (activeStep > 0) {
      console.log('Moving to step:', activeStep - 1);
      setActiveStep(activeStep - 1);
    } else {
      console.log('Already at first step');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="modal-container bg-slate-800 rounded-3xl w-full max-w-6xl max-h-[95vh] overflow-y-auto border border-white/10">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex justify-between items-center mb-4 sm:mb-6 lg:mb-8">
                <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white leading-tight">
                  Desbloquea el Poder de la IA
                </h2>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-white text-xl sm:text-2xl p-2 flex-shrink-0"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6 lg:mb-8">
                {Object.entries(subscriptionPlans).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`relative p-4 sm:p-6 rounded-2xl border ${
                      plan.popular 
                        ? 'border-purple-500 bg-purple-900/20' 
                        : 'border-white/20 bg-slate-700/50'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                          MÁS POPULAR
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center mb-4 sm:mb-6">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{plan.name}</h3>
                      <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                        €{plan.price}
                        <span className="text-sm sm:text-lg text-gray-400">{plan.period}</span>
                      </div>
                    </div>

                    <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-gray-300 text-sm sm:text-base">
                          <span className="text-green-400 mr-2 mt-1 flex-shrink-0">✓</span>
                          <span className="break-words">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.limitations.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-red-400 font-semibold mb-2">Limitaciones:</h4>
                        <ul className="space-y-1">
                          {plan.limitations.map((limitation, index) => (
                            <li key={index} className="flex items-start text-red-300 text-sm">
                              <span className="text-red-400 mr-2 mt-1">×</span>
                              {limitation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button
                      className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                        plan.popular
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                          : key === 'free'
                          ? 'bg-gray-600 text-gray-300 cursor-default'
                          : 'border border-white/20 text-white hover:bg-white/10'
                      }`}
                      onClick={() => {
                        if (key !== 'free') {
                          setUserPlan(key);
                          setShowUpgradeModal(false);
                          alert(`¡Gracias! Has actualizado a ${plan.name}. En producción esto procesaría el pago.`);
                        }
                      }}
                      disabled={key === 'free'}
                    >
                      {key === 'free' ? 'Plan Actual' : `Actualizar a ${plan.name}`}
                    </button>
                  </div>
                ))}
              </div>

              {/* Pay Per Report Option */}
              <div className="bg-gradient-to-r from-blue-900/50 to-cyan-900/50 p-6 rounded-2xl border border-blue-500/30 mb-6">
                <h3 className="text-xl font-bold text-white mb-4 text-center">
                  ¿Solo necesitas un informe? 
                </h3>
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-blue-400 mb-2">
                    €{payPerReport.price}{payPerReport.period}
                  </div>
                  <p className="text-gray-300">{payPerReport.description}</p>
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  {payPerReport.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-blue-200">
                      <span className="text-blue-400 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
                  onClick={() => {
                    setShowUpgradeModal(false);
                    alert('¡Gracias! Has comprado un informe premium. En producción esto procesaría el pago y habilitaría las funciones IA temporalmente.');
                  }}
                >
                  Comprar Informe Premium
                </button>
              </div>

              <div className="text-center text-gray-400">
                <p className="mb-2">🔒 Pago seguro con encriptación SSL</p>
                <p>💳 Aceptamos todas las tarjetas principales y PayPal</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-slate-900/50 to-orange-900/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-12 sm:pb-16">
          <div className="text-center">
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold text-white mb-6 sm:mb-8 leading-tight">
              {t('home.hero.title1')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">
                {' '}{t('home.hero.title2')}
              </span>
              <br />
              {t('home.hero.title3')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-600">
                {' '}{t('home.hero.title4')}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 max-w-2xl mx-auto px-4">
              <Link 
                to="/calculator"
                className="bg-gradient-to-r from-purple-600 to-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl text-base sm:text-lg font-bold hover:from-purple-700 hover:to-orange-700 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-purple-500/25 flex items-center justify-center gap-2"
              >
                <span>🧮</span>
                {t('home.hero.cta')}
              </Link>
              <Link 
                to="/demo"
                className="border-2 border-purple-400/50 bg-gradient-to-r from-purple-400/10 to-orange-400/10 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl text-base sm:text-lg font-bold hover:from-purple-400/20 hover:to-orange-400/20 hover:border-purple-400 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <span>▶️</span>
                {t('home.hero.ctaDemo')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Condensed */}
      <div className="py-12 bg-slate-800/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('home.features.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-orange-400">{t('home.features.titleHighlight')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-900/30 to-slate-800 p-6 rounded-xl border border-purple-500/20 text-center hover:border-purple-500/40 transition-all duration-300">
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-xl font-bold text-white mb-3">{t('home.features.aiAdvanced.title')}</h3>
              <p className="text-gray-300">{t('home.features.aiAdvanced.description')}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-900/30 to-slate-800 p-6 rounded-xl border border-orange-500/20 text-center hover:border-orange-500/40 transition-all duration-300">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-white mb-3">{t('home.features.instantValuation.title')}</h3>
              <p className="text-gray-300">{t('home.features.instantValuation.description')}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 via-slate-800 to-orange-900/30 p-6 rounded-xl border border-gradient-to-r from-purple-500/20 to-orange-500/20 text-center hover:border-purple-500/40 transition-all duration-300">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-white mb-3">{t('home.features.integralBenchmark.title')}</h3>
              <p className="text-gray-300">{t('home.features.integralBenchmark.description')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <div id="calculator" className="py-20 bg-gradient-to-br from-slate-900 to-purple-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-white mb-6">
              {t('home.calculator.title')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('home.calculator.subtitle')}
            </p>
          </div>

          {/* Brand Type Selection Screen */}
          {!brandTypeSelected && (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-4 sm:p-8 border border-white/10">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  {t('home.calculator.brandTypeQuestion')}
                </h3>
                <p className="text-xl text-gray-300">
                  {t('home.calculator.brandTypeSubtitle')}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <Link
                  to={getCalculatorUrl('company')}
                  className="group bg-gradient-to-br from-purple-600/20 to-blue-600/20 hover:from-purple-600/30 hover:to-blue-600/30 border-2 border-purple-500/30 hover:border-purple-400 rounded-3xl p-8 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 block text-decoration-none"
                >
                  <div className="text-center">
                    <div className="text-6xl mb-4 group-hover:animate-pulse">🏢</div>
                    <h4 className="text-2xl font-bold text-white mb-3">{t('home.calculator.brandCompany')}</h4>
                    <p className="text-gray-300 text-lg mb-4">
                      {t('home.calculator.brandCompanyDesc')}
                    </p>
                    <div className="bg-white/10 rounded-2xl p-4 text-sm text-gray-300">
                      <p className="mb-2">📊 {t('home.calculator.brandCompanyAnalysis')}</p>
                      <p className="mb-2">💼 {t('home.calculator.brandCompanyAssets')}</p>
                      <p>🎯 {t('home.calculator.brandCompanyMetrics')}</p>
                    </div>
                  </div>
                </Link>

                <Link
                  to={getCalculatorUrl('personal')}
                  className="group bg-gradient-to-br from-pink-600/20 to-orange-600/20 hover:from-pink-600/30 hover:to-orange-600/30 border-2 border-pink-500/30 hover:border-pink-400 rounded-3xl p-8 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-pink-500/25 block text-decoration-none"
                >
                  <div className="text-center">
                    <div className="text-6xl mb-4 group-hover:animate-pulse">👤</div>
                    <h4 className="text-2xl font-bold text-white mb-3">{t('home.calculator.brandPersonal')}</h4>
                    <p className="text-gray-300 text-lg mb-4">
                      {t('home.calculator.brandPersonalDesc')}
                    </p>
                    <div className="bg-white/10 rounded-2xl p-4 text-sm text-gray-300">
                      <p className="mb-2">📱 {t('home.calculator.brandPersonalSocial')}</p>
                      <p className="mb-2">🎬 {t('home.calculator.brandPersonalContent')}</p>
                      <p>💰 {t('home.calculator.brandPersonalMonetization')}</p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          )}

          {/* Progress Steps - Only show when brand type is selected */}
          {brandTypeSelected && steps && Array.isArray(steps) && steps.length > 0 && (
            <div className="flex justify-center mb-12">
              <div className="progress-steps flex space-x-2 sm:space-x-4 w-full max-w-full overflow-x-auto pb-2">
                {steps.map((step, index) => (
                  <div 
                    key={index}
                    className={`progress-step flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg whitespace-nowrap flex-shrink-0 ${
                      index === activeStep 
                        ? 'bg-purple-600 text-white' 
                        : index < activeStep 
                          ? 'bg-green-600 text-white' 
                          : 'bg-slate-700 text-gray-400'
                    }`}
                  >
                    <span className="text-lg sm:text-2xl">{step.icon}</span>
                    <span className="font-semibold text-sm sm:text-base hidden sm:inline">{step.title}</span>
                    <span className="font-semibold text-xs sm:hidden">{step.title.split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calculator Form - Only show when brand type is selected */}
          {brandTypeSelected && (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-4 sm:p-8 border border-white/10">
            {/* Step 1: Basic Information */}
            {activeStep === 0 && (
              <div className="space-y-6">
                <div className="calculator-step-grid grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-white text-lg font-semibold mb-2">
                      🎯 Tipo de Marca
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, brandType: 'empresa' }))}
                        className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                          formData.brandType === 'empresa'
                            ? 'border-purple-500 bg-purple-900/30'
                            : 'border-white/20 bg-slate-700/30 hover:border-purple-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-3xl">🏢</span>
                          <div>
                            <h4 className="text-white font-semibold">Marca Empresa</h4>
                            <p className="text-gray-300 text-sm">Empresas, startups, organizaciones</p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, brandType: 'personal' }))}
                        className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                          formData.brandType === 'personal'
                            ? 'border-pink-500 bg-pink-900/30'
                            : 'border-white/20 bg-slate-700/30 hover:border-pink-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-3xl">👤</span>
                          <div>
                            <h4 className="text-white font-semibold">Marca Personal</h4>
                            <p className="text-gray-300 text-sm">Profesionales, consultores, creadores</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      País
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                    >
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      Tamaño de Empresa
                    </label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                    >
                      {companySizes.map(size => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      Tipo de Negocio
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                    >
                      <option value="B2B">B2B (Business to Business)</option>
                      <option value="B2C">B2C (Business to Consumer)</option>
                    </select>
                  </div>
                    <div>
                      <label className="block text-white text-lg font-semibold mb-2">
                        🎯 Tipo de Marca
                      </label>
                      <select
                        name="brandType"
                        value={formData.brandType}
                        onChange={handleInputChange}
                        className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      >
                        <option value="">Selecciona el tipo de marca</option>
                        <option value="empresa">🏢 Marca Empresa</option>
                        <option value="personal">👤 Marca Personal</option>
                      </select>
                      <p className="text-gray-400 text-sm mt-2">
                        {formData.brandType === 'empresa' && 'Para empresas, startups y organizaciones'}
                        {formData.brandType === 'personal' && 'Para profesionales, consultores y creadores'}
                        {!formData.brandType && 'Selecciona primero el tipo de marca para continuar'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-white text-lg font-semibold mb-2">
                        🌐 Sitio Web (Opcional - para análisis avanzado)
                      </label>
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        placeholder={formData.brandType === 'personal' ? 'https://tu-nombre.com' : 'https://tu-empresa.com'}
                      />
                      <p className="text-gray-400 text-sm mt-2">
                        💡 Incluir tu web permite análisis más profundo con ChatGPT sobre competencia y posicionamiento
                      </p>
                    </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      {t('home.calculator.fields.annualRevenue')}
                    </label>
                    <input
                      type="number"
                      name="revenue"
                      value={formData.revenue}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder={t('home.calculator.fields.annualRevenuePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      {formData.businessType === 'B2B' ? 'Clientes Corporativos' : 'Clientes/Usuarios'}
                    </label>
                    <input
                      type="number"
                      name="clients"
                      value={formData.clients}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder={formData.businessType === 'B2B' ? 'ej: 50' : 'ej: 10000'}
                    />
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      {t('home.calculator.fields.yearsInMarket')}
                    </label>
                    <input
                      type="number"
                      name="yearsInMarket"
                      value={formData.yearsInMarket}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder={t('home.calculator.fields.yearsInMarketPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      {t('home.calculator.fields.sector')}
                    </label>
                    <select
                      name="sector"
                      value={formData.sector}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                    >
                      {formData.brandType === 'personal' ? 
                        personalBrandSectors.map(sector => (
                          <option key={sector.value} value={sector.value}>
                            {sector.label}
                          </option>
                        )) :
                        sectors.map(sector => (
                          <option key={sector.value} value={sector.value}>
                            {sector.label}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  
                  {/* Sector-specific fields */}
                  {formData.sector === 'ecommerce' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.mainSalesChannel')}
                        </label>
                        <select
                          name="sectorSpecificFields.main_sales_channel"
                          value={formData.sectorSpecificFields.main_sales_channel}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="website">{t('home.calculator.fields.ownWebsite')}</option>
                          <option value="marketplaces">{t('home.calculator.fields.marketplaces')}</option>
                          <option value="mixed">{t('home.calculator.fields.mixed')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.sellsOnMarketplaces')}
                        </label>
                        <select
                          name="sectorSpecificFields.sells_on_marketplaces"
                          value={formData.sectorSpecificFields.sells_on_marketplaces}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                          <option value="false">{t('home.calculator.fields.no')}</option>
                        </select>
                      </div>
                      
                      {formData.sectorSpecificFields.sells_on_marketplaces === 'true' && (
                        <div>
                          <label className="block text-white text-lg font-semibold mb-2">
                            {t('home.calculator.fields.marketplaceRevenuePercentage')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            name="sectorSpecificFields.marketplace_revenue_percentage"
                            value={formData.sectorSpecificFields.marketplace_revenue_percentage}
                            onChange={handleInputChange}
                            className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                            placeholder={t('home.calculator.fields.marketplaceRevenuePercentagePlaceholder')}
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Gestionas múltiples marcas?
                        </label>
                        <select
                          name="sectorSpecificFields.has_multiple_brands"
                          value={formData.sectorSpecificFields.has_multiple_brands}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                      
                      {formData.sectorSpecificFields.has_multiple_brands === 'true' && (
                        <div>
                          <label className="block text-white text-lg font-semibold mb-2">
                            {t('home.calculator.fields.brandCount')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            name="sectorSpecificFields.number_of_brands"
                            value={formData.sectorSpecificFields.number_of_brands}
                            onChange={handleInputChange}
                            className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                            placeholder={t('home.calculator.fields.brandCountPlaceholder')}
                          />
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Technology sector fields */}
                  {formData.sector === 'technology' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.hasMobileApp')}
                        </label>
                        <select
                          name="sectorSpecificFields.has_mobile_app"
                          value={formData.sectorSpecificFields.has_mobile_app}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">{t('home.calculator.fields.no')}</option>
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.hasSaasModel')}
                        </label>
                        <select
                          name="sectorSpecificFields.has_saas_model"
                          value={formData.sectorSpecificFields.has_saas_model}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">{t('home.calculator.fields.no')}</option>
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Ofreces API?
                        </label>
                        <select
                          name="sectorSpecificFields.has_api"
                          value={formData.sectorSpecificFields.has_api}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Consulting sector fields */}
                  {formData.sector === 'consulting' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Tasa de Retención de Clientes (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          name="sectorSpecificFields.client_retention_rate"
                          value={formData.sectorSpecificFields.client_retention_rate * 100}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) / 100;
                            setFormData(prev => ({
                              ...prev,
                              sectorSpecificFields: {
                                ...prev.sectorSpecificFields,
                                client_retention_rate: value
                              }
                            }));
                          }}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 80"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Valor Promedio de Proyecto (EUR)
                        </label>
                        <input
                          type="number"
                          name="sectorSpecificFields.average_project_value"
                          value={formData.sectorSpecificFields.average_project_value}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 15000"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Healthcare sector fields */}
                  {formData.sector === 'healthcare' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Tienes certificaciones profesionales?
                        </label>
                        <select
                          name="sectorSpecificFields.has_certifications"
                          value={formData.sectorSpecificFields.has_certifications}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Manufacturing sector fields */}
                  {formData.sector === 'manufacturing' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Tienes producción propia?
                        </label>
                        <select
                          name="sectorSpecificFields.has_own_production"
                          value={formData.sectorSpecificFields.has_own_production}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Retail sector fields */}
                  {formData.sector === 'retail' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Número de Ubicaciones
                        </label>
                        <input
                          type="number"
                          min="1"
                          name="sectorSpecificFields.number_of_locations"
                          value={formData.sectorSpecificFields.number_of_locations}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 3"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Modelo de Franquicia?
                        </label>
                        <select
                          name="sectorSpecificFields.franchise_model"
                          value={formData.sectorSpecificFields.franchise_model}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Personal Brand Specific Fields */}
                  {formData.brandType === 'personal' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Tipo de Contenido Principal
                        </label>
                        <select
                          name="personalBrandFields.contentType"
                          value={formData.personalBrandFields.contentType}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          {contentTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Alcance de Audiencia
                        </label>
                        <select
                          name="personalBrandFields.audienceLocation"
                          value={formData.personalBrandFields.audienceLocation}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          {audienceLocations.map(location => (
                            <option key={location.value} value={location.value}>
                              {location.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
                <div className="bg-slate-700/50 p-4 rounded-xl border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-blue-400">🌍</span>
                    <span className="text-white font-semibold">Información de tu perfil empresarial</span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    <strong>{countries.find(c => c.code === formData.country)?.name}:</strong> {countries.find(c => c.code === formData.country)?.multiplier > 1 ? 'Mercado con valoraciones premium' : 'Mercado con valoraciones estándar'}
                    <br />
                    <strong>{companySizes.find(s => s.value === formData.companySize)?.label}:</strong> Ajuste de valoración según escala empresarial
                    <br />
                    <strong>{formData.businessType} - {formData.brandType === 'empresa' ? 'Empresa' : 'Personal'}:</strong> Metodología específica para tu perfil
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={nextStep}
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                  >
                    {t('home.calculator.buttons.next')} →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Digital Metrics */}
            {activeStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      {formData.brandType === 'personal' ? 'Seguidores en Redes Sociales (Total)' : 'Presencia Digital (Seguidores)'}
                    </label>
                    <input
                      type="number"
                      name="socialFollowers"
                      value={formData.socialFollowers}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder={formData.brandType === 'personal' ? 'ej: 75000' : 'ej: 25000'}
                    />
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      Tráfico Web Mensual
                    </label>
                    <input
                      type="number"
                      name="monthlyTraffic"
                      value={formData.monthlyTraffic}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder="ej: 25000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white text-lg font-semibold mb-2">
                      {formData.brandType === 'personal' ? 'Valor de Activos Profesionales' : 'Valor de Activos Tangibles'}
                    </label>
                    <input
                      type="number"
                      name="tangibleAssets"
                      value={formData.tangibleAssets}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                      placeholder={formData.brandType === 'personal' ? 'ej: 25000' : 'ej: 100000'}
                    />
                  </div>
                  
                  {/* Personal Brand Specific Digital Fields */}
                  {formData.brandType === 'personal' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Seguidores Instagram
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.instagramFollowers"
                          value={formData.personalBrandFields.instagramFollowers}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 50000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Seguidores TikTok
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.tiktokFollowers"
                          value={formData.personalBrandFields.tiktokFollowers}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 25000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Seguidores LinkedIn
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.linkedinFollowers"
                          value={formData.personalBrandFields.linkedinFollowers}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 15000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Suscriptores YouTube
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.youtubeSubscribers"
                          value={formData.personalBrandFields.youtubeSubscribers}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 10000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Views Mensuales YouTube
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.youtubeViews"
                          value={formData.personalBrandFields.youtubeViews}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 100000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Engagement Rate Promedio (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          name="personalBrandFields.averageEngagementRate"
                          value={formData.personalBrandFields.averageEngagementRate}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 3.5"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Colaboraciones por Mes
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.monthlyCollaborations"
                          value={formData.personalBrandFields.monthlyCollaborations}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 5"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Cursos/Productos Vendidos por Mes
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.coursesProducts"
                          value={formData.personalBrandFields.coursesProducts}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 20"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Precio Promedio de Productos (€)
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.avgProductPrice"
                          value={formData.personalBrandFields.avgProductPrice}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 200"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Tarifa por Conferencia (€)
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.speakingFees"
                          value={formData.personalBrandFields.speakingFees}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 2000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Consultoría por Hora (€)
                        </label>
                        <input
                          type="number"
                          name="personalBrandFields.consultingHourlyRate"
                          value={formData.personalBrandFields.consultingHourlyRate}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 150"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Autoridad en tu Nicho (1-10)
                        </label>
                        <input
                          type="range"
                          name="personalBrandFields.brandAuthority"
                          min="1"
                          max="10"
                          value={formData.personalBrandFields.brandAuthority}
                          onChange={handleInputChange}
                          className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer range-slider"
                        />
                        <div className="flex justify-between text-sm text-gray-400 mt-1">
                          <span>Principiante</span>
                          <span className="text-purple-400 font-semibold">{formData.personalBrandFields.brandAuthority}</span>
                          <span>Líder del Sector</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="bg-slate-700/50 p-4 rounded-xl border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-green-400">💡</span>
                    <span className="text-white font-semibold">Consejos para {formData.brandType === 'personal' ? 'Marca Personal' : 'Marca Empresa'}</span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    {formData.brandType === 'personal' ? 
                      'Para marcas personales, la presencia digital es crucial. Incluye seguidores de LinkedIn, Instagram, Twitter, YouTube, etc. Los activos incluyen equipos, cursos, libros publicados.' :
                      'Para marcas empresa, considera el tráfico web total, presencia en múltiples canales. Los activos incluyen inmuebles, equipos, inventario, propiedad intelectual.'
                    }
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                  <button
                    onClick={prevStep}
                    className="w-full sm:w-auto bg-slate-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-all duration-300"
                  >
                    ← {t('home.calculator.buttons.previous')}
                  </button>
                  <button
                    onClick={nextStep}
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                  >
                    {t('home.calculator.buttons.next')} →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Recognition & Advanced Options */}
            {activeStep === 2 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      Nivel de Reconocimiento de Marca (1-10)
                    </label>
                    <input
                      type="range"
                      name="brandRecognition"
                      min="1"
                      max="10"
                      value={formData.brandRecognition}
                      onChange={handleInputChange}
                      className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer range-slider"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>Bajo</span>
                      <span className="text-purple-400 font-semibold">{formData.brandRecognition}</span>
                      <span>Alto</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-lg font-semibold mb-2">
                      Moneda
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                    >
                      <option value="EUR">Euros (€)</option>
                      <option value="USD">Dólares ($)</option>
                      <option value="GBP">Libras (£)</option>
                    </select>
                  </div>
                </div>

                {/* Advanced Parameters Section */}
                <div className="bg-slate-700/30 p-6 rounded-2xl border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Parámetros Avanzados (Opcional)</h3>
                    <button
                      type="button"
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      {showAdvancedOptions ? 'Ocultar' : 'Personalizar'}
                    </button>
                  </div>
                  
                  {showAdvancedOptions && (
                    <div className="space-y-4">
                      <p className="text-gray-300 text-sm mb-4">
                        Ajusta estos parámetros para personalizar el cálculo según tus necesidades específicas
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white text-sm font-semibold mb-2">
                            Ajuste del Multiplicador de Sector: {formData.customParameters.sectorMultiplierAdjustment}x
                          </label>
                          <input
                            type="range"
                            name="sectorMultiplierAdjustment"
                            min="0.5"
                            max="1.5"
                            step="0.1"
                            value={formData.customParameters.sectorMultiplierAdjustment}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customParameters: {
                                ...prev.customParameters,
                                sectorMultiplierAdjustment: parseFloat(e.target.value)
                              }
                            }))}
                            className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-white text-sm font-semibold mb-2">
                            Peso de Presencia Digital: {formData.customParameters.digitalPresenceWeight}x
                          </label>
                          <input
                            type="range"
                            name="digitalPresenceWeight"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={formData.customParameters.digitalPresenceWeight}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customParameters: {
                                ...prev.customParameters,
                                digitalPresenceWeight: parseFloat(e.target.value)
                              }
                            }))}
                            className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-white text-sm font-semibold mb-2">
                            Peso de Base de Clientes: {formData.customParameters.clientBaseWeight}x
                          </label>
                          <input
                            type="range"
                            name="clientBaseWeight"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={formData.customParameters.clientBaseWeight}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customParameters: {
                                ...prev.customParameters,
                                clientBaseWeight: parseFloat(e.target.value)
                              }
                            }))}
                            className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-white text-sm font-semibold mb-2">
                            Peso de Longevidad: {formData.customParameters.longevityWeight}x
                          </label>
                          <input
                            type="range"
                            name="longevityWeight"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={formData.customParameters.longevityWeight}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customParameters: {
                                ...prev.customParameters,
                                longevityWeight: parseFloat(e.target.value)
                              }
                            }))}
                            className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        
                        {/* Personal Brand Specific Parameters */}
                        {formData.brandType === 'personal' && (
                          <>
                            <div>
                              <label className="block text-white text-sm font-semibold mb-2">
                                Peso de Engagement: {formData.customParameters.engagementWeight}x
                              </label>
                              <input
                                type="range"
                                name="engagementWeight"
                                min="0.5"
                                max="2.0"
                                step="0.1"
                                value={formData.customParameters.engagementWeight}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  customParameters: {
                                    ...prev.customParameters,
                                    engagementWeight: parseFloat(e.target.value)
                                  }
                                }))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-white text-sm font-semibold mb-2">
                                Peso de Influencia: {formData.customParameters.influenceWeight}x
                              </label>
                              <input
                                type="range"
                                name="influenceWeight"
                                min="0.5"
                                max="2.0"
                                step="0.1"
                                value={formData.customParameters.influenceWeight}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  customParameters: {
                                    ...prev.customParameters,
                                    influenceWeight: parseFloat(e.target.value)
                                  }
                                }))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-white text-sm font-semibold mb-2">
                                Peso de Monetización: {formData.customParameters.monetizationWeight}x
                              </label>
                              <input
                                type="range"
                                name="monetizationWeight"
                                min="0.5"
                                max="2.0"
                                step="0.1"
                                value={formData.customParameters.monetizationWeight}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  customParameters: {
                                    ...prev.customParameters,
                                    monetizationWeight: parseFloat(e.target.value)
                                  }
                                }))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-white text-sm font-semibold mb-2">
                                Peso de Autoridad: {formData.customParameters.authorityWeight}x
                              </label>
                              <input
                                type="range"
                                name="authorityWeight"
                                min="0.5"
                                max="2.0"
                                step="0.1"
                                value={formData.customParameters.authorityWeight}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  customParameters: {
                                    ...prev.customParameters,
                                    authorityWeight: parseFloat(e.target.value)
                                  }
                                }))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="bg-blue-600/20 p-4 rounded-lg border border-blue-500/30">
                        <p className="text-blue-200 text-sm">
                          💡 <strong>Consejos:</strong> {formData.brandType === 'personal' ? 
                            'Para marcas personales, el engagement y la autoridad son factores clave. Aumenta el peso de monetización si tienes productos digitales exitosos.' :
                            'Aumenta el peso de presencia digital para marcas con fuerte componente online. Reduce el multiplicador de sector si tu industria está en declive. Ajusta según tu estrategia específica.'
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                  <button
                    onClick={prevStep}
                    className="w-full sm:w-auto bg-slate-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-all duration-300"
                  >
                    ← {t('home.calculator.buttons.previous')}
                  </button>
                  <button
                    id="form-calculate-button"
                    type="button"
                    className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('FORM BUTTON CLICKED - Starting Analysis');
                      
                      // Check usage limits for free users
                      const usage = getUsageLimit();
                      if (userPlan === 'free' && usage.used >= usage.limit) {
                        setShowUpgradeModal(true);
                        return;
                      }
                      
                      // Validation
                      if (!formData.revenue || !formData.clients || !formData.yearsInMarket) {
                        alert('Por favor, completa todos los campos requeridos.');
                        return;
                      }
                      
                      try {
                        // Show loading state
                        setIsCalculating(true);
                        if (userPlan !== 'free') {
                          setIsAnalyzingAI(true);
                        }
                        
                        setTimeout(() => {
                          try {
                            console.log('Starting calculations...');
                            
                            // Calculate brand value (available for all users)
                            const calculatedValue = calculateBrandValueSync();
                            console.log('Calculated value:', calculatedValue);
                            
                            if (!calculatedValue || isNaN(calculatedValue)) {
                              throw new Error('Calculation failed - invalid result');
                            }
                            
                            // Basic report for all users
                            let report;
                            try {
                              report = generatePersonalizedReport(calculatedValue, formData);
                              console.log('Generated basic report');
                            } catch (e) {
                              console.error('Report generation error:', e);
                              report = { 
                                strengths: ['Análisis básico completado'],
                                weaknesses: ['Upgrade para análisis detallado'],
                                recommendations: ['Considera actualizar para obtener insights de IA'],
                                sector: sectors.find(s => s.value === formData.sector)?.label || 'General'
                              };
                            }
                            
                            // AI analyses only for premium users
                            let predictiveAnalysis = null;
                            let sentimentAnalysis = null;
                            let competitiveAnalysis = null;
                            
                            if (userPlan !== 'free') {
                              try {
                                predictiveAnalysis = generatePredictiveAnalysis(calculatedValue, formData);
                                sentimentAnalysis = generateSentimentAnalysis(formData);
                                competitiveAnalysis = generateCompetitiveAnalysis(calculatedValue, formData);
                                console.log('Generated all AI analyses');
                              } catch (e) {
                                console.error('AI analysis error:', e);
                              }
                            }
                            
                            console.log('Analysis completed successfully');
                            
                            // Set all results
                            setResult(calculatedValue);
                            setGeneratedReport(report);
                            setAiAnalysis(predictiveAnalysis);
                            setSentimentAnalysis(sentimentAnalysis);
                            setCompetitiveAnalysis(competitiveAnalysis);
                            
                            console.log('States set, transitioning to results');
                            
                            // Complete loading and transition
                            setIsCalculating(false);
                            setIsAnalyzingAI(false);
                            setActiveStep(3);
                            
                            console.log('Successfully moved to results step');
                            
                          } catch (innerError) {
                            console.error('Error in analysis process:', innerError);
                            setIsCalculating(false);
                            setIsAnalyzingAI(false);
                            alert('Error en el análisis: ' + innerError.message);
                          }
                        }, userPlan === 'free' ? 1000 : 2000); // Faster for free users
                        
                      } catch (error) {
                        console.error('Error starting analysis:', error);
                        alert('Error iniciando el análisis: ' + error.message);
                        setIsCalculating(false);
                        setIsAnalyzingAI(false);
                      }
                    }}
                  >
                    {userPlan === 'free' ? 'Calcular Valor Básico 📊' : 'Calcular Valor con IA 🤖'}
                  </button>
                </div>
              </div>
            )}

            {/* Advanced Loading State with AI Processing */}
            {isCalculating && (
              <div className="text-center py-16 space-y-8">
                <div className="relative">
                  <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-purple-500"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-3xl">🤖</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-3xl font-bold text-white">Análisis de IA en Progreso...</h3>
                  <div className="space-y-3 text-gray-300">
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-pulse text-green-400">✓</div>
                      <p>🔍 Procesando métricas financieras</p>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-pulse text-green-400">✓</div>
                      <p>📊 Comparando con benchmarks del sector</p>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <div className={`${isAnalyzingAI ? 'animate-spin text-blue-400' : 'text-green-400'}`}>
                        {isAnalyzingAI ? '⚙️' : '✓'}
                      </div>
                      <p>🤖 Ejecutando análisis predictivo con IA</p>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <div className={`${isAnalyzingAI ? 'animate-spin text-blue-400' : 'text-green-400'}`}>
                        {isAnalyzingAI ? '⚙️' : '✓'}
                      </div>
                      <p>💭 Analizando sentimiento de marca</p>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <div className={`${isAnalyzingAI ? 'animate-spin text-blue-400' : 'text-green-400'}`}>
                        {isAnalyzingAI ? '⚙️' : '✓'}
                      </div>
                      <p>🏆 Generando benchmarking competitivo</p>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <div className={`${isAnalyzingAI ? 'animate-spin text-blue-400' : 'text-green-400'}`}>
                        {isAnalyzingAI ? '⚙️' : '✓'}
                      </div>
                      <p>📈 Creando recomendaciones estratégicas</p>
                    </div>
                  </div>
                  <div className="bg-slate-700/50 p-4 rounded-xl border border-white/10 max-w-md mx-auto">
                    <p className="text-sm text-blue-200">
                      🧠 <strong>IA Avanzada:</strong> Utilizando machine learning para análisis 
                      predictivo y benchmarking competitivo en tiempo real
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Lead Capture Form */}
            {activeStep === 3 && result !== null && result !== undefined && generatedReport && !isCalculating && !leadSubmitted && (
              <div className="max-w-2xl mx-auto text-center">
                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-3xl p-8 mb-8">
                  <h2 className="text-4xl font-bold text-white mb-6">
                    🎉 ¡Análisis <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">Completado!</span>
                  </h2>
                  <p className="text-xl text-gray-300 mb-6">
                    Tu valoración profesional de marca está lista
                  </p>
                  
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    <div className="flex items-center space-x-2">
                      <div className="text-green-400">✓</div>
                      <span className="text-gray-300">Análisis completo</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-green-400">✓</div>
                      <span className="text-gray-300">Benchmarking sectorial</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-green-400">✓</div>
                      <span className="text-gray-300">Recomendaciones IA</span>
                    </div>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
                    <div className="text-2xl font-bold text-white mb-2">
                      Valor Estimado: {formatCurrency(result)}
                    </div>
                    <p className="text-gray-300 text-sm">
                      Ingresa tu información para acceder al informe completo
                    </p>
                  </div>
                </div>

                <form onSubmit={handleLeadSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        value={leadData.name}
                        onChange={(e) => setLeadData({...leadData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Tu nombre completo"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={leadData.email}
                        onChange={(e) => setLeadData({...leadData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="tu@email.com"
                        required
                      />
                    </div>
                  </div>

                  {leadError && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4">
                      <p className="text-red-400 text-sm">{leadError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmittingLead}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingLead ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Generando informe...</span>
                      </div>
                    ) : (
                      'Ver Mi Informe Completo'
                    )}
                  </button>
                </form>

                <p className="text-gray-400 text-sm mt-4">
                  * Al continuar, accederás a tu valoración completa con análisis detallado y recomendaciones estratégicas
                </p>
              </div>
            )}

            {/* Step 4: Enhanced Results with AI Analysis */}
            {activeStep === 4 && result !== null && result !== undefined && generatedReport && !isCalculating && leadSubmitted && (
              <div className="space-y-6 sm:space-y-8">
                {/* Welcome Message */}
                <div className="text-center py-4">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    ¡Hola {leadData.name}! 👋
                  </h3>
                  <p className="text-gray-300">
                    Aquí tienes tu informe completo de valoración de marca
                  </p>
                </div>
                
                {/* Main Result */}
                <div className="text-center py-6 sm:py-8">
                  <div className="text-4xl sm:text-6xl animate-pulse mb-3 sm:mb-4">🎉</div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
                    Valor Estimado de tu Marca
                  </h3>
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6 lg:p-8 rounded-2xl inline-block mb-4 sm:mb-6 mx-2">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                      {formatCurrency(result)}
                    </div>
                    <p className="text-sm sm:text-lg lg:text-xl mt-1 sm:mt-2 opacity-90">Valoración profesional</p>
                  </div>
                </div>

                {/* Benchmarking Analysis */}
                <div className="bg-slate-700/50 p-4 sm:p-6 rounded-2xl border border-white/10">
                  <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 flex items-center">
                    📊 Análisis Comparativo del Sector
                  </h4>
                  <div className="results-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 justify-items-center">
                    {generatedReport?.benchmarks && (
                      <>
                        <div className="bg-slate-600/50 p-3 sm:p-4 rounded-xl w-full max-w-sm">
                          <div className="text-xs sm:text-sm text-gray-300">Ingresos vs Sector</div>
                          <div className={`text-xl sm:text-2xl font-bold ${(generatedReport.benchmarks.revenuePerformance || 0) > 100 ? 'text-green-400' : (generatedReport.benchmarks.revenuePerformance || 0) > 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {generatedReport.benchmarks.revenuePerformance || 0}%
                          </div>
                          <div className="text-xs text-gray-400">vs benchmark: {formatCurrency(generatedReport.benchmarks.revenue || 0)}</div>
                        </div>
                        <div className="bg-slate-600/50 p-3 sm:p-4 rounded-xl w-full max-w-sm">
                          <div className="text-xs sm:text-sm text-gray-300">Base de Clientes vs Sector</div>
                          <div className={`text-xl sm:text-2xl font-bold ${(generatedReport.benchmarks.clientPerformance || 0) > 100 ? 'text-green-400' : (generatedReport.benchmarks.clientPerformance || 0) > 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {generatedReport.benchmarks.clientPerformance || 0}%
                          </div>
                          <div className="text-xs text-gray-400">vs benchmark: {formatNumber(generatedReport.benchmarks.clients || 0)}</div>
                        </div>
                      </>
                    )}
                    {generatedReport?.benchmarks && (
                      <div className="bg-slate-600/50 p-3 sm:p-4 rounded-xl w-full max-w-sm sm:col-span-2 lg:col-span-1">
                        <div className="text-xs sm:text-sm text-gray-300">Presencia Digital vs Sector</div>
                        <div className={`text-xl sm:text-2xl font-bold ${(generatedReport.benchmarks.digitalPerformance || 0) > 100 ? 'text-green-400' : (generatedReport.benchmarks.digitalPerformance || 0) > 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {generatedReport.benchmarks.digitalPerformance || 0}%
                        </div>
                        <div className="text-xs text-gray-400">vs benchmark: {formatNumber(generatedReport.benchmarks.digital || 0)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Strengths and Weaknesses */}
                <div className="results-grid grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-green-900/20 p-4 sm:p-6 rounded-2xl border border-green-500/30">
                    <h4 className="text-lg sm:text-xl font-bold text-green-400 mb-3 sm:mb-4 flex items-center">
                      ✅ Fortalezas Identificadas
                    </h4>
                    {generatedReport && generatedReport.strengths && generatedReport.strengths.length > 0 ? (
                      <ul className="space-y-2">
                        {(generatedReport.strengths || []).map((strength, index) => (
                          <li key={index} className="text-green-200 flex items-start text-sm sm:text-base">
                            <span className="text-green-400 mr-2 flex-shrink-0">•</span>
                            <span className="break-words">{strength}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-green-200 text-sm sm:text-base">Sigue desarrollando tu marca para identificar fortalezas clave</p>
                    )}
                  </div>

                  <div className="bg-red-900/20 p-4 sm:p-6 rounded-2xl border border-red-500/30">
                    <h4 className="text-lg sm:text-xl font-bold text-red-400 mb-3 sm:mb-4 flex items-center">
                      ⚠️ Áreas de Mejora
                    </h4>
                    {generatedReport && generatedReport.weaknesses && generatedReport.weaknesses.length > 0 ? (
                      <ul className="space-y-2">
                        {(generatedReport.weaknesses || []).map((weakness, index) => (
                          <li key={index} className="text-red-200 flex items-start text-sm sm:text-base">
                            <span className="text-red-400 mr-2 flex-shrink-0">•</span>
                            <span className="break-words">{weakness}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-red-200">¡Excelente! No se identificaron debilidades significativas</p>
                    )}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-blue-900/20 p-6 rounded-2xl border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-blue-400 mb-4 flex items-center">
                    💡 Recomendaciones Personalizadas
                  </h4>
                  {generatedReport && generatedReport.recommendations && generatedReport.recommendations.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(generatedReport.recommendations || []).map((recommendation, index) => (
                        <div key={index} className="bg-blue-800/30 p-4 rounded-xl">
                          <p className="text-blue-200">{recommendation}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-blue-200">Tu marca muestra un excelente rendimiento en todas las áreas clave</p>
                  )}
                </div>

                {/* Country Insights */}
                {generatedReport && generatedReport.countryInsights && generatedReport.countryInsights.length > 0 && (
                  <div className="bg-purple-900/20 p-6 rounded-2xl border border-purple-500/30">
                    <h4 className="text-xl font-bold text-purple-400 mb-4 flex items-center">
                      🌍 Insights del Mercado - {generatedReport.country}
                    </h4>
                    <ul className="space-y-2">
                      {(generatedReport.countryInsights || []).map((insight, index) => (
                        <li key={index} className="text-purple-200 flex items-start">
                          <span className="text-purple-400 mr-2">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Company Profile Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-slate-700 p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {generatedReport.sector}
                    </div>
                    <p className="text-gray-300">Sector</p>
                  </div>
                  <div className="bg-slate-700 p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {formData.businessType} - {formData.brandType === 'empresa' ? 'Empresa' : 'Personal'}
                    </div>
                    <p className="text-gray-300">Tipo</p>
                  </div>
                  <div className="bg-slate-700 p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-purple-400">
                      {formData.brandRecognition}/10
                    </div>
                    <p className="text-gray-300">Reconocimiento</p>
                  </div>
                  <div className="bg-slate-700 p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {generatedReport.country}
                    </div>
                    <p className="text-gray-300">Mercado</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      setResult(null);
                      setGeneratedReport(null);
                      setAiAnalysis(null);
                      setSentimentAnalysis(null);
                      setCompetitiveAnalysis(null);
                      setActiveStep(0);
                      setFormData({
                        revenue: '',
                        clients: '',
                        yearsInMarket: '',
                        socialFollowers: '',
                        monthlyTraffic: '',
                        brandRecognition: '5',
                        tangibleAssets: '',
                        sector: 'tecnologia',
                        currency: 'EUR',
                        businessType: 'B2B',
                        brandType: 'empresa',
                        companySize: 'mediana',
                        country: 'ES',
                        customParameters: {
                          sectorMultiplierAdjustment: 1.0,
                          digitalPresenceWeight: 1.0,
                          clientBaseWeight: 1.0,
                          longevityWeight: 1.0
                        }
                      });
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                  >
                    Nueva Valoración
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="border-2 border-white/20 text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                  >
                    Imprimir Informe
                  </button>
                  <button
                    onClick={() => {
                      const reportData = {
                        brandValue: result,
                        report: generatedReport,
                        aiAnalysis: aiAnalysis,
                        sentimentAnalysis: sentimentAnalysis,
                        competitiveAnalysis: competitiveAnalysis,
                        timestamp: new Date().toISOString()
                      };
                      const dataStr = JSON.stringify(reportData, null, 2);
                      const dataBlob = new Blob([dataStr], {type: 'application/json'});
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'brand-analysis-report.json';
                      link.click();
                    }}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
                  >
                    Exportar Datos IA
                  </button>
                </div>

                {/* AI Predictive Analysis Section - Premium Only */}
                {userPlan === 'free' ? (
                  <div className="mt-12 space-y-8">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                        🤖 Análisis Predictivo con IA
                      </h2>
                      <p className="text-xl text-gray-300">
                        Proyecciones de valor de marca basadas en machine learning
                      </p>
                    </div>

                    {/* Premium Upsell */}
                    <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 p-8 rounded-2xl border border-purple-500/30 text-center">
                      <div className="text-6xl mb-4">🔒</div>
                      <h3 className="text-2xl font-bold text-white mb-4">
                        Desbloquea Análisis Predictivo con IA
                      </h3>
                      <p className="text-gray-300 mb-6">
                        Obtén proyecciones de crecimiento a 5 años, análisis de escenarios y 
                        recomendaciones estratégicas basadas en inteligencia artificial.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-slate-700/50 p-4 rounded-xl">
                          <div className="text-2xl mb-2">📈</div>
                          <h4 className="font-semibold text-white">Proyecciones 5 años</h4>
                          <p className="text-sm text-gray-400">3 escenarios de crecimiento</p>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-xl">
                          <div className="text-2xl mb-2">🎯</div>
                          <h4 className="font-semibold text-white">Insights de IA</h4>
                          <p className="text-sm text-gray-400">Recomendaciones personalizadas</p>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-xl">
                          <div className="text-2xl mb-2">⚠️</div>
                          <h4 className="font-semibold text-white">Análisis de Riesgos</h4>
                          <p className="text-sm text-gray-400">Factores de riesgo identificados</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 mr-4"
                      >
                        Actualizar a Pro - €29/mes
                      </button>
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="border border-white/20 text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                      >
                        Comprar Informe - €19
                      </button>
                    </div>
                  </div>
                ) : aiAnalysis && (
                  <div className="mt-12 space-y-8">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                        🤖 Análisis Predictivo con IA
                      </h2>
                      <p className="text-xl text-gray-300">
                        Proyecciones de valor de marca basadas en machine learning
                      </p>
                    </div>

                    {/* Prediction Scenarios */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {Object.entries(aiAnalysis.scenarios).map(([scenario, data]) => (
                        <div key={scenario} className={`p-6 rounded-2xl border ${
                          scenario === 'conservative' ? 'bg-red-900/20 border-red-500/30' :
                          scenario === 'realistic' ? 'bg-blue-900/20 border-blue-500/30' :
                          'bg-green-900/20 border-green-500/30'
                        }`}>
                          <h4 className={`text-xl font-bold mb-3 ${
                            scenario === 'conservative' ? 'text-red-400' :
                            scenario === 'realistic' ? 'text-blue-400' :
                            'text-green-400'
                          }`}>
                            {scenario === 'conservative' ? '📉 Conservador' :
                             scenario === 'realistic' ? '📊 Realista' :
                             '📈 Optimista'}
                          </h4>
                          <div className="space-y-2">
                            <div className={`text-2xl font-bold ${
                              scenario === 'conservative' ? 'text-red-300' :
                              scenario === 'realistic' ? 'text-blue-300' :
                              'text-green-300'
                            }`}>
                              {formatCurrency(aiAnalysis.predictions[scenario][4].value)}
                            </div>
                            <p className="text-sm text-gray-300">Valor en 5 años</p>
                            <div className="text-sm text-gray-400">
                              Confianza: {data.confidence}%
                            </div>
                            <p className="text-xs text-gray-400">{data.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 5-Year Growth Chart Visualization */}
                    <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-xl font-bold text-white mb-4">📈 Proyección de Crecimiento (5 años)</h4>
                      <div className="space-y-4">
                        {aiAnalysis.predictions.realistic.map((prediction, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-gray-300">{prediction.year}</span>
                            <div className="flex-1 mx-4 bg-slate-600 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                                style={{ width: `${Math.min((prediction.growth / 200) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-white font-semibold w-24 text-right">
                              {formatCurrency(prediction.value)}
                            </span>
                            <span className="text-green-400 text-sm w-16 text-right">
                              +{prediction.growth}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI Insights */}
                    <div className="bg-purple-900/20 p-6 rounded-2xl border border-purple-500/30">
                      <h4 className="text-xl font-bold text-purple-400 mb-4 flex items-center">
                        🧠 Insights de IA
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-semibold text-white mb-2">Análisis Predictivo</h5>
                          <ul className="space-y-1">
                            {aiAnalysis.insights.map((insight, index) => (
                              <li key={index} className="text-purple-200 text-sm flex items-start">
                                <span className="text-purple-400 mr-2">•</span>
                                {insight}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h5 className="font-semibold text-white mb-2">Recomendaciones Estratégicas</h5>
                          <ul className="space-y-1">
                            {aiAnalysis.strategicRecommendations.map((rec, index) => (
                              <li key={index} className="text-purple-200 text-sm flex items-start">
                                <span className="text-purple-400 mr-2">→</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Risk Assessment */}
                    <div className="bg-orange-900/20 p-6 rounded-2xl border border-orange-500/30">
                      <h4 className="text-xl font-bold text-orange-400 mb-4 flex items-center">
                        ⚠️ Análisis de Riesgos
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-semibold text-white mb-2">Factores de Riesgo Identificados</h5>
                          <ul className="space-y-1">
                            {aiAnalysis.riskFactors.map((risk, index) => (
                              <li key={index} className="text-orange-200 text-sm flex items-start">
                                <span className="text-orange-400 mr-2">⚡</span>
                                {risk}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-orange-400 mb-2">
                              {aiAnalysis.confidenceLevel}%
                            </div>
                            <p className="text-orange-200">Nivel de Confianza</p>
                            <p className="text-xs text-orange-300 mt-1">
                              Basado en análisis de IA
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Sentiment Analysis Section */}
                {sentimentAnalysis && (
                  <div className="mt-12 space-y-8">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                        💭 Análisis de Sentimiento de Marca
                      </h2>
                      <p className="text-xl text-gray-300">
                        Percepción de marca en tiempo real con IA
                      </p>
                    </div>

                    {/* Overall Sentiment Score */}
                    <div className="text-center">
                      <div className="inline-block bg-gradient-to-r from-pink-600 to-purple-600 p-8 rounded-2xl">
                        <div className="text-5xl font-bold text-white mb-2">
                          {sentimentAnalysis.overall.score}/100
                        </div>
                        <p className="text-xl text-white/90">{sentimentAnalysis.overall.classification}</p>
                        <p className="text-sm text-white/80 mt-1">
                          Tendencia: {sentimentAnalysis.overall.trend === 'rising' ? '📈 Creciente' : 
                                     sentimentAnalysis.overall.trend === 'declining' ? '📉 Decreciente' : '→ Estable'}
                        </p>
                      </div>
                    </div>

                    {/* Sentiment Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-green-900/20 p-6 rounded-2xl border border-green-500/30 text-center">
                        <div className="text-3xl font-bold text-green-400 mb-2">
                          {sentimentAnalysis.breakdown.positive}%
                        </div>
                        <p className="text-green-200">Positivo</p>
                        <div className="w-full bg-green-900 rounded-full h-2 mt-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${sentimentAnalysis.breakdown.positive}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="bg-yellow-900/20 p-6 rounded-2xl border border-yellow-500/30 text-center">
                        <div className="text-3xl font-bold text-yellow-400 mb-2">
                          {sentimentAnalysis.breakdown.neutral}%
                        </div>
                        <p className="text-yellow-200">Neutral</p>
                        <div className="w-full bg-yellow-900 rounded-full h-2 mt-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${sentimentAnalysis.breakdown.neutral}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="bg-red-900/20 p-6 rounded-2xl border border-red-500/30 text-center">
                        <div className="text-3xl font-bold text-red-400 mb-2">
                          {sentimentAnalysis.breakdown.negative}%
                        </div>
                        <p className="text-red-200">Negativo</p>
                        <div className="w-full bg-red-900 rounded-full h-2 mt-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${sentimentAnalysis.breakdown.negative}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Social Media Metrics */}
                    <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-xl font-bold text-white mb-4">📱 Métricas de Redes Sociales</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-400">
                            {formatNumber(sentimentAnalysis.metrics.totalMentions)}
                          </div>
                          <p className="text-gray-300 text-sm">Menciones Totales</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-400">
                            {sentimentAnalysis.metrics.engagementRate}%
                          </div>
                          <p className="text-gray-300 text-sm">Engagement Rate</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-400">
                            {formatNumber(sentimentAnalysis.metrics.reach)}
                          </div>
                          <p className="text-gray-300 text-sm">Alcance</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-400">
                            {sentimentAnalysis.metrics.influence}
                          </div>
                          <p className="text-gray-300 text-sm">Índice de Influencia</p>
                        </div>
                      </div>
                    </div>

                    {/* Trending Topics */}
                    <div className="bg-indigo-900/20 p-6 rounded-2xl border border-indigo-500/30">
                      <h4 className="text-xl font-bold text-indigo-400 mb-4">🔥 Temas en Tendencia</h4>
                      <div className="space-y-3">
                        {sentimentAnalysis.trendingTopics.map((topic, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-white">{topic.topic}</span>
                            <div className="flex items-center space-x-3">
                              <span className="text-gray-400 text-sm">
                                {topic.mentions} menciones
                              </span>
                              <span className={`px-2 py-1 rounded text-xs ${
                                topic.sentiment === 'positive' ? 'bg-green-600 text-green-100' :
                                topic.sentiment === 'negative' ? 'bg-red-600 text-red-100' :
                                'bg-yellow-600 text-yellow-100'
                              }`}>
                                {topic.sentiment === 'positive' ? '😊 Positivo' :
                                 topic.sentiment === 'negative' ? '😟 Negativo' :
                                 '😐 Neutral'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sentiment Insights and Actions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-cyan-900/20 p-6 rounded-2xl border border-cyan-500/30">
                        <h4 className="text-xl font-bold text-cyan-400 mb-4">🎯 Insights de IA</h4>
                        <ul className="space-y-2">
                          {sentimentAnalysis.sentimentInsights.map((insight, index) => (
                            <li key={index} className="text-cyan-200 flex items-start">
                              <span className="text-cyan-400 mr-2">💡</span>
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-teal-900/20 p-6 rounded-2xl border border-teal-500/30">
                        <h4 className="text-xl font-bold text-teal-400 mb-4">🚀 Acciones Recomendadas</h4>
                        <ul className="space-y-2">
                          {sentimentAnalysis.recommendedActions.map((action, index) => (
                            <li key={index} className="text-teal-200 flex items-start">
                              <span className="text-teal-400 mr-2">→</span>
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Competitive Analysis Section */}
                {competitiveAnalysis && (
                  <div className="mt-12 space-y-8">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                        🏆 Benchmarking Competitivo con IA
                      </h2>
                      <p className="text-xl text-gray-300">
                        Análisis automático de competidores y posición en el mercado
                      </p>
                    </div>

                    {/* Market Position */}
                    <div className="text-center">
                      <div className="inline-block bg-gradient-to-r from-gold via-yellow-500 to-gold p-6 rounded-2xl">
                        <div className="text-4xl font-bold text-white mb-2">
                          #{competitiveAnalysis.marketPosition}
                        </div>
                        <p className="text-xl text-white/90">Posición en el Mercado</p>
                        <p className="text-sm text-white/80 mt-1">
                          {competitiveAnalysis.marketShare}% cuota de mercado
                        </p>
                      </div>
                    </div>

                    {/* Competitor Comparison */}
                    <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-xl font-bold text-white mb-4">⚔️ Análisis de Competidores</h4>
                      <div className="space-y-4">
                        {competitiveAnalysis.competitors.slice(0, 3).map((competitor, index) => (
                          <div key={index} className={`p-4 rounded-xl border ${
                            competitor.position === 'above' ? 'bg-red-900/20 border-red-500/30' : 'bg-green-900/20 border-green-500/30'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-lg font-semibold text-white">{competitor.name}</h5>
                              <div className={`px-3 py-1 rounded text-sm ${
                                competitor.position === 'above' ? 'bg-red-600 text-red-100' : 'bg-green-600 text-green-100'
                              }`}>
                                {competitor.position === 'above' ? 'Superior' : 'Inferior'}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-gray-400">Valor Estimado</p>
                                <p className="text-white font-semibold">{formatCurrency(competitor.estimatedValue)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Ingresos</p>
                                <p className="text-white font-semibold">{formatCurrency(competitor.revenue)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Cuota Mercado</p>
                                <p className="text-white font-semibold">{competitor.marketShare}%</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Reconocimiento</p>
                                <p className="text-white font-semibold">{competitor.brandRecognition}/10</p>
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div>
                                <p className="text-green-400 font-semibold">Fortalezas:</p>
                                <ul className="text-green-200">
                                  {competitor.strengths.map((strength, i) => (
                                    <li key={i}>• {strength}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <p className="text-red-400 font-semibold">Debilidades:</p>
                                <ul className="text-red-200">
                                  {competitor.weaknesses.map((weakness, i) => (
                                    <li key={i}>• {weakness}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Market Benchmarks */}
                    <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-xl font-bold text-white mb-4">📊 Benchmarks del Mercado</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-400">
                            {formatCurrency(competitiveAnalysis.benchmarks.avgValue)}
                          </div>
                          <p className="text-gray-300 text-sm">Valor Promedio</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-400">
                            {formatCurrency(competitiveAnalysis.benchmarks.avgRevenue)}
                          </div>
                          <p className="text-gray-300 text-sm">Ingresos Promedio</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-400">
                            {competitiveAnalysis.benchmarks.avgDigitalPresence}%
                          </div>
                          <p className="text-gray-300 text-sm">Presencia Digital</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-400">
                            {competitiveAnalysis.benchmarks.avgBrandRecognition}/10
                          </div>
                          <p className="text-gray-300 text-sm">Reconocimiento</p>
                        </div>
                      </div>
                    </div>

                    {/* SWOT Analysis */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div className="bg-red-900/20 p-6 rounded-2xl border border-red-500/30">
                          <h4 className="text-xl font-bold text-red-400 mb-4">⚠️ Amenazas</h4>
                          <ul className="space-y-2">
                            {competitiveAnalysis.threats.map((threat, index) => (
                              <li key={index} className="text-red-200 flex items-start">
                                <span className="text-red-400 mr-2">⚡</span>
                                {threat}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-blue-900/20 p-6 rounded-2xl border border-blue-500/30">
                          <h4 className="text-xl font-bold text-blue-400 mb-4">🎯 Insights Competitivos</h4>
                          <ul className="space-y-2">
                            {competitiveAnalysis.competitiveInsights.map((insight, index) => (
                              <li key={index} className="text-blue-200 flex items-start">
                                <span className="text-blue-400 mr-2">💡</span>
                                {insight}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <div className="bg-green-900/20 p-6 rounded-2xl border border-green-500/30">
                          <h4 className="text-xl font-bold text-green-400 mb-4">🚀 Oportunidades</h4>
                          <ul className="space-y-2">
                            {competitiveAnalysis.opportunities.map((opportunity, index) => (
                              <li key={index} className="text-green-200 flex items-start">
                                <span className="text-green-400 mr-2">✨</span>
                                {opportunity}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-purple-900/20 p-6 rounded-2xl border border-purple-500/30">
                          <h4 className="text-xl font-bold text-purple-400 mb-4">📈 Recomendaciones</h4>
                          <ul className="space-y-2">
                            {competitiveAnalysis.strategicRecommendations.map((rec, index) => (
                              <li key={index} className="text-purple-200 flex items-start">
                                <span className="text-purple-400 mr-2">→</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Visual & Audio Benchmark Analysis Section */}
            {result !== null && result !== undefined && leadSubmitted && (
              <div className="mt-16 space-y-8">
                <div className="text-center">
                  <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                    🎨 Benchmark Visual y Auditivo con IA
                  </h2>
                  <p className="text-xl text-gray-300 mb-6">
                    Analiza y optimiza tu identidad visual comparándola con casos de éxito
                  </p>
                  
                  {!showVisualAnalysis ? (
                    <div className="max-w-4xl mx-auto">
                      <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                        <h3 className="text-2xl font-bold text-white mb-6">
                          📸 Sube tus Elementos de Marca
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                          {/* Logo Upload */}
                          <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-purple-400">Logo de la Marca</h4>
                            <div className="border-2 border-dashed border-purple-500/50 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
                              {uploadedLogo ? (
                                <div className="space-y-4">
                                  <img src={uploadedLogo} alt="Logo subido" className="max-w-32 max-h-32 mx-auto" />
                                  <p className="text-green-400">✓ Logo cargado</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <div className="text-4xl">🖼️</div>
                                  <p className="text-gray-300">Arrastra tu logo aquí</p>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    className="hidden"
                                    id="logo-upload"
                                  />
                                  <label
                                    htmlFor="logo-upload"
                                    className="inline-block bg-purple-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-purple-700 transition-colors"
                                  >
                                    Seleccionar Archivo
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Color Palette */}
                          <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-purple-400">Paleta de Colores</h4>
                            <div className="space-y-3">
                              <div className="flex space-x-2">
                                <input
                                  type="color"
                                  className="w-12 h-12 rounded-lg border border-white/20"
                                  onChange={(e) => handleColorAdd(e.target.value)}
                                />
                                <button
                                  onClick={() => handleColorAdd('#' + Math.floor(Math.random()*16777215).toString(16))}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                  Color Aleatorio
                                </button>
                              </div>
                              <div className="flex space-x-2 flex-wrap">
                                {Array.isArray(brandColors) ? brandColors.map((color, index) => (
                                  <div
                                    key={index}
                                    className="w-12 h-12 rounded-lg border-2 border-white/20 relative group cursor-pointer"
                                    style={{ backgroundColor: color }}
                                    onClick={() => handleColorRemove(color)}
                                  >
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                      <span className="text-white text-xs">×</span>
                                    </div>
                                  </div>
                                )) : null}
                              </div>
                              <p className="text-gray-400 text-sm">{Array.isArray(brandColors) ? brandColors.length : 0}/5 colores</p>
                            </div>
                          </div>
                        </div>

                        {/* Typography Selection */}
                        <div className="mb-8">
                          <h4 className="text-lg font-semibold text-purple-400 mb-4">Tipografía Principal</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                              { name: 'Modern Sans', font: 'font-sans', example: 'Aa' },
                              { name: 'Elegant Serif', font: 'font-serif', example: 'Aa' },
                              { name: 'Monospace', font: 'font-mono', example: 'Aa' },
                              { name: 'Custom', font: 'font-bold', example: 'Aa' }
                            ].map((typeface, index) => (
                              <button
                                key={index}
                                onClick={() => setSelectedFont(typeface.name)}
                                className={`p-4 rounded-xl border-2 transition-colors ${
                                  selectedFont === typeface.name 
                                    ? 'border-purple-500 bg-purple-900/30' 
                                    : 'border-white/20 hover:border-purple-400'
                                }`}
                              >
                                <div className={`text-2xl ${typeface.font} mb-2`}>{typeface.example}</div>
                                <p className="text-white text-sm">{typeface.name}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={analyzeVisualBenchmark}
                          disabled={isAnalyzingVisual}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
                        >
                          {isAnalyzingVisual ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              <span>Analizando elementos visuales...</span>
                            </div>
                          ) : (
                            '🔍 Analizar Identidad Visual con IA'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Visual Analysis Results */
                    <div className="space-y-8">
                      {/* Overall Score */}
                      <div className="text-center">
                        <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-2xl">
                          <div className="text-4xl font-bold text-white mb-2">
                            {visualBenchmark.overallScore}/100
                          </div>
                          <p className="text-xl text-white/90">Puntuación Visual Global</p>
                        </div>
                      </div>

                      {/* Detailed Scores */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-slate-700/50 p-6 rounded-xl border border-white/10 text-center">
                          <div className="text-3xl font-bold text-purple-400 mb-2">
                            {visualBenchmark.userAnalysis.logoScore}
                          </div>
                          <p className="text-white font-semibold">Logo</p>
                          <p className="text-gray-400 text-sm">Impacto y legibilidad</p>
                        </div>
                        <div className="bg-slate-700/50 p-6 rounded-xl border border-white/10 text-center">
                          <div className="text-3xl font-bold text-blue-400 mb-2">
                            {visualBenchmark.userAnalysis.colorScore}
                          </div>
                          <p className="text-white font-semibold">Colores</p>
                          <p className="text-gray-400 text-sm">Armonía y distintividad</p>
                        </div>
                        <div className="bg-slate-700/50 p-6 rounded-xl border border-white/10 text-center">
                          <div className="text-3xl font-bold text-green-400 mb-2">
                            {visualBenchmark.userAnalysis.typographyScore}
                          </div>
                          <p className="text-white font-semibold">Tipografía</p>
                          <p className="text-gray-400 text-sm">Legibilidad y personalidad</p>
                        </div>
                        <div className="bg-slate-700/50 p-6 rounded-xl border border-white/10 text-center">
                          <div className="text-3xl font-bold text-yellow-400 mb-2">
                            {visualBenchmark.userAnalysis.cohesionScore}
                          </div>
                          <p className="text-white font-semibold">Cohesión</p>
                          <p className="text-gray-400 text-sm">Consistencia visual</p>
                        </div>
                      </div>

                      {/* Success Cases Comparison */}
                      <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                        <h4 className="text-xl font-bold text-white mb-6">🏆 Comparación con Casos de Éxito</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {visualBenchmark.successCases.map((case_, index) => (
                            <div key={index} className="bg-slate-600/30 p-4 rounded-xl">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-lg font-semibold text-white">{case_.brand}</h5>
                                <div className="text-sm text-gray-400">{case_.sector}</div>
                              </div>
                              <div className="space-y-2 mb-4">
                                <div className="flex space-x-2">
                                  {case_.elements.colors.map((color, idx) => (
                                    <div
                                      key={idx}
                                      className="w-6 h-6 rounded border border-white/20"
                                      style={{ backgroundColor: color }}
                                    />
                                  ))}
                                </div>
                                <p className="text-gray-300 text-sm">{case_.elements.logo}</p>
                                <p className="text-gray-300 text-sm">{case_.elements.typography}</p>
                              </div>
                              <div className="bg-green-900/20 p-3 rounded border border-green-500/30">
                                <div className="text-green-400 font-semibold mb-1">
                                  Performance: {case_.elements.performance}/100
                                </div>
                                <p className="text-green-200 text-sm">{case_.insights}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* AI Recommendations */}
                      <div className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                        <h4 className="text-xl font-bold text-white mb-6">🤖 Recomendaciones de IA</h4>
                        <div className="space-y-4">
                          {visualBenchmark.recommendations.map((rec, index) => (
                            <div key={index} className={`p-4 rounded-xl border ${
                              rec.priority === 'Alta' ? 'bg-red-900/20 border-red-500/30' : 'bg-yellow-900/20 border-yellow-500/30'
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    rec.priority === 'Alta' ? 'bg-red-600 text-red-100' : 'bg-yellow-600 text-yellow-100'
                                  }`}>
                                    {rec.priority}
                                  </span>
                                  <h5 className="text-white font-semibold">{rec.category}</h5>
                                </div>
                                <div className="text-green-400 text-sm font-semibold">{rec.improvement}</div>
                              </div>
                              <p className="text-gray-300 mb-2">{rec.suggestion}</p>
                              <p className="text-blue-200 text-sm">
                                <span className="text-blue-400">💡 Ejemplo:</span> {rec.example}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="text-center">
                        <button
                          onClick={() => setShowVisualAnalysis(false)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl transition-colors"
                        >
                          🔄 Nuevo Análisis Visual
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Calendly CTA - Strategic Consultation */}
      {result !== null && result !== undefined && (
        <div className="py-16 bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-green-800/50 to-emerald-800/50 backdrop-blur-sm rounded-3xl p-8 border border-green-500/30 text-center">
              <div className="text-5xl mb-4">🚀</div>
              <h3 className="text-3xl font-bold text-white mb-4">
                ¿Quieres maximizar este valor?
              </h3>
              <p className="text-green-100 mb-6 text-lg">
                Analicemos juntos tu estrategia para incrementar el valor de tu marca
              </p>
              <CalendlyButton 
                text="Reserva tu sesión"
                variant="accent"
                size="large"
                className="mb-4"
              />
            </div>
          </div>
        </div>
      )}

      {/* AI-Powered Recommendations Section */}
      {result !== null && result !== undefined && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-indigo-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🤖 Recomendaciones Personalizadas con IA
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Descubre las acciones específicas que maximizarán el valor de tu marca
              </p>
            </div>

            {!showRecommendations || !aiRecommendations ? (
              <div className="text-center">
                <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 backdrop-blur-sm rounded-3xl p-10 border border-indigo-500/30 max-w-2xl mx-auto">
                  <div className="text-6xl mb-6">🚀</div>
                  <h3 className="text-3xl font-bold text-white mb-6">
                    Genera tu Estrategia Personalizada
                  </h3>
                  <p className="text-indigo-200 mb-8 text-lg">
                    Nuestra IA analizará tu perfil de marca y generará un roadmap específico 
                    con acciones priorizadas para incrementar tu valor de marca.
                  </p>
                  
                  <button
                    onClick={generateAIRecommendations}
                    disabled={isLoadingRecommendations}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold text-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingRecommendations ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Generando Recomendaciones...
                      </span>
                    ) : (
                      '✨ Generar Recomendaciones IA'
                    )}
                  </button>
                </div>
              </div>
            ) : aiRecommendations && (
              <div className="space-y-8">
                {/* User Profile & Focus Area */}
                <div className="bg-gradient-to-r from-indigo-800/50 to-purple-800/50 backdrop-blur-sm rounded-3xl p-8 border border-indigo-500/30">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">📊 Tu Perfil de Marca</h3>
                      <p className="text-indigo-200 text-lg">{aiRecommendations.user_profile}</p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">🎯 Área de Enfoque</h3>
                      <div className="flex items-center space-x-3">
                        <span className={`px-4 py-2 rounded-xl text-white font-semibold ${
                          aiRecommendations.focus_area === 'growth' ? 'bg-green-600' :
                          aiRecommendations.focus_area === 'optimization' ? 'bg-blue-600' :
                          aiRecommendations.focus_area === 'ma_prep' ? 'bg-purple-600' :
                          'bg-orange-600'
                        }`}>
                          {aiRecommendations.focus_area === 'growth' ? '📈 Crecimiento' :
                           aiRecommendations.focus_area === 'optimization' ? '⚡ Optimización' :
                           aiRecommendations.focus_area === 'ma_prep' ? '💼 Preparación M&A' :
                           '💰 Monetización'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Competitive Analysis & Market Intelligence */}
                {aiRecommendations.competitive_analysis && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                    <h3 className="text-3xl font-bold text-white mb-8 text-center">🏆 Análisis Competitivo</h3>
                    
                    {/* Market Intelligence */}
                    {aiRecommendations.market_intelligence && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">📊 Inteligencia de Mercado</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(aiRecommendations.market_intelligence).map(([key, value]) => (
                            <div key={key} className="bg-slate-700/50 p-4 rounded-xl border border-white/10">
                              <h5 className="text-blue-400 font-semibold mb-2 capitalize">
                                {key.replace(/_/g, ' ')}
                              </h5>
                              <p className="text-gray-300 text-sm">
                                {typeof value === 'string' ? value : JSON.stringify(value)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Direct Competitors */}
                    {aiRecommendations.competitive_analysis.direct_competitors && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">🎯 Competidores Directos</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {aiRecommendations.competitive_analysis.direct_competitors.map((competitor, index) => (
                            <div key={index} className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 p-4 rounded-xl border border-purple-500/30">
                              <h5 className="text-white font-bold mb-3">
                                {typeof competitor === 'string' ? competitor : competitor.name}
                              </h5>
                              {typeof competitor === 'object' && competitor !== null && (
                                <div className="space-y-2">
                                  {competitor.website && (
                                    <p className="text-blue-400 text-sm">
                                      <span className="font-semibold">🌐 Web:</span> {competitor.website}
                                    </p>
                                  )}
                                  {competitor.main_advantage && (
                                    <p className="text-green-400 text-sm">
                                      <span className="font-semibold">💪 Ventaja:</span> {competitor.main_advantage}
                                    </p>
                                  )}
                                  {competitor.estimated_value && (
                                    <p className="text-purple-400 text-sm">
                                      <span className="font-semibold">💰 Valor:</span> {competitor.estimated_value}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-between mt-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      competitor.strength === 'high' ? 'bg-red-600/30 text-red-300' :
                                      competitor.strength === 'medium' ? 'bg-yellow-600/30 text-yellow-300' :
                                      'bg-green-600/30 text-green-300'
                                    }`}>
                                      {competitor.strength === 'high' ? 'Amenaza Alta' :
                                       competitor.strength === 'medium' ? 'Competencia Media' :
                                       'Competencia Baja'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Market Gaps and Opportunities */}
                    {aiRecommendations.competitive_analysis.market_gaps && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">🔍 Oportunidades de Mercado</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {aiRecommendations.competitive_analysis.market_gaps.map((gap, index) => (
                            <div key={index} className="bg-green-900/20 p-4 rounded-xl border border-green-500/30">
                              <p className="text-green-200">{gap}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Competitive Advantages */}
                    {aiRecommendations.competitive_analysis.competitive_advantages && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">⚡ Ventajas Competitivas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {aiRecommendations.competitive_analysis.competitive_advantages.map((advantage, index) => (
                            <div key={index} className="bg-blue-900/20 p-4 rounded-xl border border-blue-500/30">
                              <p className="text-blue-200">{advantage}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Benchmarks */}
                    {aiRecommendations.market_benchmarks && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">📈 Benchmarks del Sector</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {Object.entries(aiRecommendations.market_benchmarks).map(([key, value]) => (
                            <div key={key} className="bg-slate-700/50 p-4 rounded-xl border border-white/10 text-center">
                              <h5 className="text-orange-400 font-semibold mb-2 text-sm capitalize">
                                {key.replace(/_/g, ' ')}
                              </h5>
                              <p className="text-white text-lg font-bold">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Key Metrics & Next Milestone */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">🏆 Próximo Hito</h4>
                    <p className="text-gray-300 text-lg">{aiRecommendations.next_milestone}</p>
                  </div>
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📈 Incremento Estimado</h4>
                    <p className="text-green-400 text-2xl font-bold">{aiRecommendations.estimated_value_increase}</p>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <h3 className="text-3xl font-bold text-white mb-8 text-center">🎯 Plan de Acción Personalizado</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {aiRecommendations.recommendations.map((rec, index) => (
                      <div key={index} className={`p-6 rounded-2xl border relative overflow-hidden ${
                        rec.priority === 'high' ? 'bg-gradient-to-r from-red-900/30 to-pink-900/30 border-red-500/50' :
                        rec.priority === 'medium' ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50' :
                        'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-500/50'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              rec.priority === 'high' ? 'bg-red-600 text-red-100' :
                              rec.priority === 'medium' ? 'bg-yellow-600 text-yellow-100' :
                              'bg-blue-600 text-blue-100'
                            }`}>
                              {rec.priority === 'high' ? 'ALTA' : rec.priority === 'medium' ? 'MEDIA' : 'BAJA'}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              rec.impact === 'high' ? 'bg-green-600 text-green-100' :
                              rec.impact === 'medium' ? 'bg-yellow-600 text-yellow-100' :
                              'bg-gray-600 text-gray-100'
                            }`}>
                              {rec.impact === 'high' ? 'ALTO IMPACTO' : rec.impact === 'medium' ? 'MEDIO IMPACTO' : 'BAJO IMPACTO'}
                            </span>
                          </div>
                          <span className="text-gray-400 text-sm">{rec.timeline}</span>
                        </div>
                        
                        <h4 className="text-xl font-bold text-white mb-3">{rec.title}</h4>
                        <p className="text-gray-300 mb-4">{rec.description}</p>
                        
                        {/* ROI and Investment */}
                        <div className="space-y-3 mb-4">
                          {rec.estimated_roi && (
                            <div className="bg-green-900/20 p-3 rounded-xl border border-green-500/30">
                              <span className="text-green-400 font-semibold">💰 ROI Estimado: {rec.estimated_roi}</span>
                            </div>
                          )}
                          
                          {rec.investment_required && (
                            <div className="bg-blue-900/20 p-3 rounded-xl border border-blue-500/30">
                              <span className="text-blue-400 font-semibold">💸 Inversión Requerida: {rec.investment_required}</span>
                            </div>
                          )}
                        </div>

                        {/* Success Metrics */}
                        {rec.success_metrics && rec.success_metrics.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-yellow-400 font-semibold mb-2">📊 Métricas de Éxito:</h5>
                            <ul className="text-gray-300 text-sm space-y-1">
                              {rec.success_metrics.map((metric, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-yellow-400 mr-2">•</span>
                                  {metric}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Implementation Steps */}
                        {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-purple-400 font-semibold mb-2">⚙️ Pasos de Implementación:</h5>
                            <ol className="text-gray-300 text-sm space-y-1">
                              {rec.implementation_steps.map((step, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-purple-400 mr-2 font-semibold">{idx + 1}.</span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Risk Assessment */}
                        {rec.risk_assessment && (
                          <div className="mb-4">
                            <h5 className="text-red-400 font-semibold mb-2">⚠️ Evaluación de Riesgos:</h5>
                            <div className="bg-red-900/20 p-3 rounded-xl border border-red-500/30">
                              {rec.risk_assessment.probability_success && (
                                <p className="text-red-200 text-sm mb-1">
                                  <span className="font-semibold">Probabilidad de Éxito:</span> {rec.risk_assessment.probability_success}
                                </p>
                              )}
                              {rec.risk_assessment.main_risks && (
                                <div className="text-sm">
                                  <span className="text-red-300 font-semibold">Riesgos Principales:</span>
                                  <ul className="text-red-200 mt-1">
                                    {rec.risk_assessment.main_risks.map((risk, idx) => (
                                      <li key={idx} className="ml-2">• {risk}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Case Studies */}
                        {rec.case_studies && rec.case_studies.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-cyan-400 font-semibold mb-2">📚 Casos de Estudio:</h5>
                            <div className="space-y-2">
                              {rec.case_studies.map((caseStudy, idx) => (
                                <div key={idx} className="bg-cyan-900/20 p-3 rounded-xl border border-cyan-500/30">
                                  <p className="text-cyan-200 text-sm">
                                    <span className="font-semibold">{caseStudy.company}</span> ({caseStudy.industry})
                                  </p>
                                  <p className="text-cyan-300 text-sm">{caseStudy.result}</p>
                                  <p className="text-cyan-400 text-xs">Tiempo: {caseStudy.timeframe}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Competitive Advantage */}
                        {rec.competitive_advantage && (
                          <div className="bg-indigo-900/20 p-3 rounded-xl border border-indigo-500/30">
                            <h5 className="text-indigo-400 font-semibold mb-1">🏆 Ventaja Competitiva:</h5>
                            <p className="text-indigo-200 text-sm">{rec.competitive_advantage}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Metrics to Track */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xl font-bold text-white mb-4">📊 Métricas Clave a Seguir</h4>
                  <div className="flex flex-wrap gap-3">
                    {aiRecommendations.key_metrics_to_track.map((metric, index) => (
                      <span key={index} className="bg-indigo-600/30 text-indigo-200 px-4 py-2 rounded-xl border border-indigo-500/50">
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Advanced Analysis Section - Condensed */}
                <div className="bg-gradient-to-r from-slate-800/50 to-purple-800/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/30">
                  <h3 className="text-3xl font-bold text-white mb-6 text-center">🔍 Análisis Completo</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Combined Audit & Report */}
                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">📋</div>
                      <h4 className="text-lg font-bold text-white mb-2">Auditoría Interactiva</h4>
                      <p className="text-gray-300 text-xs mb-4">Análisis profundo de identidad y posicionamiento</p>
                      <button
                        onClick={generateBrandAudit}
                        disabled={isLoadingAudit}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50"
                      >
                        {isLoadingAudit ? 'Analizando...' : '🔍 Iniciar Auditoría'}
                      </button>
                    </div>

                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">📊</div>
                      <h4 className="text-lg font-bold text-white mb-2">Reporte PDF</h4>
                      <p className="text-gray-300 text-xs mb-4">Dashboard ejecutivo completo en PDF</p>
                      <button
                        onClick={() => generateExecutiveReport('comprehensive')}
                        disabled={isGeneratingReport}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50"
                      >
                        {isGeneratingReport ? 'Generando...' : '📄 Descargar PDF'}
                      </button>
                    </div>

                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">🎯</div>
                      <h4 className="text-lg font-bold text-white mb-2">Plan de Acción</h4>
                      <p className="text-gray-300 text-xs mb-4">Siguientes pasos recomendados</p>
                      <Link
                        to="/asesoria"
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 inline-block"
                      >
                        💼 Consulta Estratégica
                      </Link>
                    </div>
                  </div>
                </div>

                {/* CTA to Book Consultation */}
                <div className="text-center">
                  <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/30">
                    <h4 className="text-2xl font-bold text-white mb-4">¿Necesitas ayuda implementando estas recomendaciones?</h4>
                    <p className="text-cyan-200 mb-6">
                      Agenda una consulta gratuita para crear un plan de implementación específico
                    </p>
                    <button 
                      onClick={() => window.open('https://calendly.com/tamara-navarrete', '_blank')}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold text-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
                    >
                      📅 Agendar Consulta Gratuita
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blockchain Certificate Section */}
      {showCertificate && blockchainCertificate && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🏆 Tu Certificado Blockchain
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Tu marca premium ha recibido un certificado verificable en blockchain de Polygon
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Certificate Display */}
              <div className="bg-gradient-to-r from-purple-800/50 to-pink-800/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/30 mb-8">
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">🏆</div>
                  <h3 className="text-3xl font-bold text-white mb-4">Certificado de Valoración Blockchain</h3>
                  <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                    <div className="text-2xl font-bold text-white mb-2">
                      Marca Certificada por YourBrandValue
                    </div>
                    <div className="text-lg text-purple-200 mb-4">
                      Verificado en Blockchain de Polygon
                    </div>
                    <div className="text-sm text-gray-300 font-mono mb-4">
                      Certificate ID: {blockchainCertificate.certificate_id}
                    </div>
                    <div className="flex justify-center space-x-4">
                      <a 
                        href={blockchainCertificate.verification_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                      >
                        🔗 Verificar Certificado
                      </a>
                      {blockchainCertificate.polygon_explorer_url && (
                        <a 
                          href={blockchainCertificate.polygon_explorer_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300"
                        >
                          🔍 Ver en Blockchain
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Sharing Badges */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📱 Badge para LinkedIn</h4>
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div 
                        dangerouslySetInnerHTML={{ __html: blockchainCertificate.linkedin_badge_html }}
                        className="text-center"
                      />
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(blockchainCertificate.linkedin_badge_html);
                        alert('HTML copiado al portapapeles');
                      }}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      📋 Copiar HTML para LinkedIn
                    </button>
                  </div>

                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📧 Badge para Email</h4>
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div 
                        dangerouslySetInnerHTML={{ __html: blockchainCertificate.email_signature_html }}
                        className="text-center"
                      />
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(blockchainCertificate.email_signature_html);
                        alert('HTML copiado al portapapeles');
                      }}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                    >
                      📋 Copiar HTML para Email
                    </button>
                  </div>
                </div>

                {/* Certificate Benefits */}
                <div className="mt-8 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 text-center">💎 Beneficios de tu Certificado</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl mb-2">🔐</div>
                      <div className="text-white font-semibold">Verificación Blockchain</div>
                      <div className="text-gray-300 text-sm">Inmutable y verificable</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2">🌍</div>
                      <div className="text-white font-semibold">Credibilidad Global</div>
                      <div className="text-gray-300 text-sm">Reconocimiento internacional</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2">📈</div>
                      <div className="text-white font-semibold">Valor Añadido</div>
                      <div className="text-gray-300 text-sm">Diferenciación competitiva</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tamara Navarrete Advisory Services Section */}
      {result !== null && result !== undefined && (
        <div id="asesoria" className="py-20 bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🚀 Tu Siguiente Paso: Asesoría Estratégica
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Ahora que conoces el valor de tu marca, descubre cómo maximizarlo con estrategias expertas 
                de M&A, desarrollo de negocio y optimización de marca.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Expert Profile */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                <div className="flex items-center mb-6">
                  <div className="w-24 h-24 rounded-2xl mr-6 relative overflow-hidden">
                    <div className="w-full h-full relative bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                      {/* Placeholder temporal - aquí irá la imagen de Tamara */}
                      <div className="text-white font-bold text-2xl">TN</div>
                      {/* Borde decorativo */}
                      <div className="absolute inset-0 border-2 border-purple-400/50 rounded-2xl"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl"></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Tamara Navarrete</h3>
                    <p className="text-purple-400 font-semibold">Estratega de M&A y Desarrollo de Negocio</p>
                    <p className="text-gray-400">Especialista en Valoración de Marcas</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-purple-900/30 p-4 rounded-xl">
                    <h4 className="text-white font-semibold mb-2">🎯 Especialidades</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Estrategias de M&A y Due Diligence</li>
                      <li>• Asesoramiento en rebranding y reposicionamiento</li>
                      <li>• Optimización de valor de marca pre-venta</li>
                      <li>• Desarrollo de negocio y crecimiento</li>
                      <li>• Auditorías integrales de marca</li>
                      <li>• Preparación para rondas de inversión</li>
                    </ul>
                  </div>

                  <div className="bg-blue-900/30 p-4 rounded-xl">
                    <h4 className="text-white font-semibold mb-2">📊 Experiencia</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• +200 valoraciones de marca realizadas</li>
                      <li>• +€50M en transacciones asesoradas</li>
                      <li>• Especializada en empresas tech y marcas personales</li>
                      <li>• Metodologías propias de optimización</li>
                    </ul>
                  </div>

                  <div className="bg-green-900/30 p-4 rounded-xl">
                    <h4 className="text-white font-semibold mb-2">🏆 Casos de Éxito</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Startup tech: +85% incremento valor pre-exit</li>
                      <li>• Rebranding B2B: +150% mejora en percepción de marca</li>
                      <li>• E-commerce: Preparación exitosa para adquisición</li>
                      <li>• Marca personal: Estrategia de monetización escalable</li>
                      <li>• Consultora: Optimización para ronda Serie A</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* HIGHLIGHTED CONSULTATION SECTION */}
              <div className="space-y-6">
                {/* PRIMARY CTA - FREE CONSULTATION */}
                <div className="bg-gradient-to-r from-cyan-600/30 to-blue-600/30 backdrop-blur-sm rounded-3xl p-10 border-2 border-cyan-400/50 text-center relative overflow-hidden">
                  {/* Background decorative elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/10 rounded-full blur-xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/10 rounded-full blur-xl"></div>
                  
                  <div className="relative z-10">
                    <div className="text-6xl mb-4">🎁</div>
                    <h3 className="text-4xl font-bold text-white mb-6">
                      Solicita tu Consulta Gratuita
                    </h3>
                    <p className="text-cyan-200 mb-8 text-xl leading-relaxed">
                      Análisis inicial de 30 minutos sin costo para evaluar tu situación actual 
                      y diseñar la estrategia perfecta para maximizar el valor de tu marca.
                    </p>
                    
                    <div className="bg-white/10 p-6 rounded-2xl mb-8">
                      <h4 className="text-white font-bold text-lg mb-4">En esta consulta descubrirás:</h4>
                      <ul className="text-cyan-200 space-y-2 text-left">
                        <li className="flex items-center"><span className="text-cyan-400 mr-3">✓</span>Las oportunidades específicas de tu marca</li>
                        <li className="flex items-center"><span className="text-cyan-400 mr-3">✓</span>Estrategias personalizadas para tu sector</li>
                        <li className="flex items-center"><span className="text-cyan-400 mr-3">✓</span>Roadmap prioritizado de acciones</li>
                        <li className="flex items-center"><span className="text-cyan-400 mr-3">✓</span>Estimación de ROI potencial</li>
                      </ul>
                    </div>
                    
                    <button 
                      onClick={() => window.open('https://calendly.com/tamara-navarrete', '_blank')}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-6 rounded-2xl font-bold text-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-110 shadow-2xl border-2 border-white/20 mb-6"
                    >
                      📅 Reservar Consulta Gratuita Ahora
                    </button>
                    
                    <div className="flex justify-center items-center space-x-4 text-sm text-cyan-300">
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        Sin compromiso
                      </span>
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        30 minutos
                      </span>
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        100% gratuito
                      </span>
                    </div>
                  </div>
                </div>

                {/* SECONDARY - OTHER SERVICES (Minimized) */}
                <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-white/5">
                  <details className="group">
                    <summary className="cursor-pointer text-center">
                      <h4 className="text-lg text-gray-300 hover:text-white transition-colors inline-flex items-center">
                        Servicios especializados
                        <svg className="ml-2 h-5 w-5 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </h4>
                    </summary>
                    
                    <div className="mt-6 space-y-4 opacity-70">
                      {/* Condensed Service List */}
                      <div className="grid grid-cols-1 gap-3 text-sm">
                        <div className="bg-slate-700/50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">🎯 Auditoría Integral de Marca</span>
                            <p className="text-gray-400 text-xs">Análisis profundo y roadmap de optimización</p>
                          </div>
                          <span className="text-purple-400 font-bold">€2,500</span>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">🚀 Preparación para M&A</span>
                            <p className="text-gray-400 text-xs">Due diligence y maximización de valuación</p>
                          </div>
                          <span className="text-blue-400 font-bold">€5,000</span>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">📈 Desarrollo de Negocio</span>
                            <p className="text-gray-400 text-xs">Estrategias de crecimiento acelerado</p>
                          </div>
                          <span className="text-green-400 font-bold">€450/h</span>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">💰 Preparación para Inversión</span>
                            <p className="text-gray-400 text-xs">Pitch deck y investor readiness</p>
                          </div>
                          <span className="text-yellow-400 font-bold">€3,500</span>
                        </div>
                        <div className="bg-slate-700/50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">🎨 Asesoramiento en Rebranding</span>
                            <p className="text-gray-400 text-xs">Transformación completa de identidad</p>
                          </div>
                          <span className="text-pink-400 font-bold">€4,200</span>
                        </div>
                      </div>
                      
                      <div className="text-center pt-4 border-t border-white/10">
                        <p className="text-gray-400 text-sm">
                          Para cualquiera de estos servicios, comenzamos siempre con la consulta gratuita
                        </p>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div className="bg-slate-800/30 p-6 rounded-2xl">
                <div className="text-3xl font-bold text-purple-400 mb-2">200+</div>
                <p className="text-gray-300">Valoraciones Realizadas</p>
              </div>
              <div className="bg-slate-800/30 p-6 rounded-2xl">
                <div className="text-3xl font-bold text-blue-400 mb-2">€50M+</div>
                <p className="text-gray-300">En Transacciones</p>
              </div>
              <div className="bg-slate-800/30 p-6 rounded-2xl">
                <div className="text-3xl font-bold text-green-400 mb-2">85%</div>
                <p className="text-gray-300">Éxito en M&A</p>
              </div>
              <div className="bg-slate-800/30 p-6 rounded-2xl">
                <div className="text-3xl font-bold text-yellow-400 mb-2">5★</div>
                <p className="text-gray-300">Rating Promedio</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="py-20 bg-slate-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              {t('home.faq.title')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('home.faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: t('home.faq.questions.q1.question'),
                answer: t('home.faq.questions.q1.answer')
              },
              {
                question: t('home.faq.questions.q2.question'),
                answer: t('home.faq.questions.q2.answer')
              },
              {
                question: t('home.faq.questions.q3.question'),
                answer: t('home.faq.questions.q3.answer')
              },
              {
                question: t('home.faq.questions.q4.question'),
                answer: t('home.faq.questions.q4.answer')
              },
              {
                question: t('home.faq.questions.q5.question'),
                answer: t('home.faq.questions.q5.answer')
              }
            ].map((faq, index) => (
              <div key={index} className="bg-slate-700/50 rounded-2xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 text-left hover:bg-slate-600/30 transition-colors duration-300 flex justify-between items-center"
                >
                  <h3 className="text-xl font-bold text-white pr-4">{faq.question}</h3>
                  <span className={`text-purple-400 text-2xl transform transition-transform duration-300 ${
                    openFAQ === index ? 'rotate-45' : ''
                  }`}>
                    +
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Brand Audit Section */}
      {result !== null && result !== undefined && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🔍 Auditoría Interactiva de Marca
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Análisis profundo y scoring de tu marca en todas las dimensiones críticas
              </p>
            </div>

            {!showAudit ? (
              <div className="text-center">
                <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-3xl p-10 border border-green-500/30 max-w-2xl mx-auto">
                  <div className="text-6xl mb-6">🎯</div>
                  <h3 className="text-3xl font-bold text-white mb-6">
                    Auditoria Completa de Marca
                  </h3>
                  <p className="text-green-200 mb-8 text-lg">
                    Evaluación exhaustiva en 4 categorías críticas: Identidad de Marca, Presencia Digital, 
                    Posición en el Mercado y Excelencia Operacional.
                  </p>
                  
                  <button
                    onClick={generateBrandAudit}
                    disabled={isLoadingAudit}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-10 py-4 rounded-2xl font-bold text-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingAudit ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Realizando Auditoría...
                      </span>
                    ) : (
                      '🔍 Iniciar Auditoría de Marca'
                    )}
                  </button>
                </div>
              </div>
            ) : brandAudit && (
              <div className="space-y-8">
                {/* Overall Score */}
                <div className="bg-gradient-to-r from-green-800/50 to-emerald-800/50 backdrop-blur-sm rounded-3xl p-8 border border-green-500/30 text-center">
                  <h3 className="text-3xl font-bold text-white mb-4">Puntuación General</h3>
                  <div className="text-8xl font-bold mb-4" style={{color: brandAudit.overall_score >= 80 ? '#10B981' : brandAudit.overall_score >= 60 ? '#F59E0B' : '#EF4444'}}>
                    {brandAudit.overall_score}/100
                  </div>
                  <p className="text-green-200 text-xl">
                    {brandAudit.overall_score >= 80 ? 'Excelente - Marca muy sólida' :
                     brandAudit.overall_score >= 60 ? 'Bueno - Oportunidades de mejora' :
                     'Necesita mejora - Plan de acción requerido'}
                  </p>
                </div>

                {/* Category Breakdown */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <h3 className="text-3xl font-bold text-white mb-8 text-center">📊 Análisis por Categorías</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {Object.entries(brandAudit.category_scores).map(([category, score]) => (
                      <div key={category} className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-xl font-bold text-white">
                            {category === 'brand_identity' ? '🎨 Identidad de Marca' :
                             category === 'digital_presence' ? '💻 Presencia Digital' :
                             category === 'market_position' ? '🏆 Posición en Mercado' :
                             '⚡ Excelencia Operacional'}
                          </h4>
                          <span className={`text-3xl font-bold ${
                            score >= 80 ? 'text-green-400' :
                            score >= 60 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {score}
                          </span>
                        </div>
                        
                        <div className="w-full bg-slate-600 rounded-full h-3 mb-4 overflow-hidden">
                          <div 
                            className={`h-3 rounded-full ${
                              score >= 80 ? 'bg-green-500' :
                              score >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(score, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Priority Actions */}
                {brandAudit.priority_actions && brandAudit.priority_actions.length > 0 && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                    <h3 className="text-3xl font-bold text-white mb-6 text-center">🚨 Acciones Prioritarias</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {brandAudit.priority_actions.slice(0, 6).map((action, index) => (
                        <div key={index} className="bg-red-900/30 p-4 rounded-xl border border-red-500/30">
                          <div className="flex items-center space-x-3">
                            <span className="bg-red-600 text-red-100 px-2 py-1 rounded text-xs font-bold">
                              CRÍTICO
                            </span>
                            <span className="text-white font-semibold">{action}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths and Weaknesses */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {brandAudit.strengths && brandAudit.strengths.length > 0 && (
                    <div className="bg-green-900/30 p-6 rounded-2xl border border-green-500/30">
                      <h4 className="text-xl font-bold text-white mb-4">💪 Fortalezas</h4>
                      <ul className="space-y-2">
                        {brandAudit.strengths.map((strength, index) => (
                          <li key={index} className="text-green-200 flex items-start">
                            <span className="text-green-400 mr-2">✓</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {brandAudit.weaknesses && brandAudit.weaknesses.length > 0 && (
                    <div className="bg-orange-900/30 p-6 rounded-2xl border border-orange-500/30">
                      <h4 className="text-xl font-bold text-white mb-4">⚠️ Debilidades</h4>
                      <ul className="space-y-2">
                        {brandAudit.weaknesses.map((weakness, index) => (
                          <li key={index} className="text-orange-200 flex items-start">
                            <span className="text-orange-400 mr-2">!</span>
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Benchmark Comparison */}
                {brandAudit.benchmark_comparison && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📈 Comparación con la Industria</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gray-400">
                          {brandAudit.benchmark_comparison.industry_average}
                        </div>
                        <div className="text-gray-300">Promedio Industria</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-yellow-400">
                          {brandAudit.benchmark_comparison.top_quartile}
                        </div>
                        <div className="text-gray-300">Top 25%</div>
                      </div>
                      <div>
                        <div className={`text-2xl font-bold ${
                          brandAudit.benchmark_comparison.your_score >= brandAudit.benchmark_comparison.top_quartile ? 'text-green-400' :
                          brandAudit.benchmark_comparison.your_score >= brandAudit.benchmark_comparison.industry_average ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {brandAudit.benchmark_comparison.your_score}
                        </div>
                        <div className="text-gray-300">Tu Puntuación</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pricing section continues here */}

      {/* Footer */}
      <footer className="bg-slate-900 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
              Your<span className="text-purple-400">Brand</span>Value
            </div>
            <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
              {t('home.footer.subtitle')}
            </p>
            
            {/* Calendly CTA in Footer */}
            <div className="mb-6">
              <CalendlyButton 
                text={t('home.footer.bookSession')}
                variant="secondary"
                size="medium"
                showMicrocopy={false}
              />
            </div>
            
            <div className="footer-grid flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-gray-400 text-sm sm:text-base">
              <Link to="/faq" className="hover:text-white transition-colors py-1">{t('home.footer.links.faq')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors py-1">{t('home.footer.links.terms')}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors py-1">{t('home.footer.links.privacy')}</Link>
              <Link to="/contacto" className="hover:text-white transition-colors py-1">{t('home.footer.links.contact')}</Link>
            </div>
            <p className="text-gray-500 mt-6 sm:mt-8 text-xs sm:text-sm">
              {t('home.footer.copyright')}
            </p>
            <p className="text-gray-500 mt-2 text-xs sm:text-sm">
              🌐 <span className="text-purple-400">yourbrandvalue.com</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Component for pre-activated calculator (accessed via /calculator button)
const BrandValueCalculatorActivated = () => {
  const { t } = useTranslations();
  // Copy ALL states from BrandValueCalculator - MUST BE IDENTICAL
  const [formData, setFormData] = useState({
    brandName: '',
    website: '', // New field for website URL
    brandType: 'empresa',
    companySize: 'mediana',
    country: 'ES',
    
    // BASIC FIELDS - CRITICAL FOR FUNCTIONALITY
    revenue: '',
    clients: '',
    yearsInMarket: '',
    socialFollowers: '',
    monthlyTraffic: '',
    brandRecognition: '',
    tangibleAssets: '',
    sector: '',
    businessType: '',
    
    // Company specific fields
    companyName: '',
    companyRevenue: '',
    industry: '',
    employees: '',
    marketPosition: '',
    digitalPresence: '',
    brandAge: '',
    
    // Personal specific fields
    personalName: '',
    personalRevenue: '',
    
    // Additional fields for complete functionality
    customerSatisfaction: '',
    geographicReach: '',
    userGoals: '',
    
    // Personal Brand Specific Fields
    personalBrandFields: {
      instagramFollowers: '',
      tiktokFollowers: '',
      linkedinFollowers: '',
      youtubeSubscribers: '',
      youtubeViews: '',
      averageEngagementRate: '',
      monthlyCollaborations: '',
      coursesProducts: '',
      avgProductPrice: '',
      speakingFees: '',
      consultingHourlyRate: '',
      personalBrandNiche: 'lifestyle',
      contentType: 'mixed',
      audienceLocation: 'national',
      brandAuthority: '5'
    },
    // Sector-specific fields
    sectorSpecificFields: {
      // Ecommerce
      has_own_website: true,
      sells_on_marketplaces: false,
      marketplaces_used: [],
      marketplace_revenue_percentage: 0,
      has_multiple_brands: false,
      number_of_brands: 1,
      main_sales_channel: 'website',
      
      // Technology
      has_mobile_app: false,
      tech_stack: [],
      has_api: false,
      has_saas_model: false,
      
      // Consulting
      service_types: [],
      client_retention_rate: 0.8,
      average_project_value: 0,
      
      // Healthcare
      has_certifications: false,
      regulatory_compliance: [],
      
      // Manufacturing
      has_own_production: false,
      distribution_channels: [],
      
      // Finance
      licenses_held: [],
      aum: 0,
      
      // Retail
      number_of_locations: 1,
      franchise_model: false
    },
    customParameters: {
      sectorMultiplierAdjustment: 1.0,
      digitalPresenceWeight: 1.0,
      clientBaseWeight: 1.0,
      longevityWeight: 1.0,
      // Personal Brand Parameters
      engagementWeight: 1.0,
      influenceWeight: 1.0,
      monetizationWeight: 1.0,
      authorityWeight: 1.0
    }
  });

  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [brandTypeSelected, setBrandTypeSelected] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [competitiveAnalysis, setCompetitiveAnalysis] = useState(null);
  const [isAnalyzingAI, setIsAnalyzingAI] = useState(false);
  const [userPlan, setUserPlan] = useState('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  
  // Lead capture states
  const [leadData, setLeadData] = useState({
    name: '',
    email: ''
  });
  const [leadId, setLeadId] = useState(null);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // Visual & Audio Benchmark states
  const [visualBenchmark, setVisualBenchmark] = useState(null);
  const [isAnalyzingVisual, setIsAnalyzingVisual] = useState(false);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [selectedFont, setSelectedFont] = useState('');
  const [brandColors, setBrandColors] = useState([]);
  
  // AI Recommendations states
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  
  // Brand Audit states
  const [brandAudit, setBrandAudit] = useState(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  
  // Executive Report states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  
  // Blockchain Certificate states
  const [blockchainCertificate, setBlockchainCertificate] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);

  // Auto-scroll to calculator section when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      const calculatorElement = document.querySelector('.calculator-section');
      if (calculatorElement) {
        calculatorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle input changes - MUST BE IDENTICAL TO MAIN COMPONENT
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested personal brand fields
    if (name.startsWith('personalBrandFields.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        personalBrandFields: {
          ...prev.personalBrandFields,
          [fieldName]: value
        }
      }));
    } 
    // Handle sector-specific fields
    else if (name.startsWith('sectorSpecificFields.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        sectorSpecificFields: {
          ...prev.sectorSpecificFields,
          [fieldName]: value === 'true' ? true : value === 'false' ? false : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Copy ALL functions from BrandValueCalculator
  const safeFormatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleBrandTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, brandType: type }));
    setBrandTypeSelected(true);
    setActiveStep(0); // Start at step 0 (basic info)
  };

  const steps = [
    { title: 'Tipo', icon: '👤' },
    { title: 'Datos', icon: '📊' },
    { title: 'Mercado', icon: '🎯' },
    { title: 'Resultado', icon: '💰' }
  ];

  // Safe step navigation function to prevent array length errors
  const nextStep = () => {
    console.log('nextStep called, current activeStep:', activeStep);
    console.log('steps array:', steps);
    console.log('steps.length:', steps ? steps.length : 'undefined');
    
    if (Array.isArray(steps) && activeStep < steps.length - 1) {
      const newStep = activeStep + 1;
      console.log('Moving to step:', newStep);
      setActiveStep(newStep);
    } else {
      console.log('Cannot move to next step, at boundary or steps not defined');
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // AI Recommendations function
  const generateAIRecommendations = async () => {
    if (!result) return;
    
    setIsLoadingRecommendations(true);
    
    try {
      const brandAnalysisData = {
        brand_name: formData.brandType === 'empresa' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        website: formData.website || null, // Website URL for deeper analysis
        revenue: parseFloat(formData.companyRevenue) || parseFloat(formData.personalRevenue) || parseFloat(formData.revenue) || 0,
        industry: formData.industry || 'technology',
        employees: parseInt(formData.employees) || null,
        brand_age: formData.brandAge || '3-5',
        market_position: formData.marketPosition || 'emerging',
        digital_presence: formData.digitalPresence || 'good',
        brand_recognition: formData.brandRecognition || 'national',
        current_value: result || 0,
        user_goals: formData.userGoals || null,
        
        // Sector-specific fields
        ...formData.sectorSpecificFields
      };

      console.log('Sending brand analysis data:', brandAnalysisData);
      console.log('Revenue being sent:', brandAnalysisData.revenue);
      console.log('Current value being sent:', brandAnalysisData.current_value);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(brandAnalysisData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recommendations = await response.json();
      console.log('AI Recommendations received:', recommendations);
      console.log('Checking for blockchain certificate...');
      console.log('recommendations.blockchain_certificate:', recommendations.blockchain_certificate);
      console.log('Certificate exists?', !!recommendations.blockchain_certificate);
      
      setAiRecommendations(recommendations);
      setShowRecommendations(true);
      
      // Check if blockchain certificate was issued for premium brand
      if (recommendations.blockchain_certificate) {
        console.log('✅ Blockchain certificate received:', recommendations.blockchain_certificate);
        setBlockchainCertificate(recommendations.blockchain_certificate);
        setShowCertificate(true);
        console.log('✅ Certificate display states set');
      } else {
        console.log('❌ No blockchain certificate in response');
        console.log('Response keys:', Object.keys(recommendations));
        console.log('Brand value used:', recommendations.current_value || 'not found');
      }
      
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      alert('Error generando recomendaciones: ' + error.message);
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  // Brand Audit function
  const generateBrandAudit = async () => {
    if (!result) return;
    
    setIsLoadingAudit(true);
    
    try {
      const auditData = {
        brand_name: formData.brandType === 'company' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        industry: formData.industry || 'general',
        current_brand_elements: {
          has_logo: formData.hasLogo === 'yes',
          has_guidelines: formData.hasBrandGuidelines === 'yes',
          brand_colors: brandColors || [],
          brand_consistency: formData.brandConsistency || 'medium'
        },
        digital_presence_data: {
          website_quality: formData.digitalPresence || 'basic',
          social_media: formData.socialMedia || 'basic',
          online_reviews: formData.onlineReviews || 'mixed',
          seo_ranking: formData.searchRanking || 'average'
        },
        market_data: {
          market_position: formData.marketPosition || 'emerging',
          brand_recognition: formData.brandRecognition || 'local',
          competitive_advantage: formData.competitiveAdvantage || 'limited',
          customer_loyalty: formData.customerLoyalty || 'medium'
        }
      };

      console.log('Sending brand audit data:', auditData);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audit = await response.json();
      console.log('Brand Audit received:', audit);
      
      setBrandAudit(audit);
      setShowAudit(true);
      
    } catch (error) {
      console.error('Error generating brand audit:', error);
      alert('Error generando auditoría: ' + error.message);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  // Executive Report function
  const generateExecutiveReport = async (reportType = 'comprehensive') => {
    if (!result && !aiRecommendations && !brandAudit) return;
    
    setIsGeneratingReport(true);
    
    try {
      const reportData = {
        brand_name: formData.brandType === 'company' ? formData.companyName : formData.personalName,
        brand_type: formData.brandType,
        report_type: reportType,
        include_charts: true,
        include_recommendations: true,
        data_source: {
          brand_value: result,
          ai_recommendations: aiRecommendations,
          audit_result: brandAudit,
          form_data: formData
        }
      };

      console.log('Generating executive report:', reportData);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-executive-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reportResult = await response.json();
      console.log('Executive Report generated:', reportResult);
      
      // Create download link
      const pdfBlob = new Blob([atob(reportResult.pdf_base64)], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setReportUrl(url);
      
      // Auto download
      const link = document.createElement('a');
      link.href = url;
      link.download = reportResult.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error generating executive report:', error);
      alert('Error generando reporte: ' + error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Copy calculateBrandValue function
  const calculateBrandValue = async () => {
    console.log('calculateBrandValue called');
    
    try {
      setIsCalculating(true);
      setIsAnalyzingAI(true);
      
      // Simulate AI analysis delay
      setTimeout(async () => {
        try {
          console.log('Starting calculation with formData:', formData);
          
          // ROBUST DATA VALIDATION AND DEBUGGING
          console.log('=== DEBUGGING CALCULATION START ===');
          console.log('formData.brandType:', formData.brandType);
          console.log('formData.companyRevenue:', formData.companyRevenue, typeof formData.companyRevenue);
          console.log('formData.personalRevenue:', formData.personalRevenue, typeof formData.personalRevenue);
          console.log('formData.revenue:', formData.revenue, typeof formData.revenue);
          console.log('formData.industry:', formData.industry);
          
          // Get revenue with comprehensive validation
          let revenue = 0;
          
          if (formData.brandType === 'company' || formData.brandType === 'empresa') {
            // Try multiple sources for company revenue
            const sources = [formData.companyRevenue, formData.revenue];
            for (const source of sources) {
              if (source !== undefined && source !== null && source !== '') {
                const parsed = parseFloat(source);
                if (!isNaN(parsed) && parsed > 0) {
                  revenue = parsed;
                  console.log('Revenue source found (company):', source, '→', revenue);
                  break;
                }
              }
            }
          } else {
            // Try multiple sources for personal revenue
            const sources = [formData.personalRevenue, formData.revenue];
            for (const source of sources) {
              if (source !== undefined && source !== null && source !== '') {
                const parsed = parseFloat(source);
                if (!isNaN(parsed) && parsed > 0) {
                  revenue = parsed;
                  console.log('Revenue source found (personal):', source, '→', revenue);
                  break;
                }
              }
            }
          }
          
          console.log('Final revenue for calculation:', revenue);
          console.log('Revenue validation:', {
            isNumber: typeof revenue === 'number',
            isNaN: isNaN(revenue),
            isPositive: revenue > 0
          });
          
          if (revenue <= 0) {
            throw new Error('Por favor ingresa un valor de ingresos válido');
          }
          
          // COMPREHENSIVE VALIDATION OF INDUSTRY AND BRAND TYPE
          console.log('=== MULTIPLIER CALCULATION DEBUG ===');
          console.log('formData.industry:', formData.industry, typeof formData.industry);
          console.log('formData.brandType:', formData.brandType, typeof formData.brandType);
          
          // Normalize industry and brand type
          const normalizedIndustry = (formData.industry || 'other').toLowerCase().trim();
          const normalizedBrandType = (formData.brandType || 'empresa').toLowerCase().trim();
          
          console.log('Normalized values:', { normalizedIndustry, normalizedBrandType });
          
          // Enhanced industry multipliers with better fallbacks
          const industryMultipliers = {
            'technology': { company: 8, personal: 10, empresa: 8, personal: 10 },
            'consulting': { company: 6, personal: 8, empresa: 6, personal: 8 },
            'ecommerce': { company: 5, personal: 7, empresa: 5, personal: 7 },
            'e-commerce': { company: 5, personal: 7, empresa: 5, personal: 7 },
            'manufacturing': { company: 4, personal: 5, empresa: 4, personal: 5 },
            'services': { company: 5, personal: 6, empresa: 5, personal: 6 },
            'healthcare': { company: 7, personal: 9, empresa: 7, personal: 9 },
            'finance': { company: 6, personal: 8, empresa: 6, personal: 8 },
            'education': { company: 5, personal: 7, empresa: 5, personal: 7 },
            'real-estate': { company: 4, personal: 5, empresa: 4, personal: 5 },
            'media': { company: 6, personal: 8, empresa: 6, personal: 8 },
            'other': { company: 4, personal: 5, empresa: 4, personal: 5 }
          };
          
          // Get multiplier with robust fallback logic
          let multiplier = 4; // Safe default
          
          if (industryMultipliers[normalizedIndustry]) {
            if (industryMultipliers[normalizedIndustry][normalizedBrandType]) {
              multiplier = industryMultipliers[normalizedIndustry][normalizedBrandType];
            } else if (industryMultipliers[normalizedIndustry]['empresa']) {
              multiplier = industryMultipliers[normalizedIndustry]['empresa'];
            } else if (industryMultipliers[normalizedIndustry]['company']) {
              multiplier = industryMultipliers[normalizedIndustry]['company'];
            }
          }
          
          console.log('Multiplier calculation:', {
            industry: normalizedIndustry,
            brandType: normalizedBrandType,
            multiplier: multiplier,
            available: industryMultipliers[normalizedIndustry] || 'not found'
          });
          
          // ROBUST CALCULATION WITH COMPREHENSIVE VALIDATION
          console.log('=== FINAL CALCULATION DEBUG ===');
          
          // Validate all inputs before calculation
          if (revenue <= 0) {
            throw new Error(`Invalid revenue: ${revenue}. Please enter a valid revenue amount.`);
          }
          
          if (isNaN(multiplier) || multiplier <= 0) {
            throw new Error(`Invalid multiplier: ${multiplier}. System error in industry calculation.`);
          }
          
          // Perform calculation with additional safety checks
          const divisionFactor = multiplier / 10;
          const rawCalculation = revenue * divisionFactor;
          const calculatedValue = Math.round(rawCalculation);
          
          console.log('Calculation step-by-step:', {
            revenue: revenue,
            multiplier: multiplier,
            divisionFactor: divisionFactor,
            rawCalculation: rawCalculation,
            finalValue: calculatedValue,
            validation: {
              revenueValid: !isNaN(revenue) && revenue > 0,
              multiplierValid: !isNaN(multiplier) && multiplier > 0,
              divisionFactorValid: !isNaN(divisionFactor) && divisionFactor > 0,
              rawCalculationValid: !isNaN(rawCalculation) && rawCalculation > 0,
              finalValueValid: !isNaN(calculatedValue) && calculatedValue > 0
            }
          });
          
          // Final validation before proceeding
          if (isNaN(calculatedValue) || calculatedValue <= 0) {
            throw new Error(`Calculation failed - invalid result. 
              Revenue: ${revenue} (${typeof revenue})
              Multiplier: ${multiplier} (${typeof multiplier})
              Division Factor: ${divisionFactor}
              Raw Result: ${rawCalculation}
              Final Result: ${calculatedValue}
              Please check input data.`);
          }
          
          console.log('✅ Calculation successful:', calculatedValue);
          console.log('=== CALCULATION DEBUG END ===');
          
          // Generate mock analyses
          const predictiveAnalysis = {
            growthPotential: Math.round(calculatedValue * 0.3),
            riskFactors: ['Competencia creciente', 'Dependencia de canal único'],
            opportunities: ['Expansión digital', 'Nuevos mercados'],
            threats: ['Saturación del mercado', 'Cambios regulatorios']
          };
          
          const sentimentAnalysis = {
            overall: 'positive',
            score: 0.7,
            mentions: 150,
            positive: 60,
            neutral: 25,
            negative: 15
          };
          
          const competitiveAnalysis = {
            position: 'strong',
            marketShare: 15,
            competitors: [
              { name: 'Competidor A', strength: 'high' },
              { name: 'Competidor B', strength: 'medium' }
            ]
          };
          
          // Generate report
          const report = {
            brandValue: calculatedValue,
            industry: formData.industry,
            brandType: formData.brandType,
            strengths: [
              'Posicionamiento sólido en el mercado',
              'Buena presencia digital',
              'Reconocimiento de marca creciente'
            ],
            weaknesses: [
              'Dependencia de canales limitados',
              'Necesita mayor diversificación'
            ],
            recommendations: [
              'Expandir presencia en redes sociales',
              'Diversificar canales de ingresos',
              'Implementar estrategia de contenidos'
            ],
            benchmarks: {
              industry: formData.industry,
              digitalPerformance: 75,
              brandRecognition: 65,
              marketPosition: 70
            },
            countryInsights: [
              'Mercado español en crecimiento',
              'Oportunidades en mercados internacionales'
            ],
            country: 'España'
          };
          
          console.log('All analyses completed');
          
          // Set all results
          setResult(calculatedValue);
          setGeneratedReport(report);
          setAiAnalysis(predictiveAnalysis);
          setSentimentAnalysis(sentimentAnalysis);
          setCompetitiveAnalysis(competitiveAnalysis);
          
          console.log('States set, transitioning to results');
          
          // Complete loading and transition to results
          setIsCalculating(false);
          setIsAnalyzingAI(false);
          setActiveStep(4); // Go to results step
          
          console.log('Successfully moved to results step');
          
          // Generate AI recommendations after calculation
          console.log('Calling generateAIRecommendations...');
          try {
            await generateAIRecommendations();
            console.log('AI Recommendations generated successfully');
            // Force show recommendations after generation
            setShowRecommendations(true);
          } catch (aiError) {
            console.error('Error generating AI recommendations:', aiError);
            // Continue even if AI recommendations fail
          }
          
        } catch (innerError) {
          console.error('Error in calculation process:', innerError);
          setIsCalculating(false);
          setIsAnalyzingAI(false);
          alert('Error en el cálculo: ' + innerError.message);
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error in calculateBrandValue:', error);
      alert('Error iniciando el análisis: ' + error.message);
      setIsCalculating(false);
      setIsAnalyzingAI(false);
    }
  };

  // Return the complete calculator interface like the main component
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      
      {/* Calculator Section - Pre-activated */}
      <div className="calculator-section py-20 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('home.calculator.mainTitle')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('home.calculator.mainSubtitle')}
            </p>
          </div>

          {/* Brand Type Selection - Same as before */}
          {!brandTypeSelected ? (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10 text-center">
              <h3 className="text-3xl font-bold text-white mb-8">
                {t('home.calculator.brandTypeQuestion')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <button
                  onClick={() => handleBrandTypeSelect('empresa')}
                  className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-2 border-purple-500/30 rounded-2xl p-8 text-left hover:from-purple-600/30 hover:to-pink-600/30 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-4">🏢</span>
                    <h4 className="text-2xl font-bold text-white">{t('home.calculator.brandCompany')}</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    {t('home.calculator.brandCompanyDesc')}
                  </p>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• {t('home.calculator.brandCompanyFeatures.feature1')}</li>
                    <li>• {t('home.calculator.brandCompanyFeatures.feature2')}</li>
                    <li>• {t('home.calculator.brandCompanyFeatures.feature3')}</li>
                    <li>• {t('home.calculator.brandCompanyFeatures.feature4')}</li>
                  </ul>
                </button>

                <button
                  onClick={() => handleBrandTypeSelect('personal')}
                  className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-2 border-cyan-500/30 rounded-2xl p-8 text-left hover:from-cyan-600/30 hover:to-blue-600/30 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-4">👤</span>
                    <h4 className="text-2xl font-bold text-white">{t('home.calculator.brandPersonal')}</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    {t('home.calculator.brandPersonalDesc')}
                  </p>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• {t('home.calculator.brandPersonalFeatures.feature1')}</li>
                    <li>• {t('home.calculator.brandPersonalFeatures.feature2')}</li>
                    <li>• {t('home.calculator.brandPersonalFeatures.feature3')}</li>
                    <li>• {t('home.calculator.brandPersonalFeatures.feature4')}</li>
                  </ul>
                </button>
              </div>
            </div>
          ) : (
            // Now copy the full calculator form and results from the main component
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              {/* Progress Steps - Only show when brand type is selected */}
              {brandTypeSelected && steps && Array.isArray(steps) && steps.length > 0 && (
                <div className="flex justify-center mb-12">
                  <div className="progress-steps flex space-x-2 sm:space-x-4 w-full max-w-full overflow-x-auto pb-2">
                    {steps.map((step, index) => (
                      <div
                        key={index}
                        className={`flex flex-col items-center min-w-0 flex-1 ${
                          index <= activeStep ? 'opacity-100' : 'opacity-50'
                        }`}
                      >
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-xl mb-2 transition-all duration-300 ${
                            index === activeStep
                              ? 'bg-purple-600 text-white shadow-lg transform scale-110'
                              : index < activeStep
                              ? 'bg-green-600 text-white'
                              : 'bg-slate-600 text-gray-400'
                          }`}
                        >
                          {index < activeStep ? '✓' : step.icon}
                        </div>
                        <span className="text-white text-xs sm:text-sm text-center hidden sm:block">{step.title}</span>
                        <span className="font-semibold text-xs sm:hidden">{step.title.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Steps */}
              {activeStep === 0 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Información Básica</h3>
                  
                  {formData.brandType === 'empresa' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-white text-sm font-semibold mb-2">
                          Nombre de la Empresa
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="Ej: TechStartup SL"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-white text-sm font-semibold mb-2">
                          🌐 Sitio Web (Opcional - para análisis avanzado)
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="https://www.tu-empresa.com"
                        />
                        <p className="text-gray-400 text-xs mt-1">
                          💡 Incluir tu web nos permite análisis más detallado de competencia y SEO
                        </p>
                      </div>
                      <div>
                        <label className="block text-white text-sm font-semibold mb-2">
                          Ingresos Anuales (€)
                        </label>
                        <input
                          type="number"
                          name="companyRevenue"
                          value={formData.companyRevenue}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="Ej: 75000"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-white text-sm font-semibold mb-2">
                          Nombre Personal
                        </label>
                        <input
                          type="text"
                          name="personalName"
                          value={formData.personalName}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="Ej: María García"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-white text-sm font-semibold mb-2">
                          🌐 Tu Web o Landing Personal (Opcional - para análisis avanzado)
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="https://www.tu-nombre.com"
                        />
                        <p className="text-gray-400 text-xs mt-1">
                          💡 Incluir tu web permite análisis más profundo de tu presencia digital
                        </p>
                      </div>
                      <div>
                        <label className="block text-white text-sm font-semibold mb-2">
                          Ingresos Anuales (€)
                        </label>
                        <input
                          type="number"
                          name="personalRevenue"
                          value={formData.personalRevenue}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                          placeholder="Ej: 120000"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Industria
                      </label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="">Selecciona una industria</option>
                        <option value="technology">Tecnología</option>
                        <option value="consulting">Consultoría</option>
                        <option value="ecommerce">E-commerce</option>
                        <option value="manufacturing">Manufactura</option>
                        <option value="services">Servicios</option>
                        <option value="healthcare">Salud</option>
                        <option value="finance">Finanzas</option>
                        <option value="education">Educación</option>
                        <option value="real-estate">Inmobiliaria</option>
                        <option value="media">Medios</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Edad de la Marca
                      </label>
                      <select
                        name="brandAge"
                        value={formData.brandAge}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="0-1">0-1 años</option>
                        <option value="1-3">1-3 años</option>
                        <option value="3-5">3-5 años</option>
                        <option value="5-10">5-10 años</option>
                        <option value="10+">10+ años</option>
                      </select>
                    </div>
                  </div>

                  {/* Additional critical fields identified by testing */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Número de Clientes
                      </label>
                      <input
                        type="number"
                        name="clients"
                        value={formData.clients}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 150"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Años en el Mercado
                      </label>
                      <input
                        type="number"
                        name="yearsInMarket"
                        value={formData.yearsInMarket}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 5"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Seguidores en Redes Sociales
                      </label>
                      <input
                        type="number"
                        name="socialFollowers"
                        value={formData.socialFollowers}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 10000"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Tráfico Web Mensual
                      </label>
                      <input
                        type="number"
                        name="monthlyTraffic"
                        value={formData.monthlyTraffic}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 5000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Reconocimiento de Marca
                      </label>
                      <select
                        name="brandRecognition"
                        value={formData.brandRecognition}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="local">Local</option>
                        <option value="national">Nacional</option>
                        <option value="international">Internacional</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Activos Tangibles (€)
                      </label>
                      <input
                        type="number"
                        name="tangibleAssets"
                        value={formData.tangibleAssets}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 50000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Posición en el Mercado
                      </label>
                      <select
                        name="marketPosition"
                        value={formData.marketPosition}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="emerging">Emergente</option>
                        <option value="established">Establecida</option>
                        <option value="leader">Líder</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Presencia Digital
                      </label>
                      <select
                        name="digitalPresence"
                        value={formData.digitalPresence}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="basic">Básica</option>
                        <option value="good">Buena</option>
                        <option value="advanced">Avanzada</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Sector-specific fields - MUST BE IDENTICAL TO MAIN COMPONENT */}
                  {formData.industry === 'ecommerce' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.mainSalesChannel')}
                        </label>
                        <select
                          name="sectorSpecificFields.main_sales_channel"
                          value={formData.sectorSpecificFields.main_sales_channel}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="website">{t('home.calculator.fields.ownWebsite')}</option>
                          <option value="marketplaces">{t('home.calculator.fields.marketplaces')}</option>
                          <option value="mixed">{t('home.calculator.fields.mixed')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.sellsOnMarketplaces')}
                        </label>
                        <select
                          name="sectorSpecificFields.sells_on_marketplaces"
                          value={formData.sectorSpecificFields.sells_on_marketplaces}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                          <option value="false">{t('home.calculator.fields.no')}</option>
                        </select>
                      </div>
                      
                      {formData.sectorSpecificFields.sells_on_marketplaces === 'true' && (
                        <div>
                          <label className="block text-white text-lg font-semibold mb-2">
                            {t('home.calculator.fields.marketplaceRevenuePercentage')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            name="sectorSpecificFields.marketplace_revenue_percentage"
                            value={formData.sectorSpecificFields.marketplace_revenue_percentage}
                            onChange={handleInputChange}
                            className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                            placeholder={t('home.calculator.fields.marketplaceRevenuePercentagePlaceholder')}
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Gestionas múltiples marcas?
                        </label>
                        <select
                          name="sectorSpecificFields.has_multiple_brands"
                          value={formData.sectorSpecificFields.has_multiple_brands}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                      
                      {formData.sectorSpecificFields.has_multiple_brands === 'true' && (
                        <div>
                          <label className="block text-white text-lg font-semibold mb-2">
                            {t('home.calculator.fields.brandCount')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            name="sectorSpecificFields.number_of_brands"
                            value={formData.sectorSpecificFields.number_of_brands}
                            onChange={handleInputChange}
                            className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                            placeholder={t('home.calculator.fields.brandCountPlaceholder')}
                          />
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Technology sector fields */}
                  {formData.industry === 'technology' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.hasMobileApp')}
                        </label>
                        <select
                          name="sectorSpecificFields.has_mobile_app"
                          value={formData.sectorSpecificFields.has_mobile_app}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">{t('home.calculator.fields.no')}</option>
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          {t('home.calculator.fields.hasSaasModel')}
                        </label>
                        <select
                          name="sectorSpecificFields.has_saas_model"
                          value={formData.sectorSpecificFields.has_saas_model}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">{t('home.calculator.fields.no')}</option>
                          <option value="true">{t('home.calculator.fields.yes')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Ofreces API?
                        </label>
                        <select
                          name="sectorSpecificFields.has_api"
                          value={formData.sectorSpecificFields.has_api}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Consulting sector fields */}
                  {formData.industry === 'consulting' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Tasa de Retención de Clientes (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          name="sectorSpecificFields.client_retention_rate"
                          value={formData.sectorSpecificFields.client_retention_rate * 100}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) / 100;
                            setFormData(prev => ({
                              ...prev,
                              sectorSpecificFields: {
                                ...prev.sectorSpecificFields,
                                client_retention_rate: value
                              }
                            }));
                          }}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 80"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          Valor Promedio de Proyecto (EUR)
                        </label>
                        <input
                          type="number"
                          name="sectorSpecificFields.average_project_value"
                          value={formData.sectorSpecificFields.average_project_value}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                          placeholder="ej: 15000"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Healthcare sector fields */}
                  {formData.industry === 'healthcare' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Tienes certificaciones profesionales?
                        </label>
                        <select
                          name="sectorSpecificFields.has_certifications"
                          value={formData.sectorSpecificFields.has_certifications}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Manufacturing sector fields */}
                  {formData.industry === 'manufacturing' && (
                    <>
                      <div>
                        <label className="block text-white text-lg font-semibold mb-2">
                          ¿Tienes producción propia?
                        </label>
                        <select
                          name="sectorSpecificFields.has_own_production"
                          value={formData.sectorSpecificFields.has_own_production}
                          onChange={handleInputChange}
                          className="w-full bg-slate-700 text-white p-4 rounded-xl border border-white/20 focus:border-purple-500 focus:outline-none text-lg"
                        >
                          <option value="false">No</option>
                          <option value="true">Sí</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between pt-6">
                    <button
                      onClick={() => setBrandTypeSelected(false)}
                      className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      ← Volver
                    </button>
                    <button
                      onClick={nextStep}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Continuar →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1 - Market Information */}
              {activeStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Información de Mercado</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Posición en el Mercado
                      </label>
                      <select
                        name="marketPosition"
                        value={formData.marketPosition}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="">Selecciona posición</option>
                        <option value="emerging">Emergente</option>
                        <option value="established">Establecida</option>
                        <option value="leader">Líder</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Presencia Digital
                      </label>
                      <select
                        name="digitalPresence"
                        value={formData.digitalPresence}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="">Selecciona presencia</option>
                        <option value="basic">Básica</option>
                        <option value="good">Buena</option>
                        <option value="advanced">Avanzada</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Reconocimiento de Marca
                      </label>
                      <select
                        name="brandRecognition"
                        value={formData.brandRecognition}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="">Selecciona reconocimiento</option>
                        <option value="local">Local</option>
                        <option value="national">Nacional</option>
                        <option value="international">Internacional</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Número de Empleados
                      </label>
                      <input
                        type="number"
                        name="employees"
                        value={formData.employees}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                        placeholder="Ej: 15"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between pt-6">
                    <button
                      onClick={prevStep}
                      className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      ← {t('home.calculator.buttons.previous')}
                    </button>
                    <button
                      onClick={nextStep}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Continuar →
                    </button>
                  </div>
                </div>
              )}

              {activeStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Métricas Digitales</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Presencia Digital
                      </label>
                      <select
                        name="digitalPresence"
                        value={formData.digitalPresence}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="basic">Básica</option>
                        <option value="good">Buena</option>
                        <option value="excellent">Excelente</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Posición en el Mercado
                      </label>
                      <select
                        name="marketPosition"
                        value={formData.marketPosition}
                        onChange={handleInputChange}
                        className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                      >
                        <option value="emerging">Emergente</option>
                        <option value="established">Establecida</option>
                        <option value="leader">Líder</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">
                      Reconocimiento de Marca
                    </label>
                    <select
                      name="brandRecognition"
                      value={formData.brandRecognition}
                      onChange={handleInputChange}
                      className="w-full p-3 rounded-lg bg-slate-700 text-white border border-slate-600 focus:border-purple-500"
                    >
                      <option value="local">Local</option>
                      <option value="national">Nacional</option>
                      <option value="international">Internacional</option>
                    </select>
                  </div>

                  <div className="flex justify-between pt-6">
                    <button
                      onClick={() => setActiveStep(1)}
                      className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      ← Volver
                    </button>
                    <button
                      onClick={nextStep}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Continuar →
                    </button>
                  </div>
                </div>
              )}

              {activeStep === 3 && (
                <div className="space-y-6 text-center">
                  <h3 className="text-2xl font-bold text-white mb-6">Listo para Calcular</h3>
                  <p className="text-gray-300 mb-8">
                    Hemos recopilado toda la información necesaria. ¡Descubre el valor de tu marca!
                  </p>
                  
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={() => setActiveStep(2)}
                      className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      ← Revisar
                    </button>
                    <button
                      onClick={calculateBrandValue}
                      disabled={isCalculating}
                      className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 disabled:opacity-50"
                    >
                      {isCalculating ? (
                        <span className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Calculando...
                        </span>
                      ) : (
                        'Calcular Valor de Marca 🚀'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results Section - Copy from main component */}
      {result !== null && result !== undefined && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                💰 Tu Valor de Marca
              </h2>
              <div className="text-6xl sm:text-8xl font-bold text-green-400 mb-4">
                €{safeFormatCurrency(result)}
              </div>
              <p className="text-xl text-gray-300">
                Valor estimado de tu marca {formData.brandType === 'company' ? formData.companyName : formData.personalName}
              </p>
            </div>

            {/* Results breakdown */}
            {generatedReport && (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">
                  📊 Detalles del Análisis
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Strengths */}
                  {generatedReport && generatedReport.strengths && generatedReport.strengths.length > 0 ? (
                    <div className="bg-green-900/30 p-6 rounded-xl">
                      <h4 className="text-xl font-bold text-white mb-4">💪 Fortalezas</h4>
                      <ul className="space-y-2">
                        {(generatedReport.strengths || []).map((strength, index) => (
                          <li key={index} className="text-green-200 flex items-start">
                            <span className="text-green-400 mr-2">•</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Weaknesses */}
                  {generatedReport && generatedReport.weaknesses && generatedReport.weaknesses.length > 0 ? (
                    <div className="bg-orange-900/30 p-6 rounded-xl">
                      <h4 className="text-xl font-bold text-white mb-4">⚠️ Áreas de Mejora</h4>
                      <ul className="space-y-2">
                        {(generatedReport.weaknesses || []).map((weakness, index) => (
                          <li key={index} className="text-orange-200 flex items-start">
                            <span className="text-orange-400 mr-2">•</span>
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                {/* Recommendations */}
                {generatedReport && generatedReport.recommendations && generatedReport.recommendations.length > 0 ? (
                  <div className="mt-8 bg-blue-900/30 p-6 rounded-xl">
                    <h4 className="text-xl font-bold text-white mb-4">🎯 Recomendaciones</h4>
                    <ul className="space-y-2">
                      {(generatedReport.recommendations || []).map((recommendation, index) => (
                        <li key={index} className="text-blue-200 flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          {recommendation}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI-Powered Recommendations Section - Copy from main component */}
      {result !== null && result !== undefined && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-indigo-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🤖 Recomendaciones Personalizadas con IA
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Descubre las acciones específicas que maximizarán el valor de tu marca
              </p>
            </div>

            {!showRecommendations || !aiRecommendations ? (
              <div className="text-center">
                <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 backdrop-blur-sm rounded-3xl p-10 border border-indigo-500/30 max-w-2xl mx-auto">
                  <div className="text-6xl mb-6">🚀</div>
                  <h3 className="text-3xl font-bold text-white mb-6">
                    Genera tu Estrategia Personalizada
                  </h3>
                  <p className="text-indigo-200 mb-8 text-lg">
                    Nuestra IA analizará tu perfil de marca y generará un roadmap específico 
                    con acciones priorizadas para incrementar tu valor de marca.
                  </p>
                  
                  <button
                    onClick={generateAIRecommendations}
                    disabled={isLoadingRecommendations}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold text-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingRecommendations ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Generando Recomendaciones...
                      </span>
                    ) : (
                      '✨ Generar Recomendaciones IA'
                    )}
                  </button>
                </div>
              </div>
            ) : aiRecommendations && (
              <div className="space-y-8">
                {/* Copy the complete AI recommendations display from main component */}
                {/* User Profile & Focus Area */}
                <div className="bg-gradient-to-r from-indigo-800/50 to-purple-800/50 backdrop-blur-sm rounded-3xl p-8 border border-indigo-500/30">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">📊 Tu Perfil de Marca</h3>
                      <p className="text-indigo-200 text-lg">{aiRecommendations.user_profile}</p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">🎯 Área de Enfoque</h3>
                      <div className="flex items-center space-x-3">
                        <span className={`px-4 py-2 rounded-xl text-white font-semibold ${
                          aiRecommendations.focus_area === 'growth' ? 'bg-green-600' :
                          aiRecommendations.focus_area === 'optimization' ? 'bg-blue-600' :
                          aiRecommendations.focus_area === 'ma_prep' ? 'bg-purple-600' :
                          'bg-orange-600'
                        }`}>
                          {aiRecommendations.focus_area === 'growth' ? '📈 Crecimiento' :
                           aiRecommendations.focus_area === 'optimization' ? '⚡ Optimización' :
                           aiRecommendations.focus_area === 'ma_prep' ? '💼 Preparación M&A' :
                           '💰 Monetización'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics & Next Milestone */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">🏆 Próximo Hito</h4>
                    <p className="text-gray-300 text-lg">{aiRecommendations.next_milestone}</p>
                  </div>
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📈 Incremento Estimado</h4>
                    <p className="text-green-400 text-2xl font-bold">{aiRecommendations.estimated_value_increase}</p>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <h3 className="text-3xl font-bold text-white mb-8 text-center">🎯 Plan de Acción Personalizado</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {aiRecommendations.recommendations.map((rec, index) => (
                      <div key={index} className={`p-6 rounded-2xl border relative overflow-hidden ${
                        rec.priority === 'high' ? 'bg-gradient-to-r from-red-900/30 to-pink-900/30 border-red-500/50' :
                        rec.priority === 'medium' ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50' :
                        'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-500/50'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              rec.priority === 'high' ? 'bg-red-600 text-red-100' :
                              rec.priority === 'medium' ? 'bg-yellow-600 text-yellow-100' :
                              'bg-blue-600 text-blue-100'
                            }`}>
                              {rec.priority === 'high' ? 'ALTA' : rec.priority === 'medium' ? 'MEDIA' : 'BAJA'}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              rec.impact === 'high' ? 'bg-green-600 text-green-100' :
                              rec.impact === 'medium' ? 'bg-yellow-600 text-yellow-100' :
                              'bg-gray-600 text-gray-100'
                            }`}>
                              {rec.impact === 'high' ? 'ALTO IMPACTO' : rec.impact === 'medium' ? 'MEDIO IMPACTO' : 'BAJO IMPACTO'}
                            </span>
                          </div>
                          <span className="text-gray-400 text-sm">{rec.timeline}</span>
                        </div>
                        
                        <h4 className="text-xl font-bold text-white mb-3">{rec.title}</h4>
                        <p className="text-gray-300 mb-4">{rec.description}</p>
                        
                        {/* ROI and Investment */}
                        <div className="space-y-3 mb-4">
                          {rec.estimated_roi && (
                            <div className="bg-green-900/20 p-3 rounded-xl border border-green-500/30">
                              <span className="text-green-400 font-semibold">💰 ROI Estimado: {rec.estimated_roi}</span>
                            </div>
                          )}
                          
                          {rec.investment_required && (
                            <div className="bg-blue-900/20 p-3 rounded-xl border border-blue-500/30">
                              <span className="text-blue-400 font-semibold">💸 Inversión Requerida: {rec.investment_required}</span>
                            </div>
                          )}
                        </div>

                        {/* Success Metrics */}
                        {rec.success_metrics && rec.success_metrics.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-yellow-400 font-semibold mb-2">📊 Métricas de Éxito:</h5>
                            <ul className="text-gray-300 text-sm space-y-1">
                              {rec.success_metrics.map((metric, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-yellow-400 mr-2">•</span>
                                  {metric}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Implementation Steps */}
                        {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-purple-400 font-semibold mb-2">⚙️ Pasos de Implementación:</h5>
                            <ol className="text-gray-300 text-sm space-y-1">
                              {rec.implementation_steps.map((step, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-purple-400 mr-2 font-semibold">{idx + 1}.</span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Risk Assessment */}
                        {rec.risk_assessment && (
                          <div className="mb-4">
                            <h5 className="text-red-400 font-semibold mb-2">⚠️ Evaluación de Riesgos:</h5>
                            <div className="bg-red-900/20 p-3 rounded-xl border border-red-500/30">
                              {rec.risk_assessment.probability_success && (
                                <p className="text-red-200 text-sm mb-1">
                                  <span className="font-semibold">Probabilidad de Éxito:</span> {rec.risk_assessment.probability_success}
                                </p>
                              )}
                              {rec.risk_assessment.main_risks && (
                                <div className="text-sm">
                                  <span className="text-red-300 font-semibold">Riesgos Principales:</span>
                                  <ul className="text-red-200 mt-1">
                                    {rec.risk_assessment.main_risks.map((risk, idx) => (
                                      <li key={idx} className="ml-2">• {risk}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Case Studies */}
                        {rec.case_studies && rec.case_studies.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-cyan-400 font-semibold mb-2">📚 Casos de Estudio:</h5>
                            <div className="space-y-2">
                              {rec.case_studies.map((caseStudy, idx) => (
                                <div key={idx} className="bg-cyan-900/20 p-3 rounded-xl border border-cyan-500/30">
                                  <p className="text-cyan-200 text-sm">
                                    <span className="font-semibold">{caseStudy.company}</span> ({caseStudy.industry})
                                  </p>
                                  <p className="text-cyan-300 text-sm">{caseStudy.result}</p>
                                  <p className="text-cyan-400 text-xs">Tiempo: {caseStudy.timeframe}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Competitive Advantage */}
                        {rec.competitive_advantage && (
                          <div className="bg-indigo-900/20 p-3 rounded-xl border border-indigo-500/30">
                            <h5 className="text-indigo-400 font-semibold mb-1">🏆 Ventaja Competitiva:</h5>
                            <p className="text-indigo-200 text-sm">{rec.competitive_advantage}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Metrics to Track */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xl font-bold text-white mb-4">📊 Métricas Clave a Seguir</h4>
                  <div className="flex flex-wrap gap-3">
                    {aiRecommendations.key_metrics_to_track.map((metric, index) => (
                      <span key={index} className="bg-indigo-600/30 text-indigo-200 px-4 py-2 rounded-xl border border-indigo-500/50">
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Competitive Analysis & Market Intelligence - ADDED FOR ACTIVATED COMPONENT */}
                {aiRecommendations.competitive_analysis && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                    <h3 className="text-3xl font-bold text-white mb-8 text-center">🏆 Análisis Competitivo</h3>
                    
                    {/* Market Intelligence */}
                    {aiRecommendations.market_intelligence && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">📊 Inteligencia de Mercado</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(aiRecommendations.market_intelligence).map(([key, value]) => (
                            <div key={key} className="bg-slate-700/50 p-4 rounded-xl border border-white/10">
                              <h5 className="text-blue-400 font-semibold mb-2 capitalize">
                                {key.replace(/_/g, ' ')}
                              </h5>
                              <p className="text-gray-300 text-sm">
                                {typeof value === 'string' ? value : JSON.stringify(value)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Direct Competitors */}
                    {aiRecommendations.competitive_analysis.direct_competitors && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">🎯 Competidores Directos</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {aiRecommendations.competitive_analysis.direct_competitors.map((competitor, index) => (
                            <div key={index} className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 p-4 rounded-xl border border-purple-500/30">
                              <h5 className="text-white font-bold mb-3">
                                {typeof competitor === 'string' ? competitor : competitor.name}
                              </h5>
                              {typeof competitor === 'object' && competitor !== null && (
                                <div className="space-y-2">
                                  {competitor.website && (
                                    <p className="text-blue-400 text-sm">
                                      <span className="font-semibold">🌐 Web:</span> {competitor.website}
                                    </p>
                                  )}
                                  {competitor.main_advantage && (
                                    <p className="text-green-400 text-sm">
                                      <span className="font-semibold">💪 Ventaja:</span> {competitor.main_advantage}
                                    </p>
                                  )}
                                  {competitor.estimated_value && (
                                    <p className="text-purple-400 text-sm">
                                      <span className="font-semibold">💰 Valor:</span> {competitor.estimated_value}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-between mt-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      competitor.strength === 'high' ? 'bg-red-600/30 text-red-300' :
                                      competitor.strength === 'medium' ? 'bg-yellow-600/30 text-yellow-300' :
                                      'bg-green-600/30 text-green-300'
                                    }`}>
                                      {competitor.strength === 'high' ? 'Amenaza Alta' :
                                       competitor.strength === 'medium' ? 'Competencia Media' :
                                       'Competencia Baja'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Market Gaps and Opportunities */}
                    {aiRecommendations.competitive_analysis.market_gaps && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">🔍 Oportunidades de Mercado</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {aiRecommendations.competitive_analysis.market_gaps.map((gap, index) => (
                            <div key={index} className="bg-green-900/20 p-4 rounded-xl border border-green-500/30">
                              <p className="text-green-200">{gap}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Competitive Advantages */}
                    {aiRecommendations.competitive_analysis.competitive_advantages && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">⚡ Ventajas Competitivas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {aiRecommendations.competitive_analysis.competitive_advantages.map((advantage, index) => (
                            <div key={index} className="bg-blue-900/20 p-4 rounded-xl border border-blue-500/30">
                              <p className="text-blue-200">{advantage}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Benchmarks */}
                    {aiRecommendations.market_benchmarks && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-white mb-6">📈 Benchmarks del Sector</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {Object.entries(aiRecommendations.market_benchmarks).map(([key, value]) => (
                            <div key={key} className="bg-slate-700/50 p-4 rounded-xl border border-white/10 text-center">
                              <h5 className="text-orange-400 font-semibold mb-2 text-sm capitalize">
                                {key.replace(/_/g, ' ')}
                              </h5>
                              <p className="text-white text-lg font-bold">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Advanced Analysis Section - Condensed */}
                <div className="bg-gradient-to-r from-slate-800/50 to-purple-800/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/30">
                  <h3 className="text-3xl font-bold text-white mb-6 text-center">🔍 Análisis Completo</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Combined Audit & Report */}
                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">📋</div>
                      <h4 className="text-lg font-bold text-white mb-2">Auditoría Interactiva</h4>
                      <p className="text-gray-300 text-xs mb-4">Análisis profundo de identidad y posicionamiento</p>
                      <button
                        onClick={generateBrandAudit}
                        disabled={isLoadingAudit}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50"
                      >
                        {isLoadingAudit ? 'Analizando...' : '🔍 Iniciar Auditoría'}
                      </button>
                    </div>

                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">📊</div>
                      <h4 className="text-lg font-bold text-white mb-2">Reporte PDF</h4>
                      <p className="text-gray-300 text-xs mb-4">Dashboard ejecutivo completo en PDF</p>
                      <button
                        onClick={() => generateExecutiveReport('comprehensive')}
                        disabled={isGeneratingReport}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50"
                      >
                        {isGeneratingReport ? 'Generando...' : '📄 Descargar PDF'}
                      </button>
                    </div>

                    <div className="bg-slate-700/50 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="text-4xl mb-3">🎯</div>
                      <h4 className="text-lg font-bold text-white mb-2">Plan de Acción</h4>
                      <p className="text-gray-300 text-xs mb-4">Siguientes pasos recomendados</p>
                      <Link
                        to="/asesoria"
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 inline-block"
                      >
                        💼 Consulta Estratégica
                      </Link>
                    </div>
                  </div>
                </div>

                {/* CTA to Book Consultation */}
                <div className="text-center">
                  <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/30">
                    <h4 className="text-2xl font-bold text-white mb-4">¿Necesitas ayuda implementando estas recomendaciones?</h4>
                    <p className="text-cyan-200 mb-6">
                      Agenda una consulta gratuita para crear un plan de implementación específico
                    </p>
                    <button 
                      onClick={() => window.open('https://calendly.com/tamara-navarrete', '_blank')}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold text-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
                    >
                      📅 Agendar Consulta Gratuita
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blockchain Certificate Section */}
      {showCertificate && blockchainCertificate && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🏆 Tu Certificado Blockchain
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Tu marca premium ha recibido un certificado verificable en blockchain de Polygon
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Certificate Display */}
              <div className="bg-gradient-to-r from-purple-800/50 to-pink-800/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/30 mb-8">
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">🏆</div>
                  <h3 className="text-3xl font-bold text-white mb-4">Certificado de Valoración Blockchain</h3>
                  <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                    <div className="text-2xl font-bold text-white mb-2">
                      Marca Certificada por YourBrandValue
                    </div>
                    <div className="text-lg text-purple-200 mb-4">
                      Verificado en Blockchain de Polygon
                    </div>
                    <div className="text-sm text-gray-300 font-mono mb-4">
                      Certificate ID: {blockchainCertificate.certificate_id}
                    </div>
                    <div className="flex justify-center space-x-4">
                      <a 
                        href={blockchainCertificate.verification_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                      >
                        🔗 Verificar Certificado
                      </a>
                      {blockchainCertificate.polygon_explorer_url && (
                        <a 
                          href={blockchainCertificate.polygon_explorer_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300"
                        >
                          🔍 Ver en Blockchain
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Sharing Badges */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📱 Badge para LinkedIn</h4>
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div 
                        dangerouslySetInnerHTML={{ __html: blockchainCertificate.linkedin_badge_html }}
                        className="text-center"
                      />
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(blockchainCertificate.linkedin_badge_html);
                        alert('HTML copiado al portapapeles');
                      }}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      📋 Copiar HTML para LinkedIn
                    </button>
                  </div>

                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📧 Badge para Email</h4>
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div 
                        dangerouslySetInnerHTML={{ __html: blockchainCertificate.email_signature_html }}
                        className="text-center"
                      />
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(blockchainCertificate.email_signature_html);
                        alert('HTML copiado al portapapeles');
                      }}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                    >
                      📋 Copiar HTML para Email
                    </button>
                  </div>
                </div>

                {/* Certificate Benefits */}
                <div className="mt-8 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 text-center">💎 Beneficios de tu Certificado</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl mb-2">🔐</div>
                      <div className="text-white font-semibold">Verificación Blockchain</div>
                      <div className="text-gray-300 text-sm">Inmutable y verificable</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2">🌍</div>
                      <div className="text-white font-semibold">Credibilidad Global</div>
                      <div className="text-gray-300 text-sm">Reconocimiento internacional</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2">📈</div>
                      <div className="text-white font-semibold">Valor Añadido</div>
                      <div className="text-gray-300 text-sm">Diferenciación competitiva</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Brand Audit Section */}
      {result !== null && result !== undefined && (
        <div className="py-20 bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                🔍 Auditoría Interactiva de Marca
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Análisis profundo y scoring de tu marca en todas las dimensiones críticas
              </p>
            </div>

            {!showAudit ? (
              <div className="text-center">
                <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-3xl p-10 border border-green-500/30 max-w-2xl mx-auto">
                  <div className="text-6xl mb-6">🎯</div>
                  <h3 className="text-3xl font-bold text-white mb-6">
                    Auditoria Completa de Marca
                  </h3>
                  <p className="text-green-200 mb-8 text-lg">
                    Evaluación exhaustiva en 4 categorías críticas: Identidad de Marca, Presencia Digital, 
                    Posición en el Mercado y Excelencia Operacional.
                  </p>
                  
                  <button
                    onClick={generateBrandAudit}
                    disabled={isLoadingAudit}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-10 py-4 rounded-2xl font-bold text-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingAudit ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Realizando Auditoría...
                      </span>
                    ) : (
                      '🔍 Iniciar Auditoría de Marca'
                    )}
                  </button>
                </div>
              </div>
            ) : brandAudit && (
              <div className="space-y-8">
                {/* Overall Score */}
                <div className="bg-gradient-to-r from-green-800/50 to-emerald-800/50 backdrop-blur-sm rounded-3xl p-8 border border-green-500/30 text-center">
                  <h3 className="text-3xl font-bold text-white mb-4">Puntuación General</h3>
                  <div className="text-8xl font-bold mb-4" style={{color: brandAudit.overall_score >= 80 ? '#10B981' : brandAudit.overall_score >= 60 ? '#F59E0B' : '#EF4444'}}>
                    {brandAudit.overall_score}/100
                  </div>
                  <p className="text-green-200 text-xl">
                    {brandAudit.overall_score >= 80 ? 'Excelente - Marca muy sólida' :
                     brandAudit.overall_score >= 60 ? 'Bueno - Oportunidades de mejora' :
                     'Necesita mejora - Plan de acción requerido'}
                  </p>
                </div>

                {/* Category Breakdown */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <h3 className="text-3xl font-bold text-white mb-8 text-center">📊 Análisis por Categorías</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {Object.entries(brandAudit.category_scores).map(([category, score]) => (
                      <div key={category} className="bg-slate-700/50 p-6 rounded-2xl border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-xl font-bold text-white">
                            {category === 'brand_identity' ? '🎨 Identidad de Marca' :
                             category === 'digital_presence' ? '💻 Presencia Digital' :
                             category === 'market_position' ? '🏆 Posición en Mercado' :
                             '⚡ Excelencia Operacional'}
                          </h4>
                          <span className={`text-3xl font-bold ${
                            score >= 80 ? 'text-green-400' :
                            score >= 60 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {score}
                          </span>
                        </div>
                        
                        <div className="w-full bg-slate-600 rounded-full h-3 mb-4 overflow-hidden">
                          <div 
                            className={`h-3 rounded-full ${
                              score >= 80 ? 'bg-green-500' :
                              score >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(score, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Priority Actions */}
                {brandAudit.priority_actions && brandAudit.priority_actions.length > 0 && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                    <h3 className="text-3xl font-bold text-white mb-6 text-center">🚨 Acciones Prioritarias</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {brandAudit.priority_actions.slice(0, 6).map((action, index) => (
                        <div key={index} className="bg-red-900/30 p-4 rounded-xl border border-red-500/30">
                          <div className="flex items-center space-x-3">
                            <span className="bg-red-600 text-red-100 px-2 py-1 rounded text-xs font-bold">
                              CRÍTICO
                            </span>
                            <span className="text-white font-semibold">{action}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths and Weaknesses */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {brandAudit.strengths && brandAudit.strengths.length > 0 && (
                    <div className="bg-green-900/30 p-6 rounded-2xl border border-green-500/30">
                      <h4 className="text-xl font-bold text-white mb-4">💪 Fortalezas</h4>
                      <ul className="space-y-2">
                        {brandAudit.strengths.map((strength, index) => (
                          <li key={index} className="text-green-200 flex items-start">
                            <span className="text-green-400 mr-2">✓</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {brandAudit.weaknesses && brandAudit.weaknesses.length > 0 && (
                    <div className="bg-orange-900/30 p-6 rounded-2xl border border-orange-500/30">
                      <h4 className="text-xl font-bold text-white mb-4">⚠️ Debilidades</h4>
                      <ul className="space-y-2">
                        {brandAudit.weaknesses.map((weakness, index) => (
                          <li key={index} className="text-orange-200 flex items-start">
                            <span className="text-orange-400 mr-2">!</span>
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Benchmark Comparison */}
                {brandAudit.benchmark_comparison && (
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">📈 Comparación con la Industria</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gray-400">
                          {brandAudit.benchmark_comparison.industry_average}
                        </div>
                        <div className="text-gray-300">Promedio Industria</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-yellow-400">
                          {brandAudit.benchmark_comparison.top_quartile}
                        </div>
                        <div className="text-gray-300">Top 25%</div>
                      </div>
                      <div>
                        <div className={`text-2xl font-bold ${
                          brandAudit.benchmark_comparison.your_score >= brandAudit.benchmark_comparison.top_quartile ? 'text-green-400' :
                          brandAudit.benchmark_comparison.your_score >= brandAudit.benchmark_comparison.industry_average ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {brandAudit.benchmark_comparison.your_score}
                        </div>
                        <div className="text-gray-300">Tu Puntuación</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pricing section continues here */}
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <div className="App">
          <Navigation />
          
          <Routes>
            <Route path="/" element={<BrandValueCalculator />} />
            <Route path="/calculator" element={<ImprovedCalculatorSimple />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/asesoria" element={<AsesoriaPageNew />} />
            <Route path="/sobre-mi" element={<SobreMiPage />} />
            <Route path="/metodologia" element={<MetodologiaPage />} />
            <Route path="/servicios" element={<ServiciosPage />} />
            <Route path="/casos-estudio" element={<CasosEstudioPage />} />
            <Route path="/reserva" element={<ReservaPage />} />
            <Route path="/r/:resultId" element={<ShareResultPage />} />
            <Route path="/contacto" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/gracias" element={<ThanksPage />} />
            <Route path="/en/thanks" element={<ThanksPage />} />
            <Route path="/test-assets" element={<TestAssetsPage />} />
          </Routes>
          
          {/* FeedbackPopup global - excluir en páginas de pago */}
          <FeedbackPopupWrapper />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

// Wrapper component para controlar en qué páginas mostrar el popup
const FeedbackPopupWrapper = () => {
  const location = useLocation();
  
  // Páginas donde NO mostrar el popup
  const excludedPaths = ['/calculator', '/checkout/success'];
  
  // No renderizar si estamos en una página excluida
  if (excludedPaths.some(path => location.pathname.includes(path))) {
    return null;
  }
  
  return <FeedbackPopup />;
};

export default App;